// LiveStreamManager.tsx
// v3.04 — Intercomm : routing natif via api_signalingWs (suppression du wrapper ws.onmessage fragile)
//
// Changements par rapport à v3.03 :
//   1. Suppression du useEffect wrapper ws.onmessage
//   2. Suppression de knownIntercommDevicesRef (géré via onDeviceConnected/onDeviceDisconnected)
//   3. handleSignalingMessageImpl passe maintenant les callbacks intercomm directement
//      à apiHandleSignalingMessage via le 5e paramètre IntercommSignalingCallbacks

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { getAdminEventContext } from '../../../backendServices/adminManagment/api_adminEvents';

import {
  useMediaStream, VideoSource, AudioOnlyVisual, RemoteConnection, SLOT_COUNT, LayoutSlot,
  DEFAULT_IMAGE_SLOTS, DEFAULT_SOUND_SLOTS, DEFAULT_VIDEO_OVERLAY_SLOTS, DEFAULT_JINGLE_SLOTS,
  DEFAULT_SCREEN_SHARE_SLOTS, DEFAULT_LAYOUT_SLOTS,
} from '../../../contexts/MediaStreamContext';
import { useProductionControlRoom } from '../../../contexts/ProductionControlRoomContext';

import { Window } from '../../widget/windows/window';
import { DeviceInfoPanel, WhipInfoPanel } from './ProductionControlRoom_InfoPanels';
import { LiveStreamLayoutV2 } from './LiveStreamLayoutV2';
import { useCanvasPipeline } from './useCanvasPipeline';
import { useLiveStreamSetup } from './useLiveStreamSetup';
import { useScenePipelineManager } from './useScenePipelineManager';
import { buildVideoRow } from './ProductionControlRoom_Deck';
import { probeHls } from '../../../dataTypes/liveStreamManagment/utils_hls';
import { createBlackVideoStream, audioOnlyVisual } from '../../../dataTypes/liveStreamManagment/utils_audioOnlySource';
import { acquireSpectrumStream, releaseSpectrumStream } from '../../../dataTypes/liveStreamManagment/spectrumVideo';
import { setMetricsChannel, setLoggerIdentity } from '../../../backendServices/debugManagment/remoteLogger';
import {
  SignalingRefs, SlaveRefs, SlaveCallbacks, decrireRegie,
} from '../../../dataTypes/liveStreamManagment/types_signaling';
import { BorrowHandle, PERSISTENT_SOURCE_TYPES } from '../../../dataTypes/liveStreamManagment/sourceRegistry';
import {
  startWhipStreaming       as apiStartWhipStreaming,
  stopWhipStreaming        as apiStopWhipStreaming,
  stopWhipStatsMonitoring as apiStopWhipStatsMonitoring,
} from '../../../backendServices/liveStreamManagment/api_whip';
import {
  startSlaveMode as apiStartSlaveMode,
} from '../../../backendServices/liveStreamManagment/api_slaveMode';
import {
  getDeviceSfuInfo,
  isSfuUnavailable,
} from '../../../backendServices/liveStreamManagment/api_whep';
import {
  ensureChannelProvisionedForRecording as apiEnsureChannelProvisionedForRecording,
  startRecording       as apiStartRecording,
  stopRecording        as apiStopRecording,
  buildRecordingFilename,
  buildFinalMixFilename,
  buildRecordingAngles,
  getRecordingSessions,
  RecordingSession,
  empiricalBytesPerSecond,
  configuredBytesPerSecond,
  estimateRemainingRecordingSeconds,
  remainingSecondsForStreams,
  computeDuration,
  formatFileSize,
} from '../../../backendServices/liveStreamManagment/api_recording';

import {
  AnglePublication,
  createRecordingAnglePath,
  deleteRecordingAnglePath,
  probeInboundVideo,
  inspectInboundVideo,
  publishAngle,
  closeAnglePublication,
} from '../../../backendServices/liveStreamManagment/api_recordingAngles';
import { openBuyStorage } from '../../../utils/utils_storagePurchase';
import { BUY_RECORDING_DURATION_LABEL, openBuyRecordingDuration } from '../../../utils/utils_recordingDurationPurchase';
import { startBandwidthMonitor } from '../../../backendServices/liveStreamManagment/api_bandwidth';

import { BroadcastingChannel, StreamSource } from '../../../dataTypes/broadcastingManagment/types';
import { XtreamCredentials } from '../../../dataTypes/liveStreamManagment/sourceRegistry/types';
import { setAvatarModelsChannel } from './videoEffects/avatarModelStore';
import { updateBroadcastingChannelStreamSource, updateBroadcastingChannelMultiviewAngles } from '../../../backendServices/broadcastingManagment/api_broadcastingChannel';
import { canPublishProgram, programActionOnControlChange } from './programPublishGate';
import { followLiveAnnounce } from '../../../backendServices/broadcastingManagment/api_liveAnnounce';
import { useLiveStreamSnapshot } from '../../../hooks/useLiveStreamSnapshot';
import { useBroadcastSync } from '../../../hooks/useBroadcastSync';
import { useRegieSync } from '../../../hooks/useRegieSync';

import { MultiviewSlotConfig } from './MultiviewPanelV2';
import { useAngleAudioOrder } from './useAngleAudioOrder';
import { angleAudioMode, AngleAudioMode, pickAngleAudio, angleNeedsRepublish, nativeAngleIds, resolveAnglePath } from '../../../dataTypes/liveStreamManagment/angleAudio';
import {
  SourceAliasMap, loadSourceAliases, saveSourceAliases, resolveSourceLabel,
} from './SourceAliasConfigModal';
import {
  AudioEffectState, AudioEffectId, StereoVolume,
  DEFAULT_AUDIO_EFFECTS,
} from './SourceConfigModal';

import { useHlsProber, HlsProbeStatus } from './useHlsProber';

import { useSourceRegistrySync } from '../../../hooks/liveStreamManagment/useSourceRegistrySync';
import { useSourceRegistry } from '../../../contexts/liveStreamManagment/SourceRegistryContext';
import { destroyAllVideoEffectsPipelines, applyVideoEffectsSettings, VideoEffectsSettings } from './videoEffects/VideoEffectsPipelineManager';
import { clearSlotsFromIndexedDB, clearSceneSlotsFromIndexedDB } from '../../../backendServices/liveStreamManagment/local_media_store';
import {
  collectExternalSources, restoreExternalSources, clearExternalSources, hlsSourceId,
  savePendingExternalSources, takePendingExternalSources, describeRestoreReport,
} from '../../../backendServices/liveStreamManagment/regie_external_sources';
import { scheduleAutosave } from '../../../backendServices/liveStreamManagment/regie_autosave';
import { readChannelSnapshot } from '../../../contexts/MediaStreamContext';
import { DECK_RESTORED_EVENT, DeckRestoredDetail, deckSettingsKeys, retargetPipDevices } from '../../../dataTypes/liveStreamManagment/deck_persistence';
import { useAudioMixer } from '../../../contexts/liveStreamManagment/AudioMixerContext';
import { useAudioMetering } from '../../../hooks/liveStreamManagment/useAudioMetering';
import { useMeteredSourceIds } from '../../../hooks/liveStreamManagment/useAudioLevel';

import RecordingStorageModal, { RecordingPublishModal } from './RecordingStorageModal';
import RecordingSessionModal, { RecordingSessionPayload, RecordingSessionSource } from './RecordingSessionModal';
import LiveExportModal from './LiveExportModal';
import { RegieConfigModal, RegieConfigPayload } from './RegieConfigModal';

import { useTypedSelector, useTypedDispatch } from '../../../store/useTypeSelector';
import { RootState        } from '../../../store/generalReducer';
import { hasActiveUploads } from '../../../store/fileManagment/actions';
import { setAdmin         } from '../../../store/adminAuthenticationManagment/actions';
import { createAdminActionLog } from '../../../backendServices/adminManagment/api';

import {
  resetRegieContext,
  RegieResetRefs,
  RegieResetSetters,
} from '../../../contexts/utils';

import { AudioSourceRegistry } from '../../../dataTypes/liveStreamManagment/audioPipeline/AudioSourceRegistry';
import { AudioSwitchState } from './ControlBar';
import { useAutoDirector } from './useAutoDirector';
import { DEFAULT_WHIP_ENCODING, loadWhipEncoding, saveWhipEncoding, sanitizeWhipEncoding, whipEncodingStorageKey, WHIP_ENCODING_RESTORED_EVENT } from '../../../dataTypes/liveStreamManagment/whipEncodingSettings';
import type { SourceAudioConfig } from '../../../contexts/MediaStreamContext';
import { DEFAULT_AUTO_DIRECTOR_SETTINGS, type AutoDirectorSettings } from './autoDirector';
import { loadAudioSwitch, saveAudioSwitch } from '../../../dataTypes/liveStreamManagment/deck_persistence';

// ── Intercomm ─────────────────────────────────────────────────────────────────
import {
  RegieIntercommMap,
  initRegieIntercommForDevice,
  handleRegieIntercommAnswer,
  handleRegieIntercommIce,
  setRegieIntercommActive,
  closeRegieIntercommForDevice,
  closeAllRegieIntercomm,
} from '../../../backendServices/liveStreamManagment/api_regieIntercomm';

import {
  closeReturnStreamToDevice,
  closeAllReturnStreams,
} from '../../../backendServices/liveStreamManagment/api_webrtcPeerConnection';

import { startMainThreadProbe } from '../../../backendServices/debugManagment/mainThreadProbe';
import { toProxiedUrl } from '../../../backendServices/liveStreamManagment/api_xtreamCode';
import { useTbarTransition } from './useTbarTransition';
import { getEnchainementById } from './enchainements/registry';
import StageRequestsDock from './StageRequestsDock';
import PollsDock from './PollsDock';
import ClientHealthBadge from './ClientHealthBadge';
import { PollOverlay } from './pollOverlay';

// ─────────────────────────────────────────────────────────────────────────────

interface LiveStreamManagerProps {
  _sharedRefs?:        SignalingRefs;
  broadcastingChannel: BroadcastingChannel;
  streamSource?                : StreamSource;
  defaultStreamSource?         : StreamSource;
  onStreamSourceChange?        : (source: StreamSource) => void;
  onDefaultStreamSourceChange? : (source: StreamSource) => void;
  onStopStreaming?              : () => void;
  onOpenStorage?                : () => void;
  hlsVolume?:         number;
  hlsMuted?:          boolean;
  onHlsVolumeChange?: (v: number) => void;
  onHlsMutedChange?:  (m: boolean) => void;
  onRegisterStart?: (fn: () => void) => void;
  onRegisterStop?:  (fn: () => void) => void;
  onConfirmStreamSource?: (source: StreamSource) => void;
  hlsMonitorStream?: MediaStream | null;
  hlsAudioRegistry?  : AudioSourceRegistry;
  hlsAudioSourceId?  : string;
  onHlsReset?: () => void;
  channelSignalConfirmed? : boolean;
  onLiveStatusChange? : (status: { isRecording: boolean; isStreaming: boolean }) => void;
}

/** Délai laissé à la relance automatique de la régie après l'activation de la chaîne. */
const REGIE_RELAUNCH_TIMEOUT_MS = 75_000;

const PROBE_TIMEOUT_MS  = 30_000;
const PROBE_INTERVAL_MS =  2_000;

async function waitForHls(url: string, timeoutMs = PROBE_TIMEOUT_MS): Promise<boolean> {
  const deadline = Date.now() + timeoutMs;
  while (Date.now() < deadline) {
    const ok = await probeHls(url);
    if (ok) return true;
    await new Promise(r => setTimeout(r, PROBE_INTERVAL_MS));
  }
  return false;
}

/** Fenêtres de fin de live (publier ? / conservé dans Stockage) : 300 px par défaut, trop étroit. */
const END_OF_LIVE_DIALOG_WIDTH = 'min(560px, 92vw)';
// REC du live en multiview : attente maximale des publications d'angles avant de démarrer.
const AUTO_RECORD_ANGLES_WAIT_MS = 8_000;

// ─────────────────────────────────────────────────────────────────────────────

const LiveStreamManager = ({
  _sharedRefs,
  broadcastingChannel,
  streamSource,
  defaultStreamSource,
  onStreamSourceChange,
  onDefaultStreamSourceChange,
  onStopStreaming,
  onOpenStorage,
  hlsVolume,
  hlsMuted,
  onHlsVolumeChange,
  onHlsMutedChange,
  onRegisterStart,
  onRegisterStop,
  onConfirmStreamSource,
  hlsMonitorStream,
  hlsAudioRegistry,
  hlsAudioSourceId,
  onHlsReset,
  channelSignalConfirmed,
  onLiveStatusChange,
}: LiveStreamManagerProps) => {

  const { authentication } = useTypedSelector((state: RootState) => state.authenticationReducer);
  const dispatch = useTypedDispatch();
  const uploadInProgress = useTypedSelector(hasActiveUploads);

  const broadcastUuid = broadcastingChannel?.uuid;
  const { isDeckExternal, isMultiviewExternal } = useBroadcastSync(broadcastUuid, 'main');

  /**
   * L'UUID de la chaîne ouverte — ou rien. AUCUN REPLI.
   *
   * Toute la régie s'écrivait `channelUuid`, et
   * `channelUuid` portait en dur l'UUID d'une chaîne de production. Quand
   * l'UUID n'était pas encore résolu, la régie travaillait donc sur CETTE chaîne-là
   * sous l'identité de celle qu'on avait ouverte : elle y chargeait son antenne, et
   * surtout elle pouvait y publier en WHIP ou y lancer un enregistrement.
   *
   * Le repli n'existe plus, ni ici ni dans les réglages. Sans chaîne identifiée :
   * l'affichage ne montre rien, et les actions refusent — voir requireChannelUuid().
   */
  const channelUuid = broadcastingChannel?.uuid?.trim() || null;

  // Modèles 3D de l'effet Avatar : rangés chez le créateur de CETTE chaîne (cf. avatarModelStore).
  useEffect(() => { setAvatarModelsChannel(channelUuid); }, [channelUuid]);

  /**
   * L'ADRESSE PUBLIQUE de la chaîne. Distincte de `channelUuid`, qui reste
   * l'identité de la chaîne pour l'API (arrêt d'enregistrement, métriques…).
   *
   * Elle sert partout où une chaîne est ADRESSÉE plutôt que désignée : le lien
   * d'invitation envoyé aux téléphones, et les identifiants de source du deck.
   * Confondre les deux fabrique un décalage silencieux — les identifiants
   * `hls-video-<...>` construits par le deck ne correspondaient plus à ceux que
   * ControlBar compare, et le filtre des sources externes cessait de mordre.
   */
  const channelPublicRef = broadcastingChannel?.publicRef?.trim() || null;

  const channelUuidRef = useRef<string | null>(channelUuid);
  useEffect(() => { channelUuidRef.current = channelUuid; }, [channelUuid]);

  // Diagnostic temporaire (15/09/2026) : la webcam reste noire sur Lovelight TV sans
  // qu'aucun des logs posés dans acquireAndSetStream/startCanvasForStream n'apparaisse —
  // ce qui suggère que CE composant n'est peut-être pas réellement remonté à chaque
  // bascule de chaîne (contrairement à ce que suppose tout le reste de ce fichier, key
  // par chaîne dans BroadcastingChannelList). Confirme si un vrai montage/démontage a
  // lieu ici pour ce channelUuid, ou si l'instance survit en silence.
  useEffect(() => {
    console.warn(`[LiveStreamManager] MONTÉ pour channelUuid=${channelUuid}`);
    return () => console.warn(`[LiveStreamManager] DÉMONTÉ pour channelUuid=${channelUuid}`);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Pour une action qui ENGAGE la chaîne — passage à l'antenne, enregistrement,
   * sauvegarde de configuration. Mieux vaut une erreur franche qu'une action juste,
   * exécutée sur la mauvaise chaîne.
   */
  const requireChannelUuid = useCallback((): string => {
    const uuid = channelUuidRef.current;

    if (!uuid) { throw new Error("Aucune chaîne identifiée — opération annulée."); }

    return uuid;
  }, []);

  const channelPublicRefRef = useRef<string | null>(channelPublicRef);
  useEffect(() => { channelPublicRefRef.current = channelPublicRef; }, [channelPublicRef]);

  /**
   * Le pendant de requireChannelUuid pour tout ce qui est ADRESSÉ par HTTP :
   * URL de lecture, publication WHIP, endpoints `/regie` du microservice.
   *
   * Les deux existent parce que les deux valeurs sont nécessaires, jamais
   * interchangeables : l'uuid nomme les paths MediaMTX et identifie la chaîne
   * auprès de l'API ; la référence est ce qui route à travers HAProxy. Choisir
   * la mauvaise ne casse pas la compilation — elle rend 404 ou 410 au moment où
   * quelqu'un regarde.
   */
  const requireChannelAddress = useCallback((): string => {
    const adresse = channelPublicRefRef.current;

    if (!adresse) { throw new Error("Aucune adresse de chaîne — opération annulée."); }

    return adresse;
  }, []);

  // ── Contextes ──────────────────────────────────────────────────────────────

  const {
    currentStream, setCurrentStream,
    isStreaming, setIsStreaming,
    videoRef, audioContextRef, peerConnectionRef,
    selectedVideoSource, setSelectedVideoSource,
    selectedAudio, setSelectedAudio,
    videoSources, setVideoSources,
    settings, setSettings, remoteConnections, setRemoteConnections,
    negotiatedWhipCodec, imageSlots, setImageSlots,
    soundSlots, setSoundSlots,
    activeImageIds, setActiveImageIds,
    activeSoundIds, setActiveSoundIds,
    videoOverlaySlots, setVideoOverlaySlots,
    activeVideoOverlayIds, setActiveVideoOverlayIds,
    jingleSlots, setJingleSlots,
    activeJingleId, setActiveJingleId,
    screenShareSlots, setScreenShareSlots,
    screenShareStreams, setScreenShareStreams,
    layoutSlots, setLayoutSlots,
    activeLayoutId, setActiveLayoutId,
    sourceAudioConfigs, setSourceAudioConfigs,
    getSourceAudioConfig, updateSourceAudioConfig,
    channelAudioEffects, replaceChannelAudioEffects,
    openChannel,
  } = useMediaStream();

  /**
   * Déclare au contexte quelle chaîne est ouverte.
   *
   * Le contexte est monté une seule fois pour toute l'application : sans cette
   * bascule, l'état d'une chaîne se déversait dans la suivante — jusqu'aux
   * connexions vivantes aux smartphones. `openChannel` range l'état de la chaîne
   * qu'on quitte, restaure celui de la chaîne qu'on ouvre si on y est déjà venu, et
   * ferme les connexions de la précédente.
   */
  // L'uuid CLOISONNE les sessions, l'adresse publique COMPOSE les URL que le
  // contexte émet. Les deux voyagent ensemble parce qu'ils ne servent pas à la
  // même chose.
  useEffect(() => { openChannel(channelUuid, channelPublicRef); }, [channelUuid, channelPublicRef, openChannel]);

  const {
    windowState, showAlert, showConfirm,
    getSdpLogRegie, addSdpLogRegie, restoreSdpLog,
    showSdpDebugRegie, setShowSdpDebugRegie,
    sdpInitialDevice, setSdpInitialDevice,
    showDeviceInfoPanel, setShowDeviceInfoPanel,
    activeDeviceInfo, showWhipInfoPanel, setShowWhipInfoPanel,
    whipInfo, setWhipInfo,
  } = useProductionControlRoom();

  // ── États locaux ───────────────────────────────────────────────────────────

  // Acquittement du bandeau « une autre régie observe ». Ce n'est pas une
  // alerte : c'est une information permanente, et le porteur doit pouvoir la
  // ranger. On mémorise QUI était là au moment du clic, pas un simple booléen —
  // acquitter « poste 2 observe » ne doit pas escamoter l'arrivée du poste 3.
  const [observationAcquittee,    setObservationAcquittee]    = useState('');
  const [showSaveConfig,          setShowSaveConfig]          = useState(false);
  const [showLoadConfig,          setShowLoadConfig]          = useState(false);
  const [isStartingStream,        setIsStartingStream]        = useState(false);
  const [isStoppingStream,        setIsStoppingStream]        = useState(false);
  const [showKeyboardShortcuts,   setShowKeyboardShortcuts]   = useState(false);
  const [showMediaboardConfig,    setShowMediaboardConfig]    = useState(false);
  const [previewMuted,            setPreviewMuted]            = useState(true);
  const [finalMuted,              setFinalMuted]              = useState(true);
  const [isStreamButtonActive,    setIsStreamButtonActive]    = useState(isStreaming);
  const [isRegiePowered,          setIsRegiePowered]          = useState(true);
  const [isRecording,             setIsRecording]             = useState(false);
  const [isTogglingRecording,     setIsTogglingRecording]     = useState(false);
  const [recordingStartedAt,      setRecordingStartedAt]      = useState<number | null>(null);
  const [recordingElapsedSec,     setRecordingElapsedSec]     = useState(0);
  const [recordingSessionId,      setRecordingSessionId]      = useState<number | null>(null);
  // Enregistrement du live qu'on vient d'arrêter, quand l'opérateur a répondu « Oui » à
  // « Voulez-vous publier… » : sa présence ouvre la fiche Publier (RecordingPublishModal).
  const [liveRecordingToPublish,  setLiveRecordingToPublish]  = useState<RecordingSession | null>(null);
  const [liveRecordingAngleCount, setLiveRecordingAngleCount] = useState(0);
  const [recordingHistory,        setRecordingHistory]        = useState<RecordingSession[]>([]);
  const [showStorageModal,        setShowStorageModal]        = useState(false);
  // Boîte "Enregistrement d'une session" — ouverte par le bouton Rec, elle nomme la
  // session et, en multi-angle, choisit les sources du deck à enregistrer.
  const [showRecordingModal,      setShowRecordingModal]      = useState(false);

  // Multi-angle : la BDD (BroadcastingChannel.multiview) est la source de vérité, mais
  // le bouton de la ControlBar la bascule sans que `broadcastingChannel` soit rechargé.
  // On garde donc ici la valeur courante, remontée par la ControlBar, pour que la boîte
  // d'enregistrement ouverte juste après une bascule affiche le bon mode.
  const [multiviewActive,         setMultiviewActive]         = useState(broadcastingChannel?.multiview ?? false);

  // ── Format du programme : le canvas suit le sens de la CHAÎNE ──────────────
  // Choisi à la création de la chaîne (onglet Configuration) : 'landscape' → 1920×1080,
  // 'portrait' → 1080×1920. Les dimensions vivent dans les réglages du poste ; on les aligne
  // sur la chaîne ouverte, pour qu'un poste qui passe d'une chaîne paysage à une chaîne
  // portrait (et inversement) compose toujours dans le bon cadre. Sans `orientation`
  // (anciennes données), paysage : aucun changement pour l'existant.
  const programOrientation = broadcastingChannel?.orientation === 'portrait' ? 'portrait' : 'landscape';
  useEffect(() => {
    const canvasWidth  = programOrientation === 'portrait' ? 1080 : 1920;
    const canvasHeight = programOrientation === 'portrait' ? 1920 : 1080;
    setSettings(prev => (prev.canvasWidth === canvasWidth && prev.canvasHeight === canvasHeight)
      ? prev
      : { ...prev, canvasWidth, canvasHeight });
  }, [programOrientation, setSettings]);
  useEffect(() => { setMultiviewActive(broadcastingChannel?.multiview ?? false); }, [broadcastingChannel?.multiview, broadcastingChannel?.id]);

  // Angles de la grille multiview confirmés pour l'exposition publique — voir
  // LiveExportModal. `null` tant qu'aucune confirmation n'a eu lieu pour cette
  // diffusion : rien n'est exposé par défaut, contrairement aux slots eux-mêmes qui
  // restent tous visibles dans la régie.
  const [exportedMultiviewSourceIds, setExportedMultiviewSourceIds] = useState<Set<string> | null>(null);
  const [showLiveExportModal,        setShowLiveExportModal]        = useState(false);
  // Quel déclencheur a ouvert LiveExportModal : 'start' (clic Live régie, il faut
  // démarrer la diffusion à la confirmation) ou 'update' (Multi-angle activé pendant
  // qu'on diffuse déjà, il n'y a rien à démarrer). Ne PAS se fier à `isStreaming` au
  // moment de la confirmation pour deviner lequel : il peut déjà valoir `true` au tout
  // premier clic sur Live régie (aperçu local actif, ou autre raison sans rapport avec
  // la diffusion publique), ce qui a fait sauter le démarrage en silence.
  const liveExportModalTriggerRef = useRef<'start' | 'update'>('start');

  // ── Réglages deck/audio persistés — survivent au démontage complet de la régie
  //    (changement d'onglet Desktop) sans attendre un clic sur "Enregistrer la
  //    configuration", contrairement au flux capturé lui-même (webcam/écran/WebRTC)
  //    qui ne peut de toute façon pas survivre à un démontage ──
  const SLOT_CONFIGS_KEY      = 'mediartc_regie_slot_configs';   // ancienne clé GLOBALE : point de départ de la migration
  const AUDIO_DEVICE_IDS_KEY  = 'mediartc_regie_audio_device_ids';
  const STEREO_VOLUMES_KEY    = 'mediartc_regie_stereo_volumes';
  // Réglages vidéo (niveaux + effet actif + sa config) PAR SOURCE, cloisonnés par
  // chaîne (ce composant remonte à chaque changement de chaîne) : sans
  // ça, SourceConfigModal repartait toujours de ses valeurs par défaut à chaque
  // ouverture (jamais persisté avant ce correctif, contrairement à l'audio, déjà lu
  // depuis MediaStreamContext) — constaté explicitement : "si on rouvre la fenêtre de
  // réglages, les paramètres [sont] remis à 0".
  const VIDEO_EFFECTS_SETTINGS_MAP_KEY = channelUuid ? `mediartc_regie_video_effects_settings:${channelUuid}` : 'mediartc_regie_video_effects_settings';

  // Réglages par source → sauvegarde automatique serveur de la chaîne (différée).
  const notifyDeckSettings = () => {
    if (channelUuid) scheduleAutosave(channelUuid, () => readChannelSnapshot(channelUuid));
  };

  // Multiview cloisonné PAR CHAÎNE (même clé que deck_persistence.deckSettingsKeys).
  // À défaut de clé propre à la chaîne, on reprend l'ancienne clé globale une fois.
  const SLOT_CONFIGS_SCOPED_KEY = channelUuid ? `${SLOT_CONFIGS_KEY}:${channelUuid}` : SLOT_CONFIGS_KEY;

  const [slotConfigs, _setSlotConfigs] = useState<MultiviewSlotConfig[]>(() => {
    const defaults = Array.from({ length: SLOT_COUNT }, () => ({ sourceId: null, customLabel: null }));
    try {
      const raw = localStorage.getItem(SLOT_CONFIGS_SCOPED_KEY) ?? localStorage.getItem(SLOT_CONFIGS_KEY);
      if (!raw) return defaults;
      const parsed = JSON.parse(raw) as MultiviewSlotConfig[];
      return defaults.map((def, i) => parsed[i] ? { ...def, ...parsed[i] } : def);
    } catch { return defaults; }
  });
  const setSlotConfigs: React.Dispatch<React.SetStateAction<MultiviewSlotConfig[]>> = (action) => {
    _setSlotConfigs(prev => {
      const next = typeof action === 'function' ? (action as (p: MultiviewSlotConfig[]) => MultiviewSlotConfig[])(prev) : action;
      try { localStorage.setItem(SLOT_CONFIGS_SCOPED_KEY, JSON.stringify(next)); } catch {}
      notifyDeckSettings();
      return next;
    });
  };

  const [closeOnFullscreen,       setCloseOnFullscreen]       = useState(false);
  const [,           setShowMultiview]           = useState(true);
  const [sourceAliases, _setSourceAliases] = useState<SourceAliasMap>(() => loadSourceAliases());
  const setSourceAliases = useCallback((next: SourceAliasMap) => {
    _setSourceAliases(next); saveSourceAliases(next);
  }, []);
  const [showSourceAliasConfig,   setShowSourceAliasConfig]   = useState(false);
  const [activeLayoutPreviewId,   setActiveLayoutPreviewId]   = useState<string | null>(null);
  const [activeSceneLayoutIds,    setActiveSceneLayoutIds]    = useState<string[]>([]);
  const [poweredOffSlots,         setPoweredOffSlots]         = useState<Set<string>>(new Set());

  const [selectedAudioDeviceIds, _setSelectedAudioDeviceIds] = useState<Map<string, string>>(() => {
    try {
      const raw = localStorage.getItem(AUDIO_DEVICE_IDS_KEY);
      return raw ? new Map(JSON.parse(raw)) : new Map();
    } catch { return new Map(); }
  });
  const setSelectedAudioDeviceIds: React.Dispatch<React.SetStateAction<Map<string, string>>> = (action) => {
    _setSelectedAudioDeviceIds(prev => {
      const next = typeof action === 'function' ? (action as (p: Map<string, string>) => Map<string, string>)(prev) : action;
      try { localStorage.setItem(AUDIO_DEVICE_IDS_KEY, JSON.stringify(Array.from(next.entries()))); } catch {}
      return next;
    });
  };

  const [stereoVolumes, _setStereoVolumes] = useState<Map<string, StereoVolume>>(() => {
    try {
      const raw = localStorage.getItem(STEREO_VOLUMES_KEY);
      return raw ? new Map(JSON.parse(raw)) : new Map();
    } catch { return new Map(); }
  });
  const setStereoVolumes: React.Dispatch<React.SetStateAction<Map<string, StereoVolume>>> = (action) => {
    _setStereoVolumes(prev => {
      const next = typeof action === 'function' ? (action as (p: Map<string, StereoVolume>) => Map<string, StereoVolume>)(prev) : action;
      try { localStorage.setItem(STEREO_VOLUMES_KEY, JSON.stringify(Array.from(next.entries()))); } catch {}
      return next;
    });
  };

  // Voies L/R affichées par le pupitre : ce que le graphe audio applique VRAIMENT.
  // `applyStoredLevels` réapplique `sourceAudioConfigs.volumeLeft/Right` à chaque
  // (re)connexion d'une source ; `stereoVolumes` (clé du poste, non cloisonnée par
  // chaîne) peut avoir divergé. Le pupitre affichait alors 0 dB pendant que la
  // source partait à −54 dB (webcam Logitech muette, 2026-10-29). `stereoVolumes`
  // ne sert plus que de repli quand la config de la chaîne n'a pas de valeur.
  const stereoVolumesShown = useMemo(() => {
    const shown = new Map(stereoVolumes);
    for (const c of sourceAudioConfigs) {
      if (c.volumeLeft !== undefined && c.volumeRight !== undefined) {
        shown.set(c.sourceId, { left: c.volumeLeft, right: c.volumeRight });
      }
    }
    return shown;
  }, [stereoVolumes, sourceAudioConfigs]);

  // Effets audio : simple LECTURE du magasin de la chaîne (MediaStreamContext), le
  // seul que le graphe audio applique. Ils avaient leur propre copie ici, sous
  // d'autres clés : les deux divergeaient (repli sur une clé non cloisonnée au
  // montage, chargement de configuration, restauration serveur clé par clé) et le
  // pupitre montrait tout décoché pendant qu'un effet « robot » tournait à l'antenne.
  const audioEffectsMap = useMemo(() => new Map<string, AudioEffectState>(
    channelAudioEffects.filter(r => r.audioEffects).map(r => [r.sourceId, r.audioEffects!])
  ), [channelAudioEffects]);
  const audioEffectConfigsMap = useMemo(() => new Map<string, Record<string, unknown>>(
    channelAudioEffects.filter(r => r.audioEffectConfigs).map(r => [r.sourceId, r.audioEffectConfigs!])
  ), [channelAudioEffects]);

  const [videoEffectsSettingsMap, _setVideoEffectsSettingsMap] = useState<Map<string, VideoEffectsSettings>>(() => {
    try {
      const raw = localStorage.getItem(VIDEO_EFFECTS_SETTINGS_MAP_KEY);
      return raw ? new Map(JSON.parse(raw)) : new Map();
    } catch { return new Map(); }
  });
  const setVideoEffectsSettingsMap: React.Dispatch<React.SetStateAction<Map<string, VideoEffectsSettings>>> = (action) => {
    _setVideoEffectsSettingsMap(prev => {
      const next = typeof action === 'function' ? (action as (p: Map<string, VideoEffectsSettings>) => Map<string, VideoEffectsSettings>)(prev) : action;
      try { localStorage.setItem(VIDEO_EFFECTS_SETTINGS_MAP_KEY, JSON.stringify(Array.from(next.entries()))); } catch {}
      notifyDeckSettings();
      return next;
    });
  };

  // ── Intercomm state ───────────────────────────────────────────────────────
  const intercommMapRef       = useRef<RegieIntercommMap>(new Map());
  const intercommMicStreamRef = useRef<MediaStream | null>(null);

  const [intercommMicStream,       setIntercommMicStream]       = useState<MediaStream | null>(null);
  const [intercommDeviceStreams,   setIntercommDeviceStreams]    = useState<Map<string, MediaStream>>(new Map());
  const [intercommSpeakingDevices, setIntercommSpeakingDevices] = useState<Set<string>>(new Set());

  // ── knownIntercommDevicesRef : évite les doublons d'init intercomm ────────
  const knownIntercommDevicesRef        = useRef<Set<string>>(new Set());
  const intercommInitPendingTimersRef   = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  const channelActive = broadcastingChannel?.active ?? false;
  // Primitif (pas l'objet broadcastingChannel entier) en dépendance de useCallback plus
  // bas : évite de recréer startWebRTCStreamingImpl à chaque changement d'un autre champ.
  const broadcastingChannelStatus = broadcastingChannel?.channelStatus;
  // Distinct de channelActive (optimiste, bascule au clic) : ne passe à true qu'une
  // fois la chaîne réellement démarrée côté serveur (channelStatus === 'active') ET que
  // le signal HLS retour est confirmé (channelSignalConfirmed, cf. hlsHasSignal dans
  // BroadcastingChannelSheet / displayChannelStatus) — sinon le bouton se débloquait
  // pendant que l'UI affichait encore l'animation "activation en cours". Repasse à
  // false immédiatement dès l'intention d'arrêt (channelStatus === 'deactivating').
  const channelFullyActive = broadcastingChannel?.channelStatus === 'active'
    && (channelSignalConfirmed ?? true);

  // L'enregistrement exige que la chaîne soit active côté serveur (channelStatus ===
  // 'active'), sans attendre la confirmation du signal HLS retour (channelFullyActive)
  // dont l'enregistrement n'a pas besoin. Les états inactive / activating /
  // deactivating / error bloquent le bouton Rec.
  const channelReadyForRecording = broadcastingChannel?.channelStatus === 'active';

  // ── Refs divers ────────────────────────────────────────────────────────────

  const regieAutoOffSlotsRef        = useRef<string[]>([]);
  const recordingTimerRef           = useRef<ReturnType<typeof setInterval> | null>(null);
  const previousStreamSourceRef     = useRef<string>(broadcastingChannel?.streamSource ?? 'video');
  const modalObservedSourceIdRef    = useRef<string | null>(null);
  const slotConfigsUserEditedRef    = useRef(false);
  const hasCheckedStaleStreamRef    = useRef(false);
  const prevChannelActiveRef        = useRef(channelActive);

  const canvasPreviewRef     = useRef<HTMLVideoElement | null>(null);

  // Largeur minimale de la régie : la ControlBar tient sur une seule ligne (sections en nowrap),
  // donc sous sa largeur naturelle ses boutons débordent de leur cadre. On observe le débordement
  // et on verrouille le conteneur à la largeur qui l'absorbe ; la page défile alors à l'horizontale.
  const [regieMinWidth, setRegieMinWidth] = useState(0);
  const regieObserverRef = useRef<ResizeObserver | null>(null);
  const regieWrapRef = useCallback((el: HTMLDivElement | null) => {
    regieObserverRef.current?.disconnect();
    regieObserverRef.current = null;
    if (!el) return;
    const measure = () => {
      const bar = el.querySelector<HTMLElement>('[data-controlbar]');
      if (!bar || bar.scrollWidth <= bar.clientWidth + 1) return;
      const needed = Math.ceil(bar.scrollWidth + (el.clientWidth - bar.clientWidth) + 10);
      setRegieMinWidth(prev => Math.max(prev, needed));
    };
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    regieObserverRef.current = ro;
    measure();
  }, []);
  // Double tampon de la source à l'antenne : deux <video> jumeaux, dont un seul
  // alimente la boucle de composition à un instant donné (cf. useCanvasPipeline,
  // startCanvasForStream v4.0). Ne jamais lire l'un des deux directement pour
  // savoir « quelle est la source à l'antenne » — utiliser getActiveSourceEl().
  const sourceVideoRef       = useRef<HTMLVideoElement | null>(null);
  const sourceVideoAltRef    = useRef<HTMLVideoElement | null>(null);
  const compositingCanvasRef = useRef<HTMLCanvasElement | null>(null);

  const activeSourceRef         = useRef<string | null>(null);
  const onAirDeviceIdRef        = useRef<string | null>(null);
  const currentBorrowHandleRef  = useRef<BorrowHandle | null>(null);
  const isStreamingRef          = useRef<boolean>(isStreaming);
  // Lu par la retentative réseau du WHIP (setTimeout dans startWebRTCStreamingImpl) : sans ce
  // ref, un arrêt manuel PENDANT une coupure réseau n'empêchait pas la boucle de retentative,
  // déjà lancée avec un `isStreamButtonActive` figé au moment de sa création, de relancer le
  // WHIP tout de même dès le retour du réseau.
  const isStreamButtonActiveRef = useRef<boolean>(isStreamButtonActive);
  const isRecordingRef          = useRef<boolean>(isRecording);
  const recordingSessionIdRef   = useRef<number | null>(recordingSessionId);
  const uploadInProgressRef     = useRef<boolean>(false);

  const acquiringRef = useRef(false);
  // Dernière demande de bascule reçue PENDANT une acquisition. Voir acquireAndSetStream.
  const pendingAcquireRef = useRef<{ source: VideoSource; afterSet?: (stream: MediaStream) => void } | null>(null);

  const activeSceneLayoutIdsRef = useRef(activeSceneLayoutIds);
  useEffect(() => { activeSceneLayoutIdsRef.current = activeSceneLayoutIds; }, [activeSceneLayoutIds]);
  useEffect(() => { isStreamingRef.current          = isStreaming;          }, [isStreaming]);
  useEffect(() => { isStreamButtonActiveRef.current  = isStreamButtonActive; }, [isStreamButtonActive]);
  useEffect(() => { isRecordingRef.current          = isRecording;          }, [isRecording]);
  useEffect(() => { recordingSessionIdRef.current   = recordingSessionId;   }, [recordingSessionId]);
  // Remonte l'état recording/streaming au parent (BroadcastingChannelSheet) en temps réel :
  // nécessaire pour signaler à l'utilisateur, au clic sur "désactiver la chaîne", qu'un
  // enregistrement/diffusion est en cours — ce composant peut être dans un onglet masqué
  // à ce moment-là, donc l'info doit être disponible côté parent, toujours visible.
  // `isStreaming` dit seulement que la liaison WebRTC de la régie est ouverte : elle l'est dès
  // que la régie a une image, y compris quand l'antenne diffuse la mire ou le programme
  // (streamSource 'video'). Ce que la désactivation interrompt, et ce dont on avertit
  // l'opérateur, c'est une diffusion réellement À L'ANTENNE : liaison ouverte ET régie
  // sélectionnée comme source (même règle que programLinkOnAir plus bas).
  const isOnAir = isStreaming && streamSource === 'webrtc';
  useEffect(() => { onLiveStatusChange?.({ isRecording, isStreaming: isOnAir }); }, [isRecording, isOnAir, onLiveStatusChange]);
  useEffect(() => { uploadInProgressRef.current      = uploadInProgress;     }, [uploadInProgress]);

  const EXTERNAL_SOURCES_KEY = 'mediartc_external_sources';
  const [externalSources] = useState<{ id: string; label: string; url: string }[]>(
    () => { try { return JSON.parse(localStorage.getItem(EXTERNAL_SOURCES_KEY) || '[]'); } catch { return []; } }
  );

  const [isSlave,    setIsSlave]    = useState(false);
  const [, setSlaveState] = useState<'disconnected'|'connecting'|'connected'|'failed'>('disconnected');
  const slaveWsRef              = useRef<WebSocket | null>(null);
  const slavePcRef              = useRef<RTCPeerConnection | null>(null);
  const slaveIceBufRef          = useRef<RTCIceCandidateInit[]>([]);
  const slaveRemoteDescSetRef   = useRef(false);
  const slaveShouldReconnectRef   = useRef(false);
  const slaveReconnectTimeoutRef  = useRef<ReturnType<typeof setTimeout> | null>(null);
  const slaveSettingsRef        = useRef({ masterWsUrl: '', deviceId: '', password: '', label: '' });

  const masterConfig      = settings.masterConfig;
  const streamDestination = settings.streamDestination;

  const deckSlotsRef       = useRef<(VideoSource | null)[]>(Array(SLOT_COUNT).fill(null));
  const deckCutCallbackRef = useRef<() => void>(() => {});
  const recordingLimitReachedRef = useRef<() => void>(() => {});
  // Angles de téléphones dont le jumeau « audio du programme » est prêt côté serveur (cf. angleAudio.ts).
  const [angleAudioReady, setAngleAudioReady] = useState<string[]>([]);
  const angleAudioStateRef = useRef<(ready: string[]) => void>(() => {});
  angleAudioStateRef.current = (ready) =>
    setAngleAudioReady(prev => (prev.join(',') === ready.join(',') ? prev : ready));

  // ── Enregistrement automatique du live (2026-10-22) ──────────────────────────
  // Lancer le live lance un REC ; l'arrêter l'arrête et propose de le publier.
  // `autoRecordPendingRef` : le live vient de basculer, le REC reste à démarrer — c'est un
  // effet qui s'en charge, une fois `isStreaming` à jour (startRecordingConfirmed, appelé
  // depuis startStreamingConfirmed, verrait encore isStreaming=false et relancerait le WHIP).
  // `liveRecordingSessionIdRef` : la session que CE live a lancée. Un REC manuel n'y figure
  // jamais — l'arrêt du live n'y touche pas. Survit à un arrêt du REC en cours de live
  // (quota, bouton REC, perte du flux) : la question se pose quand même à la fin.
  // `finishLiveRecordingRef` : peuplé plus bas, après handleStopRecording — même pattern
  // que recordingLimitReachedRef.
  const autoRecordPendingRef      = useRef(false);
  const liveRecordingSessionIdRef = useRef<number | null>(null);
  const finishLiveRecordingRef    = useRef<() => Promise<RecordingSession | null>>(async () => null);

  useSourceRegistrySync();
  const { sources, registry, acquirers, releaseChannelScopedSources } = useSourceRegistry();

  // Le nettoyage de démontage plus bas ne doit pas dépendre de l'identité de cette
  // fonction : l'effet qui le porte a des dépendances vides, il capturerait celle du
  // premier rendu.
  const releaseChannelScopedSourcesRef = useRef(releaseChannelScopedSources);
  useEffect(() => { releaseChannelScopedSourcesRef.current = releaseChannelScopedSources; }, [releaseChannelScopedSources]);
  const { mixer, audioSourceRegistry, connectAudioForSource, disconnectAudioForSource } = useAudioMixer();

  // Le mute du final vaut aussi pour les sons/jingles du médiaboard : ils partent
  // toujours vers MediaMTX, mais n'atteignent les enceintes que si le final est audible.
  useEffect(() => { mixer.setLocalMonitorMuted(finalMuted); }, [mixer, finalMuted]);

  const registryRef = useRef(registry);
  useEffect(() => { registryRef.current = registry; }, [registry]);

  useEffect(() => {
    acquirers.hls.setAudioSourceRegistry(audioSourceRegistry);
  }, [acquirers.hls, audioSourceRegistry]);

  // Coupe tout effet vidéo régie encore actif au changement de chaîne — le pipeline
  // (cf. VideoEffectsPipelineManager) est un singleton MODULE-LEVEL, jamais recréé au
  // remontage de CE composant (lui remonte bien par chaîne, key par chaîne côté
  // LiveStreamManagerBootstrap) : sans ce nettoyage explicite, un effet vidéo activé
  // sur une source dans une chaîne continuait de tourner — remplaçant la piste de
  // cette MÊME source physique (webcam, slot d'écran) — après bascule vers une AUTRE
  // chaîne, sans aucune action de l'opérateur dans la nouvelle chaîne (même symptôme
  // que le cloisonnement des effets audio par chaîne, constaté explicitement).
  useEffect(() => {
    destroyAllVideoEffectsPipelines(registryRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channelUuid]);

  useEffect(() => {
    const unsub = registry.subscribe((event) => {
      if (event.type !== 'stream-updated') return;
      const sourceId = event.sourceId;
      if (activeSourceRef.current !== sourceId) return;
      const newStream = event.source.stream;
      if (!newStream) return;
      setFinalStreamRef.current(newStream);
    });
    return () => unsub();
  }, [registry]);

  // Nom de la chaîne pour l'onglet Logs : par ref, le callback de la sonde ne doit pas
  // changer d'identité à chaque renommage.
  const channelNameForLogsRef = useRef<string | undefined>(undefined);
  // Sources HLS libres déjà journalisées « connectées » (cf. le callback de la sonde).
  const loggedHlsConnectedRef = useRef<Set<string>>(new Set());
  channelNameForLogsRef.current = broadcastingChannel?.name;

  const hlsProber = useHlsProber(useCallback((sourceId: string, status: HlsProbeStatus, persistent: boolean) => {
    // Journalise la connexion/déconnexion des sources RTMP (persistent === true,
    // cf. useHlsProber), Xtream (config.xtream renseigné) et HLS libres dans l'onglet
    // Logs du créateur — jamais pour un simple 'probing'.
    //
    // Gabarit commun à toutes les sources externes : le nom et la chaîne, à la connexion
    // comme à la déconnexion. Une source HLS libre n'est journalisée « déconnectée »
    // que si elle avait été journalisée « connectée » : un flux qui n'a jamais répondu
    // (URL fausse, encodeur absent) n'a pas à laisser une déconnexion sans connexion.
    if (status === 'ok' || status === 'failed') {
      const adminId = authentication.admin?.id;
      const source   = registryRef.current.get(sourceId);
      const config   = source?.config;
      const isRtmp   = persistent;
      const isXtream = !!config && config.type === 'hls' && !!config.xtream;
      // Les flux propres à la chaîne (« Playlist » hls-video-*, « OBS, RTMP... » hls-live-*) ne
      // sont pas des sources ajoutées par le créateur : jamais journalisés ici.
      const isHlsFree = !persistent && !!config && config.type === 'hls' && !config.xtream
                        && !sourceId.startsWith('hls-video-') && !sourceId.startsWith('hls-live-');

      const loggedConnected = loggedHlsConnectedRef.current;
      const skipHlsFree = isHlsFree && (status === 'ok' ? false : !loggedConnected.has(sourceId));
      if (isHlsFree) { if (status === 'ok') loggedConnected.add(sourceId); else loggedConnected.delete(sourceId); }

      if (adminId && (isRtmp || isXtream || isHlsFree) && !skipHlsFree) {
        const kind = isRtmp ? 'Caméra RTMP' : isXtream ? 'Source Xtream' : 'Source HLS';
        const chan = channelNameForLogsRef.current;
        createAdminActionLog(
          adminId,
          isRtmp ? 'RtmpInput' : isXtream ? 'XtreamSource' : 'HlsSource',
          status === 'ok' ? 'Connexion' : 'Deconnexion',
          status === 'ok' ? 'Connected' : 'Disconnected',
          `${kind} : ${source?.label ?? sourceId}` + (chan ? ` — Chaîne : ${chan}` : ''),
        );
      }
    }
    if (status !== 'failed') return;
    registryRef.current.forceRelease(sourceId);
    // Une caméra RTMP absente, ou le flux « OBS, RTMP... » d'une chaîne sur
    // laquelle personne ne publie, ne sont pas des sources en panne : leur
    // encodeur peut repartir à tout instant. Les éteindre obligeait le
    // réalisateur à rallumer le slot à la main pour les voir revenir — la
    // sonde, elle, continue en veille (cf. useHlsProber).
    if (persistent) return;
    setPoweredOffSlots(prev => new Set(Array.from(prev).concat(sourceId)));
  }, [authentication.admin?.id]));

  const scenePipelineManager = useScenePipelineManager(registry);

  const sceneVideoSources = useMemo(
    () => scenePipelineManager.getSceneVideoSources(activeSceneLayoutIds, layoutSlots),
    [activeSceneLayoutIds, layoutSlots]
  );

  const _localCurrentStreamRef = useRef<MediaStream | null>(null);
  const _localCanvasStreamRef  = useRef<MediaStream | null>(null);
  const _localAudioMixerRef    = useRef<MediaStreamAudioDestinationNode | null>(null);
  const _localWhipStatsRef     = useRef<ReturnType<typeof setInterval> | null>(null);
  const _localWsRef                 = useRef<WebSocket | null>(null);
  const _localIceBufRef        = useRef<Map<string, RTCIceCandidateInit[]>>(new Map());
  const _localRemoteDescRef    = useRef<Map<string, boolean>>(new Map());
  const _localRemoteConnsRef   = useRef<Map<string, RemoteConnection>>(remoteConnections);

  const currentStreamRef         = (_sharedRefs?.currentStreamRef         ?? _localCurrentStreamRef) as React.MutableRefObject<MediaStream | null>;
  const canvasStreamRef          = (_sharedRefs?.canvasStreamRef          ?? _localCanvasStreamRef)  as React.MutableRefObject<MediaStream | null>;
  const audioMixerDestRef        = (_sharedRefs?.audioMixerDestRef        ?? _localAudioMixerRef)    as React.MutableRefObject<MediaStreamAudioDestinationNode | null>;
  const whipStatsIntervalRef     = (_sharedRefs?.whipStatsIntervalRef     ?? _localWhipStatsRef)     as React.MutableRefObject<ReturnType<typeof setInterval> | null>;
  const signalingWsRef           = (_sharedRefs?.wsRef                    ?? _localWsRef)                  as React.MutableRefObject<WebSocket | null>;
  const iceCandidateBufferRef    = (_sharedRefs?.iceCandidateBufferRef    ?? _localIceBufRef)        as React.MutableRefObject<Map<string, RTCIceCandidateInit[]>>;
  const remoteDescSetRef         = (_sharedRefs?.remoteDescSetRef         ?? _localRemoteDescRef)    as React.MutableRefObject<Map<string, boolean>>;
  const remoteConnectionsRef     = (_sharedRefs?.remoteConnectionsRef     ?? _localRemoteConnsRef)   as React.MutableRefObject<Map<string, RemoteConnection>>;

  useEffect(() => { currentStreamRef.current  = currentStream;  }, [currentStream]);

  // Bandeau du sondage à l'antenne : écrit par PollsDock à chaque relevé, lu par la boucle de composition.
  const pollOverlayRef = useRef<PollOverlay | null>(null);

  const {
    startCanvasForStream, initCanvasStream, initAudioMixer, attachMixSources,
    getActiveSourceEl, releaseSourceElements,
    stopCompositingLoop,
    toggleImageSlot, triggerSoundSlot, startVideoOverlay, triggerJingleSlot,
    pipVideoEls, imageSlotsRef, soundSlotsRef, videoOverlaySlotsRef, jingleSlotsRef,
    loadedAudioBuffersRef,
    setDeckTransitionProgress,
  } = useCanvasPipeline({
    compositingCanvasRef, canvasPreviewRef, sourceVideoRef, sourceVideoAltRef,
    canvasStreamRef, audioMixerDestRef, currentStreamRef,
    useFiligrane: broadcastingChannel?.ownerUseFiligrane ?? false,
    pollOverlayRef,
  });

  const startCanvasForStreamRef = useRef(startCanvasForStream);
  useEffect(() => { startCanvasForStreamRef.current = startCanvasForStream; }, [startCanvasForStream]);

  const startWebRTCStreamingRef = useRef<(opts?: { silent?: boolean; retryDeadline?: number; networkRetry?: boolean }) => Promise<void>>(async () => {});
  const startWebRTCStreaming    = useCallback((opts?: { silent?: boolean }) => startWebRTCStreamingRef.current(opts), []);

  // ── Mesure des débits ──────────────────────────────────────────────────────
  //
  // Démarrée avec la régie et jamais arrêtée tant qu'elle est ouverte : sa raison
  // d'être première n'est pas l'onglet Débits mais la ligne qu'elle écrit dans les
  // journaux toutes les 30 s. On vient la relire APRÈS une prise ratée — elle doit
  // donc avoir couru pendant, sans que personne ait pensé à l'allumer.
  useEffect(() => startBandwidthMonitor(), []);

  // Nom du fichier de métriques de CETTE régie. Il porte la chaîne, pas le poste :
  // Symfony préfixe déjà chaque nom par l'IP de l'émetteur, donc deux régies sur
  // la même chaîne restent dans deux fichiers distincts.
  useEffect(() => {
    setMetricsChannel(`regie_${channelUuid ?? 'sans-chaine'}`);
  }, [channelUuid]);

  // La régie se nomme dans ses journaux : la chaîne sur laquelle elle travaille,
  // et le poste qu'elle tient. Sans ça, tous les fichiers d'un même administrateur
  // se confondaient, quelle que soit la chaîne ouverte.
  useEffect(() => {
    setLoggerIdentity({
      channel: broadcastingChannel?.name || channelUuid || '',
      poste  : 'Regie',
    });
  }, [broadcastingChannel?.name, channelUuid]);

  // Rechargement de la liste des sources externes. Par ref : la fonction a
  // besoin de handleRemoveSource, défini bien plus bas — la déclarer ici en
  // dépendrait avant son initialisation.
  const reloadExternalSourcesRef = useRef<(reason?: string) => void>(() => {});
  // Relance de la Playlist après un rechargement de la mire (définie après handleSlotPowerToggle).
  const restartPlaylistAfterMireRef = useRef<() => void>(() => {});

  // Superadmin chez un AUTRE créateur : observateur discret. Il ne prend jamais la main et le
  // créateur n'en est pas averti (le serveur ne l'annonce pas). Tant que le serveur n'a pas dit
  // si le compte est superadmin, 'pending' retient la connexion : se connecter d'abord comme
  // porteur volerait la main au créateur avant de basculer.
  const [ isSuperAdmin, setIsSuperAdmin ] = useState<boolean | null>(null);
  useEffect(() => {
    const id = authentication.admin?.id;
    if (!id) { setIsSuperAdmin(false); return; }
    let cancelled = false;
    getAdminEventContext(id).then(r => { if (!cancelled) setIsSuperAdmin(r.ok && r.data.superadmin); });
    return () => { cancelled = true; };
  }, [authentication.admin?.id]);
  const channelOwnerId     = broadcastingChannel?.ownerId;
  const chezUnAutreCreateur = channelOwnerId != null && channelOwnerId !== authentication.admin?.id;
  const observerMode: 'pending' | 'silent' | 'normal' =
    !chezUnAutreCreateur ? 'normal' : isSuperAdmin === null ? 'pending' : isSuperAdmin ? 'silent' : 'normal';

  const setup = useLiveStreamSetup({
    _sharedRefs, externalSources, channelActive, observerMode,
    channelStatus       : broadcastingChannelStatus,
    // L'ADRESSE PUBLIQUE de la chaîne, pas son uuid. UN SEUL point d'entrée pour
    // 19 usages en aval : signalisation, WHIP, HLS de retour, sources SFU. C'est
    // ce qui rend la bascule tenable — et c'est aussi pourquoi elle attendait que
    // le backend WebRTC d'HAProxy sache traduire le préfixe : la même variable
    // alimente la PUBLICATION et la LECTURE, on ne pouvait pas en basculer une
    // sans l'autre.
    channelStreamPath   : broadcastingChannel?.publicRef,
    channelName         : broadcastingChannel?.name,
    channelUuid,
    onHandleDeckSwitch  : (source) => handleDeckSwitch(source),
    onToggleImageSlot   : toggleImageSlot,
    onTriggerSoundSlot  : triggerSoundSlot,
    onStartVideoOverlay : startVideoOverlay,
    onTriggerJingleSlot : triggerJingleSlot,
    onHandleDeckCutRef  : deckCutCallbackRef,
    onRecordingLimitReachedRef : recordingLimitReachedRef,
    onAngleAudioStateRef       : angleAudioStateRef,
    imageSlotsRef, soundSlotsRef, videoOverlaySlotsRef, jingleSlotsRef,
    deckSlotsRef, startWebRTCStreaming,
    canvasStreamRef, audioMixerDestRef, currentStreamRef,
    sceneVideoSources,
    reloadExternalSourcesRef,
  });

const setupRef = useRef(setup);
useEffect(() => { 
//console.log ('[LiveStreamManager] setupRef.current = ', setupRef.current)
setupRef.current = setup; }, [setup]);

  const {
    devices, remoteDevices, previewStreams, setPreviewStreams, previewStreamsRef,
    deckPreviewSourceId, setDeckPreviewSourceId, deckPreviewVideoRef,
    deckSlots, deckSlotLayouts,
    keyMap, handleKeyMapChange,
    handleDeckSlotChange,
    handleDeckSlotsSwap,
    reserveDeckSlot,
    resetDeckToDefaults,
    remapSourceIds,
    markSourcesKnown,
    deleteDeviceNow,
    handleDeckSlotLayoutChange,
    loadDevices, handleRegisterDevice, handleDeleteDevice, handleDisconnectDevice, handleRegeneratePassword,
    handleSetDirector, directorDevices, directorDevicesRef, sendDirectorDeck,
    deviceNetworkQuality,
    deviceGeo,
    setDeviceGeoWatch,
    setDeviceMetricsLog,
    handleDeckPreview,
    startPreviewForSource,
    buildSignalingRefs,
    buildSignalingCallbacks,
    resetDeckInit,
  } = setup;

  // ── Sonde de saturation du thread principal ───────────────────────────────
  // Montée avec la régie, retirée avec elle. Silencieuse tant que le thread
  // rend la main dans les temps ; c'est la seule mesure qui survit à un
  // figement, puisqu'elle mesure le figement lui-même.
  useEffect(() => startMainThreadProbe('régie'), []);

  // ── Capture mic intercomm (une seule fois au montage) ─────────────────────
  useEffect(() => {
    let stream: MediaStream | null = null;
    navigator.mediaDevices.getUserMedia({ audio: true, video: false })
      .then(s => {
        stream = s;
        intercommMicStreamRef.current = s;
        setIntercommMicStream(s);
        addSdpLogRegie({ direction: 'info', type: 'event', label: '🎙 Mic intercomm régie capturé' });
      })
      .catch(err => {
        console.warn('[Intercomm] Capture mic régie échouée:', err?.message);
        addSdpLogRegie({ direction: 'info', type: 'event', label: `🎙 Mic intercomm régie indisponible: ${err?.message}` });
      });
    return () => {
      stream?.getTracks().forEach(t => t.stop());
      intercommMicStreamRef.current = null;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Callback activation intercomm (appelé depuis IntercommView) ───────────
  const handleIntercommActivate = useCallback((
    active           : boolean,
    selectedDeviceIds: Set<string>,
  ) => {
    setRegieIntercommActive(
      active,
      selectedDeviceIds,
      intercommMapRef,
      signalingWsRef,
      { addSdpLog: addSdpLogRegie },
    );
  }, [addSdpLogRegie, signalingWsRef]);

  // ── Callback changement de micro (appelé depuis IntercommView) ────────────
  // Ouvre un nouveau getUserMedia, met à jour le stream et fait replaceTrack
  // sur tous les senders intercomm existants sans renegociation.
  const handleIntercommMicChange = useCallback(async (selectedMicId: string) => {
    let newStream: MediaStream | null = null;
    try {
      const constraint = selectedMicId
        ? { audio: { deviceId: { exact: selectedMicId } }, video: false as const }
        : { audio: true,                                   video: false as const };
      newStream = await navigator.mediaDevices.getUserMedia(constraint);
    } catch (err) {
      console.warn('[Intercomm] Changement mic échoué:', (err as any)?.message);
      addSdpLogRegie({ direction: 'info', type: 'event', label: `🎙 Changement mic intercomm échoué: ${(err as any)?.message}` });
      return;
    }

    intercommMicStreamRef.current?.getTracks().forEach(t => t.stop());
    intercommMicStreamRef.current = newStream;
    setIntercommMicStream(newStream);
    addSdpLogRegie({ direction: 'info', type: 'event', label: `🎙 Mic intercomm → ${selectedMicId || 'défaut'}` });

    const newAudioTrack = newStream.getAudioTracks()[0];
    if (!newAudioTrack) return;

    intercommMapRef.current.forEach((entry, devId) => {
      if (!entry.audioSender) return;
      const currentEnabled = entry.audioSender.track?.enabled ?? false;
      const clone = newAudioTrack.clone();
      clone.enabled = currentEnabled;
      entry.micTrack = newAudioTrack;
      entry.audioSender.replaceTrack(clone)
        .catch(e => console.warn(`[Intercomm] replaceTrack mic échoué pour ${devId}:`, e));
    });
  }, [addSdpLogRegie]);

  // ── Intercomm : init pour un device ──────────────────────────────────────
  // Appelé depuis handleSignalingMessageImpl via onDeviceConnected
  const initIntercommForDevice = useCallback((deviceId: string) => {
    if (knownIntercommDevicesRef.current.has(deviceId)) return;
    knownIntercommDevicesRef.current.add(deviceId);

    addSdpLogRegie({ direction: 'info', type: 'event', label: `🎙 Init intercomm pour device ${deviceId}` });

    const doInit = (mic: MediaStream | null) => {
      initRegieIntercommForDevice(
        deviceId,
        mic,
        intercommMapRef,
        signalingWsRef,
        {
          addSdpLog: addSdpLogRegie,
          onDeviceReceiveStreamReady: (did, stream) => {
            setIntercommDeviceStreams(prev => {
              const next = new Map(prev);
              next.set(did, stream);
              return next;
            });
          },
        },
      ).catch(err => {
        console.warn('[Intercomm] initRegieIntercommForDevice échoué:', err?.message);
        knownIntercommDevicesRef.current.delete(deviceId);
      });
    };

    const waitForMicAndInit = (attempts = 0) => {
      // Annulé si cleanupIntercommForDevice a été appelé entretemps
      if (!knownIntercommDevicesRef.current.has(deviceId)) return;

      intercommInitPendingTimersRef.current.delete(deviceId);

      if (intercommMicStreamRef.current) {
        addSdpLogRegie({ direction: 'info', type: 'event', label: `🎙 Init intercomm ${deviceId} — mic dispo` });
        doInit(intercommMicStreamRef.current);
      } else if (attempts < 20) {
        const tid = setTimeout(() => waitForMicAndInit(attempts + 1), 250);
        intercommInitPendingTimersRef.current.set(deviceId, tid);
      } else {
        addSdpLogRegie({ direction: 'info', type: 'event', label: `🎙 Init intercomm ${deviceId} — mic toujours null après 5s, init sans mic` });
        doInit(null);
      }
    };

    // 1s pour laisser la PC principale démarrer
    const initialTid = setTimeout(() => waitForMicAndInit(), 1000);
    intercommInitPendingTimersRef.current.set(deviceId, initialTid);
  }, [addSdpLogRegie, signalingWsRef]);

  // ── On air : renvoie le statut si ce device était déjà à l'antenne avant sa reconnexion ──
  const resendOnAirIfNeeded = useCallback((deviceId: string) => {
    if (onAirDeviceIdRef.current !== deviceId) return;
    const ws = signalingWsRef.current;
    if (ws?.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ type: 'on_air', target: deviceId, active: true }));
    }
  }, [signalingWsRef]);

  // ── Intercomm : cleanup pour un device ───────────────────────────────────
  const cleanupIntercommForDevice = useCallback((deviceId: string) => {
    knownIntercommDevicesRef.current.delete(deviceId);
    const pendingTid = intercommInitPendingTimersRef.current.get(deviceId);
    if (pendingTid !== undefined) {
      clearTimeout(pendingTid);
      intercommInitPendingTimersRef.current.delete(deviceId);
    }
    closeRegieIntercommForDevice(deviceId, intercommMapRef, { addSdpLog: addSdpLogRegie });
    setIntercommDeviceStreams(prev => { const next = new Map(prev); next.delete(deviceId); return next; });
    setIntercommSpeakingDevices(prev => { const next = new Set(prev); next.delete(deviceId); return next; });
  }, [addSdpLogRegie]);

  const audioMetering = useAudioMetering({
    deckSlots,
    selectedAudioDeviceIds,
    remoteConnections,
    screenShareStreams,
    getSourceAudioConfig,
  });

  useEffect(() => {
    setSelectedAudioDeviceIds(prev => {
      const next = new Map(prev);
      let changed = false;
      deckSlots.forEach(slot => {
        if (!slot) return;
        if (next.has(slot.id)) return;
        let derivedDeviceId: string | null = null;
        if (slot.type === 'device') {
          const videoDevice = devices.video.find(d => d.deviceId === slot.deviceId);
          if (videoDevice?.groupId) {
            const matchingAudio = devices.audio.find(d => d.groupId === videoDevice.groupId);
            derivedDeviceId = matchingAudio?.deviceId ?? null;
          }
        } else if (slot.type === 'url') {
          derivedDeviceId = `hls-audio-${slot.id}`;
        } else if (slot.type === 'remote') {
          const deviceId = slot.id.replace('remote-', '');
          derivedDeviceId = `remote-audio-${deviceId}`;
        } else if (slot.type === 'screen') {
          derivedDeviceId = `screen-audio-${slot.id}`;
        }
        if (derivedDeviceId) { next.set(slot.id, derivedDeviceId); changed = true; }
      });
      return changed ? next : prev;
    });
  }, [deckSlots, devices.video, devices.audio]);

  const {
    disconnectAudioForSlot,
    changeAudioDevice: changeAudioDeviceForSlot,
  } = audioMetering;

  // Signal de rafraîchissement des dérivés de l'AudioSourceRegistry, qui n'est
  // pas réactif. Remplace l'ancienne sonde `sourceAudioLevels` : ne change que
  // lorsqu'une source est branchée ou retirée, au lieu de 60 fois par seconde.
  const meteredSourceIds = useMeteredSourceIds();

  const [audioSwitchState, setAudioSwitchState] = useState<AudioSwitchState>({
    mode               : 'source',
    fixedAudioSourceId : '',
  });

/*
  useEffect(() => {
    const { mode, fixedAudioSourceId } = audioSwitchState;
    deckSlots.forEach(slot => {
      if (!slot) return;
      const regId = `audio-${slot.id}`;
      if (!audioSourceRegistry.has(regId)) return;
      audioSourceRegistry.setOutputGain(regId, 1);
      audioSourceRegistry.setMonitorGain(regId, 0.0001);
    });
    if (mode === 'source') {
      if (activeSourceRef.current) {
        const regId = `audio-${activeSourceRef.current}`;
        if (audioSourceRegistry.has(regId)) {
          audioSourceRegistry.setMonitorGain(regId, finalMuted ? 0 : 1);
        }
        initAudioMixer(activeSourceRef.current, null);
      }
      return;
    }
    if (!fixedAudioSourceId) return;
    let fixedSlotId: string | null = null;
    if (fixedAudioSourceId.startsWith('remote:')) {
      fixedSlotId = `remote-${fixedAudioSourceId.slice('remote:'.length)}`;
    } else if (fixedAudioSourceId.startsWith('dev:')) {
      const deviceId = fixedAudioSourceId.slice('dev:'.length);
      for (const slot of deckSlots) {
        if (!slot) continue;
        if (selectedAudioDeviceIds.get(slot.id) === deviceId) { fixedSlotId = slot.id; break; }
      }
    } else if (fixedAudioSourceId.startsWith('hls:')) {
      fixedSlotId = fixedAudioSourceId.slice('hls:'.length);
    }
    if (fixedSlotId) {
      const regId = `audio-${fixedSlotId}`;
      if (audioSourceRegistry.has(regId)) {
        audioSourceRegistry.setMonitorGain(regId, finalMuted ? 0 : 1);
      }
      if (activeSourceRef.current && activeSourceRef.current !== fixedSlotId) {
        const activeRegId = `audio-${activeSourceRef.current}`;
        if (audioSourceRegistry.has(activeRegId)) {
          audioSourceRegistry.setMonitorGain(activeRegId, 0);
        }
      }
      initAudioMixer(fixedSlotId, null);
    }
  }, [audioSwitchState, audioSourceRegistry, deckSlots, selectedAudioDeviceIds, initAudioMixer, selectedVideoSource, finalMuted]);
*/

// ── Résolution "option du Switch audio" → source audio réelle ───────────────
// Une option du Switch audio se branche de deux façons :
//   • 'deck'       — elle correspond à un slot du deck, dont useAudioMetering a
//                    déjà créé l'entrée dans l'AudioSourceRegistry. Rien à faire.
//   • 'standalone' — elle ne correspond à aucun slot (micro système inutilisé,
//                    invité connecté mais pas monté sur le deck…). On l'acquiert
//                    à la demande sous un id dédié, uniquement pour le Switch
//                    audio : elle n'apparaît ni au pupitre ni aux VU-mètres.
// `null` = l'option ne désigne plus rien d'exploitable (invité déconnecté, flux
// HLS disparu…) : l'appelant retombe alors sur la source active.

type FixedAudioTarget =
  | { kind: 'deck';       sourceId: string }
  | { kind: 'standalone'; sourceId: string; optionId: string;
      type: 'device' | 'remote' | 'screen' | 'url';
      deviceId?: string; stream?: MediaStream | null };

// Préfixe des sources acquises pour le seul Switch audio. Doit rester distinct
// des ids de slots du deck : l'entrée du registry en dérive (`audio-<id>`).
const STANDALONE_FIXED_AUDIO_PREFIX = 'fixed-audio-';

const describeFixedAudioTarget = useCallback((optionId: string): FixedAudioTarget | null => {
  if (!optionId) return null;

  const inDeck    = (slotId: string) => deckSlots.some(s => s?.id === slotId);
  const standaloneId = `${STANDALONE_FIXED_AUDIO_PREFIX}${optionId.replace(/:/g, '-')}`;

  const remoteStream = (deviceId: string): MediaStream | null => {
    const stream = remoteConnections.get(deviceId)?.stream ?? null;
    return stream && stream.getAudioTracks().length > 0 ? stream : null;
  };
  const registryStream = (sourceId: string): MediaStream | null => {
    const stream = registryRef.current.getStream(sourceId) ?? null;
    return stream && stream.getAudioTracks().length > 0 ? stream : null;
  };

  if (optionId.startsWith('remote:')) {
    const deviceId = optionId.slice('remote:'.length);
    const slotId   = `remote-${deviceId}`;
    if (inDeck(slotId)) return { kind: 'deck', sourceId: slotId };
    const stream = remoteStream(deviceId);
    return stream
      ? { kind: 'standalone', sourceId: standaloneId, optionId, type: 'remote', stream }
      : null;
  }

  if (optionId.startsWith('hls:')) {
    const slotId = optionId.slice('hls:'.length);
    if (inDeck(slotId)) return { kind: 'deck', sourceId: slotId };
    const stream = registryStream(slotId);
    return stream
      ? { kind: 'standalone', sourceId: standaloneId, optionId, type: 'url', stream }
      : null;
  }

  if (optionId.startsWith('dev:')) {
    const deviceId = optionId.slice('dev:'.length);
    for (const slot of deckSlots) {
      if (!slot) continue;
      if (selectedAudioDeviceIds.get(slot.id) === deviceId) return { kind: 'deck', sourceId: slot.id };
    }
    // Ids "étendus" (cf. extendedAudioDevices) : ce ne sont pas des périphériques
    // système, leur flux existe déjà quelque part — inutile de repasser par
    // getUserMedia. Le Switch audio ne les propose que sous la forme
    // `dev:screen-audio-*`, les deux autres restent gérés par prudence : une
    // configuration de régie sauvegardée peut encore en contenir.
    //
    // Le slot d'origine porte déjà ce flux dans le registry — à condition que son
    // audio n'ait pas été redirigé ailleurs au pupitre (auquel cas son entrée
    // contient un autre son, et on acquiert le flux séparément).
    const deckSlotCarrying = (slotId: string) =>
      inDeck(slotId) && !selectedAudioDeviceIds.get(slotId) ? slotId : null;

    if (deviceId.startsWith('screen-audio-')) {
      const slotId = deviceId.slice('screen-audio-'.length);
      const owner  = deckSlotCarrying(slotId);
      if (owner) return { kind: 'deck', sourceId: owner };
      const stream = screenShareStreams.get(slotId) ?? null;
      return stream && stream.getAudioTracks().length > 0
        ? { kind: 'standalone', sourceId: standaloneId, optionId, type: 'screen', stream }
        : null;
    }
    if (deviceId.startsWith('remote-audio-')) {
      const remoteDeviceId = deviceId.slice('remote-audio-'.length);
      const owner          = deckSlotCarrying(`remote-${remoteDeviceId}`);
      if (owner) return { kind: 'deck', sourceId: owner };
      const stream = remoteStream(remoteDeviceId);
      return stream
        ? { kind: 'standalone', sourceId: standaloneId, optionId, type: 'remote', stream }
        : null;
    }
    if (deviceId.startsWith('hls-audio-')) {
      const slotId = deviceId.slice('hls-audio-'.length);
      const owner  = deckSlotCarrying(slotId);
      if (owner) return { kind: 'deck', sourceId: owner };
      const stream = registryStream(slotId);
      return stream
        ? { kind: 'standalone', sourceId: standaloneId, optionId, type: 'url', stream }
        : null;
    }
    // Périphérique système non affecté à un slot : acquis par getUserMedia à la
    // demande (cf. AudioSourceRegistry.borrowFromMicrophone).
    return { kind: 'standalone', sourceId: standaloneId, optionId, type: 'device', deviceId };
  }

  return null;
}, [deckSlots, selectedAudioDeviceIds, remoteConnections, screenShareStreams]);

// Slot du deck correspondant à une option, s'il existe.
const resolveFixedSlotId = useCallback((fixedAudioSourceId: string): string | null => {
  const target = describeFixedAudioTarget(fixedAudioSourceId);
  return target?.kind === 'deck' ? target.sourceId : null;
}, [describeFixedAudioTarget]);

// ── Sources fixes hors deck : acquisition à la demande ──────────────────────
// Une seule à la fois — celle que désigne le Switch audio. On libère la
// précédente dès que la sélection change ou que le mode repasse en "Source",
// sinon un micro resterait ouvert (voyant allumé) sans jamais servir.
const standaloneFixedAudioRef = useRef<{ optionId: string; sourceId: string } | null>(null);
// Option dont l'acquisition vient d'échouer (autorisation micro refusée, flux sans
// piste audio…). L'effet d'acquisition se réexécute à chaque changement du deck ou
// des connexions : sans cette mémoire, une autorisation refusée serait redemandée
// en boucle. On ne retente qu'après un changement de sélection.
const failedFixedAudioOptionRef = useRef<string | null>(null);
const [standaloneFixedAudioSourceId, setStandaloneFixedAudioSourceId] = useState<string | null>(null);

// ── Source fixe tombée en cours de direct (2026-11-03) ──────────────────────
// Le slot d'une source fixe peut rester au deck alors que son son a disparu :
// micro débranché, iPhone en Continuité déconnecté du Mac… Le 03/11, le direct
// d'Akinax est parti 15 minutes en silence (−85 dB) : le Switch audio « Fixe »
// pointait le micro de l'iPhone, déconnecté à 17:44:30, et rien ne le signalait.
// On sonde donc la source fixe : plus aucune piste audio vivante (ou entrée
// absente du registry au-delà d'un délai de grâce) = indisponible. Elle est
// alors traitée comme non résoluble — repli sur la source active, bandeau dans
// la régie — et reprend la main d'elle-même dès que son son revient.
const FIXED_AUDIO_CHECK_MS         = 2_000;
const FIXED_AUDIO_MISSING_GRACE_MS = 12_000;   // > MIXER_ATTACH_TIMEOUT_MS (10 s)
const [downFixedAudio, setDownFixedAudio] = useState<{ optionId: string; label: string } | null>(null);
const fixedAudioMissingSinceRef = useRef<{ optionId: string; since: number } | null>(null);

useEffect(() => {
  const { mode, fixedAudioSourceId } = audioSwitchState;
  if (mode !== 'fixe' || !fixedAudioSourceId) {
    fixedAudioMissingSinceRef.current = null;
    setDownFixedAudio(null);
    return;
  }
  const check = () => {
    const target = describeFixedAudioTarget(fixedAudioSourceId);
    let down = false;
    let label = fixedAudioSourceId;
    if (target) {
      const sourceId = target.sourceId;
      label = deckSlots.find(s => s?.id === sourceId)?.label || label;
      const streams = [
        audioSourceRegistry.getStream(`audio-${sourceId}`),
        registryRef.current.getStream(sourceId),
      ].filter((stream): stream is MediaStream => !!stream);
      const tracks = streams.flatMap(stream => stream.getAudioTracks());
      if (streams.length > 0) {
        // Un flux connu : vivant s'il porte au moins une piste audio vivante.
        fixedAudioMissingSinceRef.current = null;
        down = !tracks.some(track => track.readyState === 'live');
      } else if (target.kind === 'deck' && !audioSourceRegistry.has(`audio-${sourceId}`)) {
        // Aucun flux ni entrée : peut-être une acquisition en cours. Indisponible
        // seulement si ça dure (le rattrapage d'initAudioMixer abandonne à 10 s).
        const missing = fixedAudioMissingSinceRef.current;
        if (!missing || missing.optionId !== fixedAudioSourceId) {
          fixedAudioMissingSinceRef.current = { optionId: fixedAudioSourceId, since: Date.now() };
        } else {
          down = Date.now() - missing.since >= FIXED_AUDIO_MISSING_GRACE_MS;
        }
      } else {
        // Entrée présente sans flux exposé (lecture HLS par Web Audio…) : on ne
        // sait pas juger, on ne touche à rien.
        fixedAudioMissingSinceRef.current = null;
      }
    }
    setDownFixedAudio(prev => {
      if (!down) {
        if (prev) console.warn(`[AUDIO-SWITCH] source fixe « ${prev.label} » de nouveau disponible — retour sur elle`);
        return null;
      }
      if (prev?.optionId === fixedAudioSourceId) return prev;
      console.warn(`[AUDIO-SWITCH] source fixe « ${label} » sans son vivant (${fixedAudioSourceId}) — repli sur la source active`);
      return { optionId: fixedAudioSourceId, label };
    });
  };
  check();
  const timer = setInterval(check, FIXED_AUDIO_CHECK_MS);
  return () => clearInterval(timer);
}, [audioSwitchState, describeFixedAudioTarget, audioSourceRegistry, deckSlots]);

// Source audio réellement branchable pour une option : le slot du deck, ou la
// source hors deck une fois acquise (l'acquisition est asynchrone — getUserMedia
// peut demander une autorisation). Tant qu'elle ne l'est pas, on renvoie null et
// l'appelant conserve la source active. Une source fixe sans son vivant (voir
// ci-dessus) est elle aussi non branchable.
const resolveFixedAudioSourceId = useCallback((fixedAudioSourceId: string): string | null => {
  if (downFixedAudio && downFixedAudio.optionId === fixedAudioSourceId) return null;
  const target = describeFixedAudioTarget(fixedAudioSourceId);
  if (!target) return null;
  if (target.kind === 'deck') return target.sourceId;
  return audioSourceRegistry.has(`audio-${target.sourceId}`) ? target.sourceId : null;
}, [describeFixedAudioTarget, audioSourceRegistry, downFixedAudio]);

const wasMixRef = useRef(false);
useEffect(() => {
  const { mode, fixedAudioSourceId } = audioSwitchState;

  console.log('[AUDIO-SWITCH] mode=', mode,
    'activeSource=', activeSourceRef.current,
    'selectedVideoSource=', selectedVideoSource,
    'fixedAudioSourceId=', fixedAudioSourceId,
  );

  // Toutes les sources du deck repartent muettes côté monitoring local ; on ne
  // rouvre ensuite que celle qui doit être audible.
  deckSlots.forEach(slot => {
    if (!slot) return;
    const regId = `audio-${slot.id}`;
    if (!audioSourceRegistry.has(regId)) return;
    audioSourceRegistry.setOutputGain(regId, 1);
    audioSourceRegistry.setMonitorGain(regId, 0.0001);
  });

  // Mode "Mix" : géré par l'effet dédié ci-dessous. En sortant de ce mode, on
  // libère d'abord toutes les entrées du bus ; les branches Source/Fixe
  // rebranchent ensuite la leur.
  if (mode === 'mix') { wasMixRef.current = true; return; }
  if (wasMixRef.current) { wasMixRef.current = false; attachMixSources([]); }

  const applySourceMode = () => {
    if (!activeSourceRef.current) {
      console.log('[AUDIO-SWITCH] aucune source active — rien à brancher');
      return;
    }
    const regId = `audio-${activeSourceRef.current}`;
    if (audioSourceRegistry.has(regId)) {
      audioSourceRegistry.setMonitorGain(regId, finalMuted ? 0 : 1);
    }
    initAudioMixer(activeSourceRef.current, null);
  };

  if (mode === 'source') { applySourceMode(); return; }

  const fixedSlotId = resolveFixedAudioSourceId(fixedAudioSourceId);

  if (!fixedSlotId) {
    // Source fixe non résoluble : slot retiré du deck, invité déconnecté, régie
    // réinitialisée… ou source hors deck pas encore acquise (l'effet d'acquisition
    // ci-dessous relancera celui-ci une fois l'entrée créée). Sans ce repli, on
    // s'arrêtait juste après la boucle de coupure ci-dessus : plus aucun son nulle
    // part, et aucun chemin pour le rétablir tant que le toggle restait sur "Fixe".
    console.warn('[AUDIO-SWITCH] source fixe non résoluble :', fixedAudioSourceId || '(vide)',
      '— repli sur la source active');
    applySourceMode();
    return;
  }

  console.log('[AUDIO-SWITCH] mode=fixe → fixedSlotId=', fixedSlotId);

  const regId = `audio-${fixedSlotId}`;
  if (audioSourceRegistry.has(regId)) {
    audioSourceRegistry.setMonitorGain(regId, finalMuted ? 0 : 1);
  }
  if (activeSourceRef.current && activeSourceRef.current !== fixedSlotId) {
    const activeRegId = `audio-${activeSourceRef.current}`;
    if (audioSourceRegistry.has(activeRegId)) {
      audioSourceRegistry.setMonitorGain(activeRegId, 0);
    }
  }
  initAudioMixer(fixedSlotId, null);
  // standaloneFixedAudioSourceId : sonde de rafraîchissement. L'AudioSourceRegistry
  // n'est pas réactif — cet état change au moment précis où une source hors deck
  // vient d'être acquise, ce qui relance cet effet pour la rendre audible.
}, [audioSwitchState, audioSourceRegistry, deckSlots, resolveFixedAudioSourceId, initAudioMixer,
    selectedVideoSource, finalMuted, standaloneFixedAudioSourceId, attachMixSources]);


// ── Audio des angles de la multivue : celui du programme quand le Switch audio est « Fixe » ──
// En mode « Fixe » (avec une source fixe réellement branchée), l'audio du programme EST la source
// fixe : on demande au microservice que TOUS les angles de la multivue le portent. Dans les autres
// modes, chaque angle garde l'audio de sa source. Voir dataTypes/liveStreamManagment/angleAudio.ts.
const angleAudio = angleAudioMode({
  multiviewActive    : multiviewActive,
  switchMode         : audioSwitchState.mode,
  fixedSourceResolved: resolveFixedAudioSourceId(audioSwitchState.fixedAudioSourceId) !== null,
});
// Le message ne concerne que les angles servis par le microservice (HLS AAC) et les téléphones (jumeaux
// `-prog`, voir plus bas). Les angles que la RÉGIE publie elle-même — sources locales, lues directement
// dans MediaMTX par les spectateurs — reçoivent l'audio du programme à leur publication : voir l'effet de
// publication des angles plus bas. L'appel du hook est après multiviewAngleSlots (il en a besoin).


// ── Acquisition/libération des sources fixes hors deck ──────────────────────
// Déclaré après l'effet ci-dessus : lors d'un retour en mode "Source", celui-ci
// a déjà rebranché le mixer sur la source active quand on libère l'entrée.
useEffect(() => {
  const { mode, fixedAudioSourceId } = audioSwitchState;
  const target  = mode === 'fixe' ? describeFixedAudioTarget(fixedAudioSourceId) : null;
  const wanted  = target?.kind === 'standalone' ? target : null;
  const current = standaloneFixedAudioRef.current;

  if (current && current.optionId !== wanted?.optionId) {
    console.log('[AUDIO-SWITCH] libération de la source fixe hors deck', current.optionId);
    disconnectAudioForSource(current.sourceId);
    standaloneFixedAudioRef.current = null;
    setStandaloneFixedAudioSourceId(prev => (prev === current.sourceId ? null : prev));
  }

  if (failedFixedAudioOptionRef.current && failedFixedAudioOptionRef.current !== wanted?.optionId) {
    failedFixedAudioOptionRef.current = null;
  }

  if (!wanted || standaloneFixedAudioRef.current) return;
  if (failedFixedAudioOptionRef.current === wanted.optionId) return;

  console.log('[AUDIO-SWITCH] acquisition de la source fixe hors deck', wanted.optionId,
    'type=', wanted.type);
  standaloneFixedAudioRef.current = { optionId: wanted.optionId, sourceId: wanted.sourceId };

  connectAudioForSource(wanted.sourceId, wanted.type, wanted.stream ?? null, wanted.deviceId)
    .then(() => {
      // La sélection a pu changer pendant l'acquisition (getUserMedia est lent, et
      // l'autorisation navigateur peut prendre plusieurs secondes) : dans ce cas
      // l'entrée tout juste créée n'a plus de raison d'exister.
      if (standaloneFixedAudioRef.current?.sourceId !== wanted.sourceId) {
        disconnectAudioForSource(wanted.sourceId);
        return;
      }
      if (!audioSourceRegistry.has(`audio-${wanted.sourceId}`)) {
        console.warn('[AUDIO-SWITCH] source fixe hors deck sans piste audio exploitable :',
          wanted.optionId, '— repli sur la source active');
        failedFixedAudioOptionRef.current = wanted.optionId;
        standaloneFixedAudioRef.current   = null;
        return;
      }
      setStandaloneFixedAudioSourceId(wanted.sourceId);
    })
    .catch(err => {
      console.warn('[AUDIO-SWITCH] acquisition impossible pour', wanted.optionId,
        '— repli sur la source active, sans nouvelle tentative avant changement de sélection', err);
      failedFixedAudioOptionRef.current = wanted.optionId;
      if (standaloneFixedAudioRef.current?.sourceId === wanted.sourceId) {
        standaloneFixedAudioRef.current = null;
      }
    });
}, [audioSwitchState, describeFixedAudioTarget, connectAudioForSource, disconnectAudioForSource,
    audioSourceRegistry]);

// Le micro d'une source fixe hors deck reste ouvert tant qu'on ne le libère pas :
// il doit mourir avec la régie, sans attendre la destruction du registry.
useEffect(() => () => {
  const current = standaloneFixedAudioRef.current;
  if (!current) return;
  disconnectAudioForSource(current.sourceId);
  standaloneFixedAudioRef.current = null;
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, []);



  const stopStreamingImpl = useCallback(() => {
    apiStopWhipStreaming({ peerConnectionRef, whipStatsIntervalRef }, { setIsStreaming, setWhipInfo });
  }, [peerConnectionRef, setIsStreaming, setWhipInfo]);

  const stopStreamingImplRef = useRef(stopStreamingImpl);
  useEffect(() => { stopStreamingImplRef.current = stopStreamingImpl; }, [stopStreamingImpl]);

  const defaultStreamSourceRef = useRef<StreamSource>(defaultStreamSource ?? 'video');
  useEffect(() => { defaultStreamSourceRef.current = defaultStreamSource ?? 'video'; }, [defaultStreamSource]);

  // ── Avertissement fermeture pendant une diffusion Régie (webrtc) ou un upload en cours ──
  // Les navigateurs modernes ignorent le texte personnalisé de `returnValue`
  // (mesure anti-abus) et affichent leur propre message générique — c'est la
  // forme finale possible de cet avertissement, pas une variante à enrichir.
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      const isRegieStreaming = streamSource === 'webrtc' && isStreamingRef.current;
      if (!isRegieStreaming && !uploadInProgressRef.current) return;
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [streamSource]);

  // ── Heartbeat régie ──────────────────────────────────────────────────────
  // Signale au microservice qu'un navigateur diffuse activement en WebRTC pour
  // cette chaîne. Monté seulement pendant une diffusion réelle (pas juste
  // panneau Régie ouvert) pour que "room non vide" signifie sans ambiguïté
  // "publisher webrtc actif" côté microservice (cf. RegieSyncRoom/watchdog).
  const regieSyncWsBase = useMemo(() => settings.serverUrl
    .replace(/\/$/, '')
    .replace(/^https:\/\//, 'wss://')
    .replace(/^http:\/\//, 'ws://'), [settings.serverUrl]);

  // `useRegieSync` compose `${wsBase}/${…}/regie/sync` : c'est une ADRESSE, qui
  // traverse HAProxy comme le reste de `/regie`. Vérifié sur le proxy :
  // `/<référence>/regie/link-quality` rend 200, `/<uuid>/…` rend 404.
  const regieHeartbeatAddress = (streamSource === 'webrtc' && isStreaming)
    ? (channelPublicRef ?? undefined)
    : undefined;

  useRegieSync(regieHeartbeatAddress, regieSyncWsBase);

  const broadcastingChannelIdRef = useRef<number | undefined>(broadcastingChannel?.id);
  useEffect(() => { broadcastingChannelIdRef.current = broadcastingChannel?.id; }, [broadcastingChannel?.id]);

  const onStreamSourceChangeRef = useRef(onStreamSourceChange);
  useEffect(() => { onStreamSourceChangeRef.current = onStreamSourceChange; }, [onStreamSourceChange]);

  useLiveStreamSnapshot({
    channelActive, channelUuid, selectedVideoSource, selectedAudio, isStreaming,
    getSdpLogRegie, sdpInitialDevice, whipInfo, showSdpDebugRegie, showDeviceInfoPanel,
    onRestore: useCallback((snap) => {
      if (snap.selectedVideoSource) setSelectedVideoSource(snap.selectedVideoSource);
      if (snap.selectedAudio)       setSelectedAudio(snap.selectedAudio);
      if (snap.isStreaming)         setIsStreaming(snap.isStreaming);
      if (snap.sdpLogRegie?.length)  restoreSdpLog(snap.sdpLogRegie);
      if (snap.sdpInitialDevice)     setSdpInitialDevice(snap.sdpInitialDevice);
      if (snap.whipInfo)             setWhipInfo(snap.whipInfo);
      if (snap.showSdpDebugRegie)    setShowSdpDebugRegie(snap.showSdpDebugRegie);
      if (snap.showDeviceInfoPanel)  setShowDeviceInfoPanel(snap.showDeviceInfoPanel);
    }, []),
  });

  // Relance automatique de la régie : une chaîne arrêtée alors qu'elle était réglée sur « Régie »
  // garde ce choix (la source mémorisée reste 'webrtc'). À l'activation suivante la régie se
  // relance seule, dès que la chaîne est active ET qu'une source vidéo est acquise (effet plus
  // bas). Faute de quoi, au bout de REGIE_RELAUNCH_TIMEOUT_MS, retour à la source par défaut.
  const streamSourceForRelaunchRef = useRef<StreamSource | undefined>(streamSource);
  streamSourceForRelaunchRef.current = streamSource;
  const regieRelaunchPendingRef = useRef(false);
  const regieRelaunchTimerRef   = useRef<ReturnType<typeof setTimeout> | null>(null);
  const showLiveExportModalRef  = useRef(false);
  showLiveExportModalRef.current = showLiveExportModal;

  useEffect(() => {
    const wasActive = prevChannelActiveRef.current;
    prevChannelActiveRef.current = channelActive;
    // Une bascule demandée avant l'activation/désactivation ne vaut plus après le reset.
    if (wasActive !== channelActive) pendingAcquireRef.current = null;
    // Pas de sortie anticipée sans UUID : cet effet consomme la transition
    // (prevChannelActiveRef vient d'être écrit) et ne sera jamais rejoué. Le reset
    // de la régie, lui, n'a pas besoin de l'UUID — seules les sondes HLS en ont
    // besoin, et elles se contentent de ne rien faire.
    const uuid   = channelUuid;        // identité : appels API (arrêt d'enregistrement)
    const adresse = channelPublicRef;  // adresse : identifiants et URL des sondes HLS
    const base   = settings.serverUrl.replace(/\/$/, '');
    const hlsIds = uuid ? [`hls-video-${adresse}`, `hls-live-${adresse}`] : [];
    const resetRefs: RegieResetRefs = { videoRef, canvasPreviewRef, deckPreviewVideoRef, previewStreamsRef, activeSourceRef, currentBorrowHandleRef, audioContextRef, peerConnectionRef };
    const resetSetters: RegieResetSetters = { setSelectedVideoSource, setCurrentStream, setDeckPreviewSourceId, setPreviewStreams, setActiveImageIds, setActiveSoundIds, setActiveVideoOverlayIds, setActiveJingleId, setActiveLayoutId, setActiveLayoutPreviewId, setActiveSceneLayoutIds, setIsStreamButtonActive, setIsRecording, setRecordingSessionId, setRecordingStartedAt, setPoweredOffSlots, setAudioSwitchState, setIsStreaming };
    if (wasActive && !channelActive) {
      // Signale et arrête réellement (appel API, pas juste un reset d'état local) une
      // diffusion et/ou un enregistrement en cours au moment où la chaîne est désactivée
      // — sinon l'enregistrement backend (MediaMTX) restait orphelin indéfiniment
      // pendant que l'UI se remettait à zéro comme si de rien n'était.
      const wasRecording = isRecordingRef.current;
      const wasStreaming = isStreamingRef.current;
      if (wasRecording) {
        const sid = recordingSessionIdRef.current;
        if (sid != null && uuid) {
          apiStopRecording({ channelUuid: uuid, sessionId: sid }).catch(err => console.error('[channelActive→false] arrêt enregistrement', err));
        }
      }
      if (wasStreaming) {
        stopStreamingImplRef.current();
        // La source mémorisée reste 'webrtc' : le prochain démarrage relance la régie (cf. plus bas).
      }
      regieRelaunchPendingRef.current = false;
      if (regieRelaunchTimerRef.current) { clearTimeout(regieRelaunchTimerRef.current); regieRelaunchTimerRef.current = null; }
      // Le signalement à l'utilisateur se fait au niveau de BroadcastingChannelSheet
      // (cf. onLiveStatusChange / handleActiveToggle) : cet onglet Régie peut être
      // masqué (autre onglet actif) au moment du clic sur "désactiver la chaîne",
      // un showAlert ici ne serait alors pas vu.
      resetRegieContext(resetRefs, resetSetters, { stopCompositingLoop, scenePipelineManager, activeSceneLayoutIds: activeSceneLayoutIdsRef.current, registry, hlsProber, hlsIds, clearLocalStorage: true });
      releaseSourceElements();
      resetDeckInit();
      closeAllRegieIntercomm(intercommMapRef, { addSdpLog: addSdpLogRegie });
      knownIntercommDevicesRef.current.clear();
      return;
    }
    if (!wasActive && channelActive) {
      if (streamSourceForRelaunchRef.current === 'webrtc') {
        regieRelaunchPendingRef.current = true;
        if (regieRelaunchTimerRef.current) clearTimeout(regieRelaunchTimerRef.current);
        regieRelaunchTimerRef.current = setTimeout(() => {
          regieRelaunchTimerRef.current = null;
          regieRelaunchPendingRef.current = false;
          // Démarrée ou en attente de la fenêtre multiview : rien à défaire.
          if (isStreamingRef.current || showLiveExportModalRef.current) return;
          const fallback = defaultStreamSourceRef.current;
          const chId     = broadcastingChannelIdRef.current;
          showAlert(`La régie n'a pas pu se relancer toute seule : la chaîne reste sur la source par défaut.`, 'important');
          if (chId) { updateBroadcastingChannelStreamSource(chId, fallback).then(() => onStreamSourceChangeRef.current?.(fallback)).catch(err => console.error('[relance régie]', err)); }
        }, REGIE_RELAUNCH_TIMEOUT_MS);
      }
      resetRegieContext(resetRefs, resetSetters, { stopCompositingLoop, scenePipelineManager, activeSceneLayoutIds: activeSceneLayoutIdsRef.current, registry, hlsProber, hlsIds, clearLocalStorage: false });
      releaseSourceElements();
      resetDeckInit();
      if (uuid) {
        hlsProber.retryProbe(`hls-video-${adresse}`, `${base}/video_${adresse}/index.m3u8`);
        if (obsInDeckRef.current) hlsProber.retryProbe(`hls-live-${adresse}`,  `${base}/live_${adresse}/index.m3u8`, { persistent: true });
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channelActive]);

  useEffect(() => {
    const base = settings.serverUrl.replace(/\/$/, '');
    const uuid = channelPublicRef;   // l'ADRESSE : ces identifiants et ces URL sont publics
    if (!uuid) return;
    const hlsSources: { id: string; url: string; persistent?: boolean }[] = [
      { id: `hls-video-${uuid}`, url: `${base}/video_${uuid}/index.m3u8` },
      // « OBS, RTMP... » n'est PAS sondée ici : voir l'effet obsInDeck ci-dessous.
    ];
    deckSlots.forEach(slot => {
      if (slot?.type === 'url' && slot.externalUrl) {
        if (!hlsSources.find(h => h.id === slot.id)) hlsSources.push({ id: slot.id, url: slot.externalUrl });
      }
    });
    // Les caméras RTMP du deck ne sont pas reconnaissables ici : c'est leur
    // déclaration (handleAddHlsSource, autoRetry) qui les marque persistantes,
    // et le régime retenu par la sonde reste attaché à l'identifiant.
    hlsSources.forEach(({ id, url, persistent }) => hlsProber.startProbe(id, url, persistent ? { persistent } : undefined));
  }, [settings.serverUrl, channelUuid]);

  useEffect(() => {
    deckSlots.forEach(slot => {
      if (!slot || slot.type !== 'url') return;
      if (poweredOffSlots.has(slot.id)) return;
      const status = hlsProber.statusMap.get(slot.id);
      if (status !== 'ok') return;
      if (previewStreamsRef.current.get(slot.id)?.active) return;
      startPreviewForSource(slot);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hlsProber.statusMap, deckSlots, poweredOffSlots]);

  // ── La Playlist qui disparaît du multiview ────────────────────────────────
  //
  // L'effet ci-dessus n'agit que quand `statusMap` CHANGE. Or la sonde peut rester
  // 'ok' pendant que l'acquisition, elle, échoue : ffmpeg_playlist_publisher
  // redémarre côté serveur (video_UUID absent ~4 s, relevé le 24/10/2026 à 16:08),
  // le lecteur hls.js tombe sur `manifestLoadError httpCode=0`, le registre passe la
  // source en `error` — et plus rien ne rejoue l'emprunt : la vignette reste vide
  // jusqu'à ce qu'on envoie la Playlist en final, geste qui repasse par une
  // acquisition neuve. `startPreviewForSource` avale l'échec en comptant sur un
  // changement de sonde qui n'arrive pas.
  //
  // On rejoue donc l'emprunt, et seulement pour la Playlist : la panne y est
  // toujours transitoire (le serveur la republie seul), alors qu'une caméra ou un
  // flux tiers en erreur est une panne que l'opérateur relance lui-même — l'auto-
  // emprunt s'en tient volontairement à distance (cf. useLiveStreamSetup).
  const playlistRetryRef = useRef({ deckSlots, poweredOffSlots, hlsProber, startPreviewForSource });
  playlistRetryRef.current = { deckSlots, poweredOffSlots, hlsProber, startPreviewForSource };
  // Espacement des reprises, par source : 5 s, 15 s, 30 s, puis 60 s, et abandon après
  // PLAYLIST_RETRY_MAX essais jusqu'au retour de l'onglet ou au succès. Sans ce frein, un
  // onglet en arrière-plan (Chrome y met la vidéo en pause : `AbortError: video-only
  // background media was paused to save power`) faisait échouer chaque essai — 58
  // réacquisitions en 5 minutes relevées le 25/10/2026, jusqu'au rechargement de la page.
  const PLAYLIST_RETRY_DELAYS_MS = [5_000, 15_000, 30_000, 60_000];
  const PLAYLIST_RETRY_MAX = 8;
  const playlistRetryStateRef = useRef<Map<string, { attempts: number; nextAt: number }>>(new Map());
  useEffect(() => {
    const state = playlistRetryStateRef.current;
    const timer = setInterval(() => {
      // Onglet masqué : rien à tenter, et chaque essai échouerait. Les compteurs sont
      // remis à zéro au retour (cf. visibilitychange ci-dessous).
      if (document.hidden) return;
      const { deckSlots: slots, poweredOffSlots: off, hlsProber: prober, startPreviewForSource: start } = playlistRetryRef.current;
      const now = Date.now();
      slots.forEach(slot => {
        if (!slot || slot.type !== 'url' || !slot.id.startsWith('hls-video-')) return;
        if (off.has(slot.id)) return;
        if (prober.getStatus(slot.id) !== 'ok') return;
        if (previewStreamsRef.current.get(slot.id)?.active) { state.delete(slot.id); return; }
        const entry = state.get(slot.id) ?? { attempts: 0, nextAt: 0 };
        if (entry.attempts >= PLAYLIST_RETRY_MAX || now < entry.nextAt) return;
        entry.attempts += 1;
        entry.nextAt = now + PLAYLIST_RETRY_DELAYS_MS[Math.min(entry.attempts - 1, PLAYLIST_RETRY_DELAYS_MS.length - 1)];
        state.set(slot.id, entry);
        console.warn(`[MINIVUE] 🔁 ${slot.id} — Playlist sans image alors que la sonde répond, nouvel emprunt (${entry.attempts}/${PLAYLIST_RETRY_MAX})`);
        start(slot);
      });
    }, 5_000);
    const onVisible = () => { if (!document.hidden) state.clear(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => { clearInterval(timer); document.removeEventListener('visibilitychange', onVisible); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isRecording && recordingStartedAt !== null) {
      recordingTimerRef.current = setInterval(() => { setRecordingElapsedSec(Math.floor((Date.now() - recordingStartedAt) / 1000)); }, 1000);
    } else {
      if (recordingTimerRef.current) { clearInterval(recordingTimerRef.current); recordingTimerRef.current = null; }
      setRecordingElapsedSec(0);
    }
    return () => { if (recordingTimerRef.current) { clearInterval(recordingTimerRef.current); recordingTimerRef.current = null; } };
  }, [isRecording, recordingStartedAt]);

  useEffect(() => { if (!isStreaming && isRecording) handleStopRecording(); }, [isStreaming]);

  const handleSlotConfigsChange = useCallback((configs: MultiviewSlotConfig[]) => {
    slotConfigsUserEditedRef.current = true; setSlotConfigs(configs);
  }, []);

  /**
   * Glisser-déposer d'une mini-vue du Multiview sur une autre.
   *
   * Deux tableaux à échanger, dans deux états différents : les emplacements du
   * deck (source + scène), tenus par useLiveStreamSetup, et les réglages de
   * mini-vue (libellé personnalisé, source épinglée), tenus ici. Sans le second,
   * un libellé saisi à la main resterait sur le numéro d'emplacement au lieu de
   * suivre l'image à laquelle il se rapporte.
   *
   * `slotConfigsUserEditedRef` est levé au passage : l'effet qui recompose les
   * réglages depuis `deckSlots` écraserait sinon cet échange au rendu suivant.
   */
  const handleMultiviewReorder = useCallback((fromIndex: number, toIndex: number) => {
    if (fromIndex === toIndex) return;

    handleDeckSlotsSwap(fromIndex, toIndex);

    slotConfigsUserEditedRef.current = true;
    setSlotConfigs(prev => {
      if (fromIndex < 0 || toIndex < 0 || fromIndex >= prev.length || toIndex >= prev.length) return prev;
      const next = [...prev];
      [ next[fromIndex] , next[toIndex] ] = [ next[toIndex] , next[fromIndex] ];
      return next;
    });
  }, [handleDeckSlotsSwap]);

  useEffect(() => {
    if (!deckSlots.some(s => s !== null)) return;
    if (slotConfigsUserEditedRef.current) return;
    const uuid = channelUuid;
    const rows = buildVideoRow(deckSlots, uuid);
    setSlotConfigs(rows.map(row => ({ sourceId: row.slot?.id ?? null, customLabel: null })));
  }, [deckSlots, channelUuid]);

  useEffect(() => {
    const validIds = new Set<string>();
    for (let i = 0; i < deckSlots.length; i++) { if (deckSlots[i]) validIds.add(deckSlots[i]!.id); }
    for (let i = 0; i < deckSlotLayouts.length; i++) { if (deckSlotLayouts[i]) validIds.add('scene-' + deckSlotLayouts[i]!.id); }
    for (let i = 0; i < videoSources.length; i++) { if (videoSources[i].type === 'scene') validIds.add(videoSources[i].id); }
    setSlotConfigs(prev => {
      let needsUpdate = false;
      for (let i = 0; i < prev.length; i++) { if (prev[i].sourceId && !validIds.has(prev[i].sourceId!)) { needsUpdate = true; break; } }
      if (!needsUpdate) return prev;
      return prev.map(cfg => ({ ...cfg, sourceId: cfg.sourceId && validIds.has(cfg.sourceId) ? cfg.sourceId : null }));
    });
  }, [deckSlots, deckSlotLayouts, videoSources]);

  useEffect(() => {
    for (let i = 0; i < slotConfigs.length; i++) {
      const sourceId = slotConfigs[i].sourceId;
      if (!sourceId || !sourceId.startsWith('scene-')) continue;
      const layoutId   = sourceId.slice('scene-'.length);
      const layoutSlot = layoutSlots.find(l => l.id === layoutId);
      if (!layoutSlot) continue;
      if (!scenePipelineManager.isActive(layoutId)) {
        scenePipelineManager.activateScene(layoutSlot, videoSources, currentStreamRef.current).then(() => {
          setActiveSceneLayoutIds(prev => prev.includes(layoutId) ? prev : [...prev, layoutId]);
          const stream = scenePipelineManager.getStream(layoutId);
          if (stream) { setup.previewStreamsRef.current.set(sourceId, stream); setup.setPreviewStreams(new Map(setup.previewStreamsRef.current)); }
        }).catch(err => console.warn('[MultiviewScene]', layoutId, err));
      } else {
        const stream = scenePipelineManager.getStream(layoutId);
        if (stream && !setup.previewStreamsRef.current.has(sourceId)) {
          setup.previewStreamsRef.current.set(sourceId, stream);
          setup.setPreviewStreams(new Map(setup.previewStreamsRef.current));
        }
      }
    }
  }, [slotConfigs, layoutSlots]);

  useEffect(() => {
    // Le reset de montage ne doit JAMAIS être sauté : au montage l'UUID de la chaîne
    // n'est souvent pas encore résolu, et cet effet ne tourne qu'une fois. Le sauter
    // laissait l'état de la session précédente en place — deck jamais réinitialisé,
    // régie inerte. Les sondes HLS, elles, sont reportées à l'effet suivant.
    const uuid   = channelPublicRef;  // l'ADRESSE : identifiants des sondes HLS
    const hlsIds = uuid ? [`hls-video-${uuid}`, `hls-live-${uuid}`] : [];
    const resetRefs: RegieResetRefs = { videoRef, canvasPreviewRef, deckPreviewVideoRef, previewStreamsRef, activeSourceRef, currentBorrowHandleRef, audioContextRef, peerConnectionRef };
    const resetSetters: RegieResetSetters = { setSelectedVideoSource, setCurrentStream, setDeckPreviewSourceId, setPreviewStreams, setActiveImageIds, setActiveSoundIds, setActiveVideoOverlayIds, setActiveJingleId, setActiveLayoutId, setActiveLayoutPreviewId, setActiveSceneLayoutIds, setIsStreamButtonActive, setIsRecording, setRecordingSessionId, setRecordingStartedAt, setPoweredOffSlots, setAudioSwitchState, setIsStreaming };
    resetRegieContext(resetRefs, resetSetters, { stopCompositingLoop, scenePipelineManager, activeSceneLayoutIds: activeSceneLayoutIdsRef.current, registry, hlsProber, hlsIds, clearLocalStorage: true });
    releaseSourceElements();
    resetDeckInit();

    // Le registre vit maintenant à la racine (index.tsx) et survit à la bascule de
    // chaîne : plus personne ne libère les flux de celle qu'on quitte. Sans ce
    // nettoyage, un lecteur HLS resterait à tirer ses segments en arrière-plan pour
    // une chaîne qui n'est plus à l'écran. La webcam et le partage d'écran, eux, sont
    // épargnés — c'est tout l'objet du déplacement.
    //
    // peerConnectionRef, lui, vit dans MediaStreamContext — PARTAGÉ par toutes les
    // chaînes, contrairement à tout le reste de cette régie qui est propre à ce
    // montage. Sans cet arrêt explicite à la fermeture, la connexion WHIP de la
    // chaîne qu'on quitte restait ouverte, et la première action de mise à
    // l'antenne sur la chaîne suivante la réutilisait (même ref) pour lui
    // envoyer SES pistes : la chaîne quittée diffusait alors le contenu de la
    // suivante. La fermer ici retombe sur le repli mire déjà prévu côté serveur
    // pour une perte de source — le même chemin qu'une fermeture d'onglet.
    return () => {
      stopStreamingImplRef.current();
      releaseChannelScopedSourcesRef.current();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Switch audio : mémorisé par chaîne sur action de l'opérateur seulement (cf. deck_persistence).
  const handleAudioSwitchChange = useCallback((s: AudioSwitchState) => {
    setAudioSwitchState(s);
    saveAudioSwitch(channelUuid, s);
  }, [channelUuid]);

  // Rechargement de la page, réactivation de la chaîne, reprise serveur : le reset de la régie
  // a replacé le Switch sur « Source » — on remet ce que l'opérateur avait réglé. Déclaré APRÈS
  // les effets de reset, qui s'exécutent donc avant lui.
  useEffect(() => {
    if (!channelUuid || !channelActive) return;
    const stored = loadAudioSwitch(channelUuid);
    if (stored) setAudioSwitchState(stored);
    const onRestored = (e: Event) => {
      const d = (e as CustomEvent<{ uuid: string; settings: boolean }>).detail;
      if (!d?.settings || d.uuid !== channelUuid) return;
      const again = loadAudioSwitch(channelUuid);
      if (again) setAudioSwitchState(again);
    };
    window.addEventListener('regie-deck-restored', onRestored);
    return () => window.removeEventListener('regie-deck-restored', onRestored);
  }, [channelUuid, channelActive]);

  /**
   * Les sondes HLS de la chaîne, une fois son UUID connu.
   *
   * Séparées du reset de montage ci-dessus : l'UUID arrive souvent après, et un
   * effet à dépendances vides ne les aurait jamais lancées.
   */
  useEffect(() => {
    if (!channelUuid) return;
    const base = settings.serverUrl.replace(/\/$/, '');
    hlsProber.retryProbe(`hls-video-${channelPublicRef}`, `${base}/video_${channelPublicRef}/index.m3u8`);
    if (obsInDeckRef.current) hlsProber.retryProbe(`hls-live-${channelPublicRef}`,  `${base}/live_${channelPublicRef}/index.m3u8`, { persistent: true });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channelUuid, settings.serverUrl]);

  // « OBS, RTMP... » (live_<adresse>) : le plus souvent HORS LIGNE, donc sondée seulement
  // quand un bouton du deck la porte (c'est la sonde qui colore et active ce bouton).
  // Sinon, test unique au clic (handleDeckSwitch) — jamais en boucle : une sonde
  // permanente interrogeait le serveur toutes les 15 s et journalisait une
  // « déconnexion » à chaque échec, pour une source que personne n'utilisait.
  const obsInDeck = !!channelPublicRef && deckSlots.some(
    sl => sl?.type === 'url' && !!sl.externalUrl && sl.externalUrl.includes(`live_${channelPublicRef}`));
  const obsInDeckRef = useRef(obsInDeck);
  obsInDeckRef.current = obsInDeck;

  useEffect(() => {
    if (!obsInDeck || !channelPublicRef) return;
    const base = settings.serverUrl.replace(/\/$/, '');
    hlsProber.startProbe(`hls-live-${channelPublicRef}`, `${base}/live_${channelPublicRef}/index.m3u8`, { persistent: true });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [obsInDeck, channelPublicRef, settings.serverUrl]);

  // ── Cleanup global ────────────────────────────────────────────────────────
  useEffect(() => {
    return () => {
      closeAllRegieIntercomm(intercommMapRef, { addSdpLog: addSdpLogRegie });
      knownIntercommDevicesRef.current.clear();
      intercommMicStreamRef.current?.getTracks().forEach(t => t.stop());
      intercommMicStreamRef.current = null;
      // Le retour d'antenne meurt avec la régie qui le compose — sinon le device
      // garde l'image figée de la dernière frame, indéfiniment. Voir
      // closeAllReturnStreams. Les caméras montantes, elles, restent connectées :
      // changer de chaîne ou d'onglet ne doit pas les faire renégocier.
      closeAllReturnStreams({ addSdpLog: addSdpLogRegie });
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOpenFullscreen = useCallback((closePanel: boolean) => {
    // L'onglet est ouvert par LiveStreamLayoutV2 (portail) : ici seulement le repli du panneau.
    if (closePanel) setShowMultiview(false);
  }, []);

  const handleOpenDeckFullscreen = useCallback(() => {
    const uuid = channelUuid;
    if (!uuid) return;
    window.open(`/regie/${uuid}/deck`, '_blank');
  }, [channelUuid]);

  const handleToggleLayout = useCallback(async (slot: LayoutSlot) => {
    const isPipelineActive = scenePipelineManager.isActive(slot.id);
    const isCurrentFinal   = activeLayoutId === slot.id;
    if (isCurrentFinal) {
      setActiveLayoutId(null); setSelectedVideoSource(''); activeSourceRef.current = null;
      const isInPreview = activeLayoutPreviewId === slot.id;
      if (!isInPreview) { scenePipelineManager.deactivateScene(slot.id); setActiveSceneLayoutIds(prev => prev.filter(id => id !== slot.id)); }
    } else {
      if (activeLayoutId) {
        const prevIsInPreview = activeLayoutPreviewId === activeLayoutId;
        if (!prevIsInPreview) { scenePipelineManager.deactivateScene(activeLayoutId); setActiveSceneLayoutIds(prev => prev.filter(id => id !== activeLayoutId)); }
      }
      if (!isPipelineActive) { await scenePipelineManager.activateScene(slot, videoSources, currentStreamRef.current); setActiveSceneLayoutIds(prev => prev.includes(slot.id) ? prev : [...prev, slot.id]); }
      setActiveLayoutId(slot.id); setSelectedVideoSource(''); activeSourceRef.current = `scene-${slot.id}`;
      const sceneStream = scenePipelineManager.getStream(slot.id);
      if (sceneStream) await setFinalStreamRef.current(sceneStream);
    }
  }, [scenePipelineManager, activeLayoutId, activeLayoutPreviewId, videoSources, setActiveLayoutId, setSelectedVideoSource, currentStreamRef]);

  const prevLayoutSlotsRef = useRef<LayoutSlot[]>(layoutSlots);
  useEffect(() => {
    const prev = prevLayoutSlotsRef.current;
    prevLayoutSlotsRef.current = layoutSlots;
    if (activeSceneLayoutIdsRef.current.length === 0) return;
    for (const layoutId of activeSceneLayoutIdsRef.current) {
      const prevSlot = prev.find(s => s.id === layoutId);
      const nextSlot = layoutSlots.find(s => s.id === layoutId);
      if (!nextSlot || prevSlot === nextSlot) continue;
      scenePipelineManager.activateScene(nextSlot, videoSources, currentStreamRef.current).then(() => {
        const stream = scenePipelineManager.getStream(layoutId);
        if (!stream) return;
        const sceneSourceId = `scene-${layoutId}`;
        setup.previewStreamsRef.current.set(sceneSourceId, stream);
        setup.setPreviewStreams(new Map(setup.previewStreamsRef.current));
        if (deckPreviewSourceId === sceneSourceId && deckPreviewVideoRef.current) { deckPreviewVideoRef.current.srcObject = stream; deckPreviewVideoRef.current.play().catch(() => {}); }
        if (activeLayoutId === layoutId) setFinalStreamRef.current(stream);
      }).catch(err => console.warn('[scene-dirty]', layoutId, err));
    }
  }, [layoutSlots]);

  const handleDeckPreviewLayout = useCallback(async (slot: LayoutSlot) => {
    const isAlreadyPreview = activeLayoutPreviewId === slot.id;
    if (isAlreadyPreview) {
      setActiveLayoutPreviewId(null); setDeckPreviewSourceId(null);
      if (activeLayoutId !== slot.id) { scenePipelineManager.deactivateScene(slot.id); setActiveSceneLayoutIds(prev => prev.filter(id => id !== slot.id)); }
      return;
    }
    if (!scenePipelineManager.isActive(slot.id)) { await scenePipelineManager.activateScene(slot, videoSources, currentStreamRef.current); setActiveSceneLayoutIds(prev => prev.includes(slot.id) ? prev : [...prev, slot.id]); }
    setActiveLayoutPreviewId(slot.id); setDeckPreviewSourceId(`scene-${slot.id}`);
    const stream = scenePipelineManager.getStream(slot.id);
    if (stream && deckPreviewVideoRef.current) { deckPreviewVideoRef.current.srcObject = stream; deckPreviewVideoRef.current.play().catch(() => {}); }
  }, [activeLayoutPreviewId, activeLayoutId, scenePipelineManager, videoSources, currentStreamRef, setDeckPreviewSourceId, deckPreviewVideoRef]);

  const handleSourceVolumeChange = useCallback((sourceId: string, volume: number) => { updateSourceAudioConfig(sourceId, { volume, muted: false }); }, [updateSourceAudioConfig]);
  const handleSourceMuteToggle   = useCallback((sourceId: string) => { const cfg = getSourceAudioConfig(sourceId); updateSourceAudioConfig(sourceId, { muted: !cfg.muted }); }, [getSourceAudioConfig, updateSourceAudioConfig]);
  const handleAliasChange        = useCallback((sourceId: string, alias: string) => { setSourceAliases({ ...sourceAliases, [sourceId]: alias }); }, [setSourceAliases, sourceAliases]);

  // ── Callbacks d'UI stabilisés ───────────────────────────────────────────────
  // Ces handlers étaient écrits en lambdas inline dans le JSX, donc recréés à
  // chaque rendu de LiveStreamManager. Ils descendent jusqu'à MultiviewPanelV2,
  // qui est enveloppé dans React.memo : une seule prop instable suffisait à
  // rendre cette mémoïsation inopérante et à re-rendre tout le panneau (1485 l.,
  // tuiles vidéo comprises) à chaque rendu du parent. Les setters de useState
  // étant stables, ces useCallback peuvent avoir des deps vides.
  const handleTogglePreviewMuted      = useCallback(() => setPreviewMuted(v => !v), []);
  const handleToggleFinalMuted        = useCallback(() => setFinalMuted(v => !v), []);
  const handleOpenStorageModal        = useCallback(() => setShowStorageModal(true), []);
  const handleOpenKeyboardShortcuts   = useCallback(() => setShowKeyboardShortcuts(true), []);
  const handleCloseKeyboardShortcuts  = useCallback(() => setShowKeyboardShortcuts(false), []);
  const handleToggleMediaboardConfig  = useCallback(() => setShowMediaboardConfig(v => !v), []);
  const handleCloseMediaboardConfig   = useCallback(() => setShowMediaboardConfig(false), []);
  const handleToggleCloseOnFullscreen = useCallback(() => setCloseOnFullscreen(v => !v), []);
  const handleOpenSourceAliasConfig   = useCallback(() => setShowSourceAliasConfig(true), []);
  const handleCloseSourceAliasConfig  = useCallback(() => setShowSourceAliasConfig(false), []);
  const handleOpenSaveConfig          = useCallback(() => setShowSaveConfig(true), []);
  const handleOpenLoadConfig          = useCallback(() => setShowLoadConfig(true), []);
  const handleCloseStorageModal       = useCallback(() => setShowStorageModal(false), []);

  // Même raison : `devices.audio.map(...)` produisait un tableau neuf à chaque
  // rendu, transmis tel quel à MultiviewPanelV2.
  const localMics = useMemo(
    () => devices.audio.map(d => ({ deviceId: d.deviceId, label: d.label })),
    [devices.audio]
  );

  type ExtendedAudioDevice = { deviceId: string; label: string; kind: 'local'|'remote'|'screen'|'url'; sourceId?: string; };

  const extendedAudioDevices: ExtendedAudioDevice[] = useMemo(() => {
    const list: ExtendedAudioDevice[] = [];
    devices.audio.forEach(d => { list.push({ deviceId: d.deviceId, label: d.label || `Micro ${d.deviceId.slice(0, 8)}`, kind: 'local' }); });
    remoteDevices.forEach(rd => {
      const conn = remoteConnections.get(rd.device_id);
      if (conn?.stream && conn.stream.getAudioTracks().length > 0) list.push({ deviceId: `remote-audio-${rd.device_id}`, label: `📱 ${rd.label || rd.device_id} (audio)`, kind: 'remote', sourceId: `remote-${rd.device_id}` });
    });
    screenShareStreams.forEach((stream, slotId) => {
      if (stream.getAudioTracks().length === 0) return;
      const slotLabel = screenShareSlots.find(s => s.id === slotId)?.label ?? slotId;
      list.push({ deviceId: `screen-audio-${slotId}`, label: `🖥️ ${slotLabel} (audio)`, kind: 'screen', sourceId: slotId });
    });
    deckSlots.forEach(slot => {
      if (!slot || slot.type !== 'url') return;
      list.push({ deviceId: `hls-audio-${slot.id}`, label: `🎵 ${slot.label || 'HLS'} (audio)`, kind: 'url', sourceId: slot.id });
    });
    return list;
  }, [devices.audio, remoteDevices, remoteConnections, screenShareStreams, screenShareSlots, deckSlots, sources]);

  // Options du Switch audio déjà rattachées au deck : celles qui résolvent vers un
  // slot ayant une entrée dans l'AudioSourceRegistry. Passé à ControlBar, qui s'en
  // sert pour répartir la liste en deux groupes (rattachées / non rattachées) et
  // pour présélectionner une source immédiatement audible au passage en "Fixe".
  // Les autres restent sélectionnables : elles sont acquises à la demande.
  const deckAttachedAudioIdsRef = useRef<string[]>([]);
  const deckAttachedAudioIds = useMemo(() => {
    const ids: string[] = [];
    const push = (optionId: string) => {
      const slotId = resolveFixedSlotId(optionId);
      if (slotId && audioSourceRegistry.has(`audio-${slotId}`)) ids.push(optionId);
    };
    remoteDevices.forEach(d => push(`remote:${d.device_id}`));
    extendedAudioDevices.forEach(d => {
      if (d.deviceId.startsWith('hls-audio-')) push(`hls:${d.deviceId.replace('hls-audio-', '')}`);
      else if (!d.deviceId.startsWith('remote-audio-')) push(`dev:${d.deviceId}`);
    });
    // Identité stable tant que le contenu ne change pas : ce memo est réévalué au
    // rythme des VU-mètres, inutile de faire re-rendre la ControlBar pour autant.
    const previous = deckAttachedAudioIdsRef.current;
    if (previous.length === ids.length && previous.every((v, i) => v === ids[i])) return previous;
    deckAttachedAudioIdsRef.current = ids;
    return ids;
    // meteredSourceIds sert de sonde de rafraîchissement : l'AudioSourceRegistry
    // n'est pas réactif, on réévalue quand l'ensemble des sources mesurées change
    // — c'est-à-dire exactement quand l'audio d'un slot vient d'être connecté.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolveFixedSlotId, audioSourceRegistry, remoteDevices, extendedAudioDevices, meteredSourceIds]);

  // ── Mode "Mix" ─────────────────────────────────────────────────────────────
  // Toutes les options audio proposées par la ControlBar (invités distants,
  // périphériques, partages d'écran, flux HLS) : même liste, mêmes ids.
  const mixOptionIds = useMemo(() => {
    const ids: string[] = [];
    remoteConnections.forEach((_conn, deviceId) => ids.push(`remote:${deviceId}`));
    extendedAudioDevices.forEach(d => {
      if (d.deviceId.startsWith('hls-audio-')) ids.push(`hls:${d.deviceId.replace('hls-audio-', '')}`);
      else if (!d.deviceId.startsWith('remote-audio-')) ids.push(`dev:${d.deviceId}`);
    });
    return ids;
  }, [remoteConnections, extendedAudioDevices]);

  // Id d'option → id de source du registry (slot du deck, ou id dédié hors deck),
  // pour lire le niveau des VU-mètres du Mixeur.
  const mixSourceIds = useMemo(() => {
    const map: Record<string, string> = {};
    mixOptionIds.forEach(optionId => {
      const target = describeFixedAudioTarget(optionId);
      if (target) map[optionId] = target.sourceId;
    });
    return map;
  }, [mixOptionIds, describeFixedAudioTarget]);

  // Acquisition des sources hors deck, TOUTES ensemble (là où le mode Fixe n'en
  // acquiert qu'une). Libérées dès qu'on quitte le Mix, sinon un micro resterait
  // ouvert sans servir. Une acquisition ratée n'est retentée qu'au prochain
  // passage en Mix (autorisation micro refusée, flux sans piste audio…).
  // Mixeur ouvert : on entend le résultat du mixage même si le son du flux final
  // est coupé en local (sinon régler les niveaux se ferait à l'aveugle).
  // Brouillon du Mixeur (null = fermé). Seule l'écoute locale l'entend ; la
  // diffusion garde les réglages validés jusqu'au clic sur Valider.
  const [mixDraft, setMixDraft] = useState<{ gains: Record<string, number>; muted: Record<string, boolean>; monitorMuted?: boolean } | null>(null);
  const mixerOpen = mixDraft !== null;
  const mixStandaloneRef = useRef<Map<string, string>>(new Map());
  const mixFailedRef     = useRef<Set<string>>(new Set());
  const [mixAcquiredTick, setMixAcquiredTick] = useState(0);
  useEffect(() => {
    const isMix = audioSwitchState.mode === 'mix';
    const wanted = new Map<string, Extract<FixedAudioTarget, { kind: 'standalone' }>>();
    if (isMix) {
      mixOptionIds.forEach(optionId => {
        const t = describeFixedAudioTarget(optionId);
        if (t?.kind === 'standalone') wanted.set(optionId, t);
      });
    } else {
      mixFailedRef.current.clear();
    }
    mixStandaloneRef.current.forEach((sourceId, optionId) => {
      if (wanted.has(optionId)) return;
      disconnectAudioForSource(sourceId);
      mixStandaloneRef.current.delete(optionId);
      setMixAcquiredTick(n => n + 1);
    });
    wanted.forEach((t, optionId) => {
      if (mixStandaloneRef.current.has(optionId) || mixFailedRef.current.has(optionId)) return;
      mixStandaloneRef.current.set(optionId, t.sourceId);
      connectAudioForSource(t.sourceId, t.type, t.stream ?? null, t.deviceId)
        .then(() => {
          if (mixStandaloneRef.current.get(optionId) !== t.sourceId) { disconnectAudioForSource(t.sourceId); return; }
          if (!audioSourceRegistry.has(`audio-${t.sourceId}`)) {
            mixFailedRef.current.add(optionId);
            mixStandaloneRef.current.delete(optionId);
            return;
          }
          setMixAcquiredTick(n => n + 1);
        })
        .catch(err => {
          console.warn('[AUDIO-MIX] acquisition impossible pour', optionId, err);
          mixFailedRef.current.add(optionId);
          if (mixStandaloneRef.current.get(optionId) === t.sourceId) mixStandaloneRef.current.delete(optionId);
        });
    });
  }, [audioSwitchState.mode, mixOptionIds, describeFixedAudioTarget, connectAudioForSource,
      disconnectAudioForSource, audioSourceRegistry]);

  // Branchement et niveaux. Déclaré après l'effet Switch audio (qui remet tout au
  // repos) pour que les gains du Mixeur passent en dernier. meteredSourceIds et
  // mixAcquiredTick servent de sondes : le registry n'est pas réactif.
  useEffect(() => {
    if (audioSwitchState.mode !== 'mix') return;
    const gains = audioSwitchState.mixGains ?? {};
    const muted = audioSwitchState.mixMuted ?? {};
    const slotIds: string[] = [];
    mixOptionIds.forEach(optionId => {
      const slotId = resolveFixedAudioSourceId(optionId);
      if (!slotId || slotIds.includes(slotId)) return;
      const regId = `audio-${slotId}`;
      if (!audioSourceRegistry.has(regId)) return;
      const g = muted[optionId] ? 0 : Math.max(0, gains[optionId] ?? 1);
      // Diffusion : réglages validés. Écoute locale : brouillon du Mixeur s'il est ouvert.
      const gMonitor = mixDraft
        ? ((mixDraft.monitorMuted || mixDraft.muted[optionId]) ? 0 : Math.max(0, mixDraft.gains[optionId] ?? 1))
        : g;
      audioSourceRegistry.setOutputGain(regId, g);
      audioSourceRegistry.setMonitorGain(regId, (finalMuted && !mixerOpen) ? 0 : gMonitor);
      slotIds.push(slotId);
    });
    attachMixSources(slotIds);
  }, [audioSwitchState, audioSourceRegistry, deckSlots, mixOptionIds, resolveFixedAudioSourceId,
      attachMixSources, finalMuted, mixDraft, mixerOpen, meteredSourceIds, mixAcquiredTick]);

  const resolveAudioSourceId = useCallback((): string | null => {
    const { mode, fixedAudioSourceId } = audioSwitchState;
    if (mode === 'source' || mode === 'mix') return activeSourceRef.current;
    // Repli sur la source active si la source fixe n'est pas branchable —
    // même règle que l'effet Switch audio, pour que les deux ne divergent pas.
    return resolveFixedAudioSourceId(fixedAudioSourceId) ?? activeSourceRef.current;
  }, [audioSwitchState, resolveFixedAudioSourceId]);

  // Après un changement de source audio, l'entrée `audio-<slot>` est neuve : le mixer
  // doit s'y raccrocher si ce slot l'alimente. Avant, on ne le faisait que pour la
  // source à l'antenne — en mode « Fixe », changer le son de la source fixe laissait
  // le mixer sur l'entrée détruite. L'entrée neuve naît aussi avec une écoute locale
  // quasi muette (0.0001) : on la rouvre comme le fait setFinalStream.
  // Appelé une fois le changement fini, parfois plusieurs secondes après le clic
  // (autorisation micro) : on passe par un ref pour lire le Switch audio d'alors.
  const reattachMixerAfterAudioChange = useCallback((sourceId: string) => {
    if (audioSwitchState.mode === 'mix') return;   // l'effet Mix se relance de lui-même
    const mixerSourceId = resolveAudioSourceId();
    if (sourceId !== activeSourceRef.current && sourceId !== mixerSourceId) return;
    if (mixerSourceId) {
      const regId = `audio-${mixerSourceId}`;
      if (audioSourceRegistry.has(regId)) audioSourceRegistry.setMonitorGain(regId, finalMuted ? 0 : 1);
    }
    initAudioMixer(mixerSourceId, null);
  }, [resolveAudioSourceId, audioSourceRegistry, finalMuted, initAudioMixer, audioSwitchState.mode]);
  const reattachMixerAfterAudioChangeRef = useRef(reattachMixerAfterAudioChange);
  useEffect(() => { reattachMixerAfterAudioChangeRef.current = reattachMixerAfterAudioChange; }, [reattachMixerAfterAudioChange]);

  const handleAudioDeviceChange = useCallback((sourceId: string, audioDeviceId: string) => {
    setSelectedAudioDeviceIds(prev => { const next = new Map(prev); next.set(sourceId, audioDeviceId); return next; });
    updateSourceAudioConfig(sourceId, { audioDeviceId });
    changeAudioDeviceForSlot(sourceId, audioDeviceId)
      .then(() => reattachMixerAfterAudioChangeRef.current(sourceId))
      .catch(err => console.warn(`[handleAudioDeviceChange]`, err));
  }, [updateSourceAudioConfig, changeAudioDeviceForSlot]);

  /**
   * Réacquisition de l'entrée audio d'un slot, sans changer de périphérique.
   *
   * L'entrée effectivement branchée n'est pas toujours celle qui est associée à la
   * source vidéo : le mètre peut rester accroché à un flux précédent, ou la piste
   * micro mourir sans que rien ne le signale. `changeAudioDevice` déconnecte puis
   * reconnecte sans court-circuiter quand l'identifiant ne bouge pas — le rappeler
   * avec la valeur courante suffit donc à reprendre l'entrée à zéro.
   *
   * On ne repasse volontairement pas par `handleAudioDeviceChange` : rien ne change
   * dans la configuration, il n'y a ni état à réécrire ni préférence à persister.
   */
  const handleReloadAudio = useCallback(async (sourceId: string) => {
    const deviceId = selectedAudioDeviceIds.get(sourceId) ?? '';
    try {
      await changeAudioDeviceForSlot(sourceId, deviceId);
      reattachMixerAfterAudioChangeRef.current(sourceId);
    } catch (err) {
      console.warn(`[handleReloadAudio] réacquisition de "${sourceId}" échouée :`, err);
    }
  }, [selectedAudioDeviceIds, changeAudioDeviceForSlot]);

  const handleStereoVolumeChange = useCallback((sourceId: string, stereo: StereoVolume) => {
    setStereoVolumes(prev => { const next = new Map(prev); next.set(sourceId, stereo); return next; });
    updateSourceAudioConfig(sourceId, { volumeLeft: stereo.left, volumeRight: stereo.right });
  }, [updateSourceAudioConfig]);

  const handleToggleAudioEffect = useCallback((sourceId: string, effect: AudioEffectId) => {
    const current = audioEffectsMap.get(sourceId) ?? { ...DEFAULT_AUDIO_EFFECTS };
    updateSourceAudioConfig(sourceId, { audioEffects: { ...current, [effect]: !current[effect] } });
  }, [audioEffectsMap, updateSourceAudioConfig]);

  const handleAudioEffectConfigsChange = useCallback((sourceId: string, configs: Record<string, unknown>) => {
    updateSourceAudioConfig(sourceId, { audioEffectConfigs: configs });
  }, [updateSourceAudioConfig]);

  const handleVideoEffectsSettingsChange = useCallback((sourceId: string, settings: VideoEffectsSettings) => {
    setVideoEffectsSettingsMap(prev => { const next = new Map(prev); next.set(sourceId, settings); return next; });
  }, []);

  const handleAudioVisualChange = useCallback((sourceId: string, visual: AudioOnlyVisual) => {
    setup.updateAudioOnlySource(sourceId, { audioVisual: visual === 'black' ? undefined : visual });
  }, [setup.updateAudioOnlySource]);

  // Image d'une source audio seul : écran noir, ou analyseur de spectre / oscilloscope
  // de son entrée audio-<id> (flux partagé, cf. spectrumVideo.ts).
  const audioOnlyVideoStream = useCallback((source: VideoSource): MediaStream => {
    const visual = audioOnlyVisual(source);
    return visual === 'black'
      ? createBlackVideoStream(settings.canvasWidth, settings.canvasHeight)
      : acquireSpectrumStream(source.id, () => audioSourceRegistry.getAnalysers(`audio-${source.id}`), settings.canvasWidth, settings.canvasHeight, visual);
  }, [audioSourceRegistry, settings.canvasWidth, settings.canvasHeight]);

  const handleSourceConfigOpen  = useCallback(async (sourceId: string, source: VideoSource | null) => { if (!source) return; modalObservedSourceIdRef.current = sourceId; }, []);
  const handleSourceConfigClose = useCallback((sourceId: string | null) => { if (!sourceId) return; modalObservedSourceIdRef.current = null; }, []);

  const setFinalStream = useCallback(async (stream: MediaStream) => {
    setCurrentStream(stream);
    if (videoRef.current) videoRef.current.srcObject = stream;
    await startCanvasForStreamRef.current(stream);
    const activeSource = activeSourceRef.current ? videoSources.find(s => s.id === activeSourceRef.current) : null;
    const micStream = (() => {
      if (activeSource?.type === 'device') return null;
      if (audioSwitchState.mode === 'fixe') return null;
      const audioTrack = stream.getAudioTracks()[0];
      return audioTrack ? new MediaStream([audioTrack]) : null;
    })();
    // Mode "Mix" : le bus porte déjà les sources choisies au Mixeur, changer la
    // source vidéo n'y touche pas.
    const isMix = audioSwitchState.mode === 'mix';
    const mixerSourceId = isMix ? null : resolveAudioSourceId();
    if (!isMix) initAudioMixer(mixerSourceId, micStream);
    // Seule la source qui alimente réellement le mixer devient audible : en mode
    // "Fixe", la source qu'on vient de mettre à l'antenne ne doit pas s'ajouter à
    // la source fixe (elle était jusqu'ici muette par effet de bord de la
    // déconnexion du nœud monitor, cf. AudioMixerPipeline.disconnectMonitorOutput).
    if (mixerSourceId) {
      const regId = `audio-${mixerSourceId}`;
      if (audioSourceRegistry.has(regId)) {
        audioSourceRegistry.setMonitorGain(regId, finalMuted ? 0 : 1);
      }
    }
    if (!isMix && activeSourceRef.current && activeSourceRef.current !== mixerSourceId) {
      const activeRegId = `audio-${activeSourceRef.current}`;
      if (audioSourceRegistry.has(activeRegId)) {
        audioSourceRegistry.setMonitorGain(activeRegId, 0);
      }
    }
    if (isStreamingRef.current && peerConnectionRef.current) {
      const mixerAudioTrack = audioMixerDestRef.current?.stream.getAudioTracks()[0];
      if (mixerAudioTrack) { const audioSender = peerConnectionRef.current.getSenders().find(s => s.track?.kind === 'audio'); if (audioSender) try { await audioSender.replaceTrack(mixerAudioTrack); } catch {} }
      const canvasVideoTrack = canvasStreamRef.current?.getVideoTracks()[0];
      if (canvasVideoTrack) { const videoSender = peerConnectionRef.current.getSenders().find(s => s.track?.kind === 'video'); if (videoSender) try { await videoSender.replaceTrack(canvasVideoTrack); } catch {} }
    }
  }, [setCurrentStream, videoRef, initAudioMixer, peerConnectionRef, videoSources, finalMuted, audioSourceRegistry, resolveAudioSourceId, audioSwitchState]);

  const setFinalStreamRef = useRef(setFinalStream);
  useEffect(() => { setFinalStreamRef.current = setFinalStream; }, [setFinalStream]);

  // ── Images générées des sources audio seul (spectre, oscilloscope) ─────────────
  // Un flux par emplacement hors écran noir, posé dans `previewStreams` : la
  // mini-vue et la Preview le montrent comme n'importe quelle image. Il est libéré
  // quand l'emplacement repasse à l'écran noir ou quitte le deck. Si la source est à
  // l'antenne au moment du changement, on y remet tout de suite la nouvelle image —
  // sinon le final garderait un flux arrêté (ou le noir) jusqu'au prochain clic.
  const spectrumSlotIdsRef = useRef<Map<string, AudioOnlyVisual>>(new Map());
  useEffect(() => {
    const wanted = new Map<string, VideoSource>();
    deckSlots.forEach(slot => { if (slot?.type === 'audio') wanted.set(slot.id, slot); });

    let previewChanged = false;
    const onAirChanged: VideoSource[] = [];
    wanted.forEach((slot, id) => {
      const visual = audioOnlyVisual(slot);
      const before = spectrumSlotIdsRef.current.get(id);
      if (before === visual) return;
      spectrumSlotIdsRef.current.set(id, visual);
      if (visual !== 'black') {
        previewStreamsRef.current.set(id, audioOnlyVideoStream(slot));
      } else {
        releaseSpectrumStream(id);
        previewStreamsRef.current.delete(id);
      }
      previewChanged = true;
      if (before !== undefined && activeSourceRef.current === id) onAirChanged.push(slot);
    });
    spectrumSlotIdsRef.current.forEach((_, id) => {
      if (wanted.has(id)) return;
      if (activeSourceRef.current === id) return;   // encore à l'antenne : libéré à un passage suivant
      spectrumSlotIdsRef.current.delete(id);
      releaseSpectrumStream(id);
      if (previewStreamsRef.current.delete(id)) previewChanged = true;
    });
    if (previewChanged) setPreviewStreams(new Map(previewStreamsRef.current));
    onAirChanged.forEach(slot => { setFinalStreamRef.current(audioOnlyVideoStream(slot)).catch(() => {}); });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deckSlots, audioOnlyVideoStream, selectedVideoSource]);

  useEffect(() => {
    if (videoRef.current && currentStreamRef.current && videoRef.current.srcObject !== currentStreamRef.current) { videoRef.current.srcObject = currentStreamRef.current; videoRef.current.play().catch(() => {}); }
    if (canvasPreviewRef.current && canvasStreamRef.current && canvasPreviewRef.current.srcObject !== canvasStreamRef.current) { canvasPreviewRef.current.srcObject = canvasStreamRef.current; canvasPreviewRef.current.play().catch(() => {}); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const clearCurrentStream = useCallback(() => {
    if (currentBorrowHandleRef.current) { try { currentBorrowHandleRef.current.release(); } catch (e) { console.warn('[clearCurrentStream]', e); } currentBorrowHandleRef.current = null; }
    if (videoRef.current) videoRef.current.srcObject = null;
    setCurrentStream(null);
  }, [setCurrentStream, videoRef]);

  const handleDeckSlotChangeWrapped = useCallback((slotIndex: number, source: VideoSource | null) => {
    setup.handleDeckSlotChange(slotIndex, source);
    if (source?.type === 'url' && source.externalUrl) hlsProber.startProbe(source.id, source.externalUrl);
  }, [setup.handleDeckSlotChange, hlsProber]);

  // Sauvegarde du deck : seulement ce qui concerne CE deck. Les tables par source
  // (micros, volumes, effets, alias) sont des mémoires du POSTE, partagées par toutes
  // les chaînes et jamais purgées : sans ce filtre, la sauvegarde d'une chaîne
  // emportait les sources de toutes les autres (et des identifiants de comptes Xtream).
  // Sont gardées : les sources posées sur un emplacement, et les deux flux internes
  // de cette chaîne (Playlist, OBS/RTMP), qui peuvent servir au Switch audio sans
  // occuper d'emplacement.
  const deckScopeIds = (): Set<string> => {
    const ids = new Set<string>(deckSlots.filter((s): s is VideoSource => !!s).map(s => s.id));
    if (channelPublicRef) { ids.add(`hls-video-${channelPublicRef}`); ids.add(`hls-live-${channelPublicRef}`); }
    return ids;
  };
  const scopeMap = <T,>(m: Map<string, T>, ids: Set<string>): Map<string, T> =>
    new Map(Array.from(m.entries()).filter(([id]) => ids.has(id)));
  const scopeRecord = <T,>(rec: Record<string, T>, ids: Set<string>): Record<string, T> =>
    Object.fromEntries(Object.entries(rec).filter(([id]) => ids.has(id)));

  // Réalisateur auto : déclaré plus bas, d'où cette passerelle.
  const applyAutoDirectorRef = useRef<(s: AutoDirectorSettings) => void>(() => {});
  const setAutoDirectorEnabledRef = useRef<(on: boolean) => void>(() => {});
  const handleLoadConfig = useCallback(async (payload: RegieConfigPayload) => {
    const { features, data } = payload; const p = data.pupitre;

    // Sources externes : on les REDÉCLARE au microservice avant de placer le deck, pour
    // retrouver la même liste, connectées ou non, comme si on venait de les ajouter.
    // Une caméra RTMP qui n'a pas pu garder sa clé change d'identifiant : `extRemap`
    // reporte l'emplacement et les réglages sur la nouvelle.
    let extRemap = new Map<string, string>();
    // Chaîne d'origine de la sauvegarde. Dans la régie d'UNE AUTRE chaîne, les smartphones
    // et les caméras RTMP (liés à l'identité de leur chaîne) ne sont PAS importés : ni
    // recréés, ni réservés au deck. Les flux HLS et Xtream, eux, se reprennent partout.
    const origin = payload.originChannelUuid ?? payload.channelUuid;
    const sameChain = !origin || !channelUuid || origin === channelUuid;
    const foreignIds = new Set<string>();
    let sources = p?.externalSources;
    if (sources && !sameChain) {
      sources.rtmp.forEach(r => foreignIds.add(r.sourceId));
      sources.devices.forEach(d => foreignIds.add(`remote-${d.device_id}`));
      const nbPhones = sources.devices.length, nbRtmp = sources.rtmp.length;
      sources = { ...sources, rtmp: [], devices: [] };
      if (nbPhones + nbRtmp > 0) showAlert(`Cette sauvegarde vient d'une AUTRE chaîne : ses ${nbPhones} smartphone(s) et ${nbRtmp} caméra(s) RTMP ne sont pas importés — ils restent liés à leur chaîne d'origine.`, 'important');
    }
    if (sources && channelPublicRef) {
      try {
        const microserviceUrl = `${settings.serverUrl}/${channelPublicRef}/regie`;
        const { idRemap, report } = await restoreExternalSources(sources, {
          microserviceUrl, serverUrl: settings.serverUrl, streamPath: channelPublicRef,
          publicBaseUrl: settings.serverUrl, existingDevices: remoteDevices, registerDevice: handleRegisterDevice,
        });
        extRemap = idRemap;
        const note = describeRestoreReport(report);
        if (note) showAlert(note, 'important');
        reloadExternalSourcesRef.current();
        loadDevices();
      } catch (e) {
        // Microservice injoignable : la chaîne n'est pas active (son container n'existe
        // qu'à l'activation). On garde la liste et on le DIT — avant, l'échec ne
        // laissait qu'une ligne de console et la liste était perdue en silence.
        console.warn('[handleLoadConfig] redéclaration des sources externes impossible :', e);
        if (channelUuid) savePendingExternalSources(channelUuid, sources);
        showAlert("Les sources externes (smartphones, caméras RTMP, flux HLS et Xtream) n'ont pas pu être recréées : le microservice de cette chaîne ne répond pas — la chaîne n'est sans doute pas active. Elles seront recréées automatiquement dès que la chaîne sera activée.", 'important');
      }
    }
    if (p?.deviceTransport) setSettings(prev => ({ ...prev, deviceTransport: p.deviceTransport! }));
    if (p?.autoDirector) applyAutoDirectorRef.current(p.autoDirector);
    if (p?.whipEncoding) {
      const key = whipEncodingStorageKey(broadcastingChannel?.id ?? null);
      saveWhipEncoding(key, sanitizeWhipEncoding(p.whipEncoding));
      window.dispatchEvent(new CustomEvent(WHIP_ENCODING_RESTORED_EVENT, { detail: { key } }));
    }

    let pipIdMap = new Map<string, string>();   // webcams de la sauvegarde → webcams de ce poste
    if (p) {
      // Webcams locales : un deviceId n'existe que sur le poste qui les a vues. La
      // n-ième webcam locale du deck sauvegardé prend la n-ième webcam de CE poste
      // (hors celles que la sauvegarde nomme déjà) ; tous les réglages rangés par
      // identifiant de source la suivent, sinon ils resteraient sur un fantôme.
      const known    = new Set(videoSources.map(s => s.id));
      const spare    = videoSources.filter(s => s.type === 'device' && !p.deckSlots.some(d => d.id === s.id));
      const idMap    = new Map<string, string>();
      p.deckSlots.forEach(d => {
        if (d.type === 'device' && d.id && !known.has(d.id)) {
          const mine = spare.shift();
          if (mine) idMap.set(d.id, mine.id);
        }
      });
      pipIdMap       = idMap;
      const mapId    = (id: string) => extRemap.get(id) ?? idMap.get(id) ?? id;
      const rekey    = <T,>(rec: Record<string, T> | undefined): Map<string, T> =>
        new Map(Object.entries(rec ?? {}).map(([id, v]) => [mapId(id), v] as [string, T]));

      p.deckSlots.forEach((serialized, idx) => {
        if (!serialized.id) { handleDeckSlotChangeWrapped(idx, null); return; }
        const id = mapId(serialized.id);
        // Source « audio seul » : elle n'existe que par son emplacement, donc absente de
        // videoSources tant qu'on ne l'a pas reposée — on la recrée depuis la sauvegarde.
        if (serialized.type === 'audio') {
          handleDeckSlotChangeWrapped(idx, {
            id: serialized.id, label: serialized.label, type: 'audio',
            ...(serialized.deviceId ? { deviceId: serialized.deviceId } : {}),
            ...(serialized.audioVisual === 'spectrum' || serialized.audioVisual === 'oscilloscope' ? { audioVisual: serialized.audioVisual } : {}),
          });
          return;
        }
        const match = videoSources.find(s => s.id === id);
        if (match) { handleDeckSlotChangeWrapped(idx, match); return; }
        // Source absente de ce poste (webcam sans équivalent, source externe supprimée,
        // appareil distant hors ligne) : on ne la RECRÉE pas — l'emplacement reste vide,
        // mais réservé, et la source le reprend si elle revient.
        handleDeckSlotChangeWrapped(idx, null);
        if (foreignIds.has(serialized.id)) return;   // source d'une autre chaîne : place laissée libre
        reserveDeckSlot(idx, { kind: serialized.type === 'device' ? 'device' : serialized.type === 'remote' ? 'remote' : 'other', id });
      });
      // Sources que la régie connaissait à la sauvegarde : pas reposées d'office sur un bouton.
      if (p.knownSourceIds) markSourcesKnown(p.knownSourceIds.map(mapId));
      setup.handleKeyMapChange({ ...keyMap, ...p.keyMap }); setSlotConfigs(p.slotConfigs.map(c => c.sourceId ? { ...c, sourceId: mapId(c.sourceId) } : c));
      setSourceAliases(Object.fromEntries(Object.entries(p.sourceAliases ?? {}).map(([id, v]) => [mapId(id), v])) as typeof p.sourceAliases);
      setSourceAudioConfigs(Object.entries(p.sourceAudioConfigs).map(([sourceId, cfg]) => ({ ...cfg, sourceId: mapId(sourceId) })));
      // Effets audio : remplacés en bloc dans le magasin de la chaîne. Ce qu'appliquait
      // le poste à la sauvegarde (sourceAudioConfigs) prime sur ce qu'affichait son
      // pupitre (audioEffectsMap), les deux ayant pu diverger avant leur fusion.
      // Sauvegarde sans réglages fins : on garde ceux de ce poste.
      {
        const shown   = rekey(p.audioEffectsMap);
        const configs = p.audioEffectConfigsMap ? rekey(p.audioEffectConfigsMap) : null;
        const applied = rekey(p.sourceAudioConfigs);
        const ids = new Set([...Array.from(shown.keys()), ...(configs ? Array.from(configs.keys()) : []), ...Array.from(applied.keys())]);
        replaceChannelAudioEffects(Array.from(ids).map(sourceId => ({
          sourceId,
          audioEffects      : applied.get(sourceId)?.audioEffects ?? shown.get(sourceId),
          audioEffectConfigs: applied.get(sourceId)?.audioEffectConfigs ?? configs?.get(sourceId)
                              ?? (configs ? undefined : audioEffectConfigsMap.get(sourceId)),
        })).filter(r => r.audioEffects || r.audioEffectConfigs));
      }
      setSelectedAudioDeviceIds(new Map(Object.entries(p.selectedAudioDeviceIds).map(([id, v]) => [mapId(id), v] as [string, string])));
      setStereoVolumes(rekey(p.stereoVolumes));
      if (p.audioSwitch) {
        // Les identifiants de webcam suivent le remappage par rang, comme les autres tables.
        const a = p.audioSwitch;
        const remapOpt = (id: string) => {
          const m = id.match(/^(dev:|)(.*)$/); const bare = m ? m[2] : id;
          const to = mapId(bare) !== bare ? mapId(bare) : undefined;
          return to ? (m?.[1] ?? '') + to : id;
        };
        const rekeyObj = <T,>(o?: Record<string, T>) => Object.fromEntries(Object.entries(o ?? {}).map(([k, v]) => [remapOpt(k), v]));
        const next = { mode: a.mode, fixedAudioSourceId: a.fixedAudioSourceId ? remapOpt(a.fixedAudioSourceId) : '', mixGains: rekeyObj(a.mixGains), mixMuted: rekeyObj(a.mixMuted) };
        setAudioSwitchState(next);
        saveAudioSwitch(channelUuid, next);
      }
      if (p.videoEffectsSettingsMap) {
        const videoSettings = rekey(p.videoEffectsSettingsMap);
        setVideoEffectsSettingsMap(videoSettings);
        // La table ne sert qu'à réamorcer la fenêtre de réglages : ce qui change
        // l'image, c'est le pipeline. On le relance pour les sources présentes ; une
        // source absente garde son réglage en table et le retrouvera à sa prochaine ouverture.
        videoSettings.forEach((settings, sourceId) => {
          if (!videoSources.some(s => s.id === sourceId)) return;
          applyVideoEffectsSettings(registry, sourceId, settings).catch(e => console.warn('[handleLoadConfig] effets vidéo', sourceId, e));
        });
      }
    }
    if (features.image && data.image)  setImageSlots(data.image.slots);
    if (features.son   && data.son)    setSoundSlots(data.son.slots);
    if (features.video && data.video)  setJingleSlots(data.video.slots);
    if (features.scene && data.scene)  setLayoutSlots(data.scene.slots);
    if (features.pip   && data.pip) {
      const localCams = new Set(videoSources.filter(sv => sv.type === 'device').map(sv => sv.id));
      setVideoOverlaySlots(retargetPipDevices(data.pip.slots, localCams, pipIdMap));
    }
  }, [channelUuid, broadcastingChannel?.id, keyMap, markSourcesKnown, setSettings, showAlert, settings.serverUrl, channelPublicRef, remoteDevices, handleRegisterDevice, loadDevices, setVideoOverlaySlots, videoSources, handleDeckSlotChangeWrapped, setup.handleKeyMapChange, reserveDeckSlot, setSlotConfigs, setSourceAliases, setSourceAudioConfigs, setSelectedAudioDeviceIds, setStereoVolumes, replaceChannelAudioEffects, audioEffectConfigsMap, setVideoEffectsSettingsMap, updateSourceAudioConfig, registry, setImageSlots, setSoundSlots, setJingleSlots, setLayoutSlots]);

  // ── Tout réinitialiser (barre de contrôle, section Régie) ────────────────────
  //
  // Remet la régie comme à la création de la chaîne : mediaboard vide, deck et
  // multiview à leur disposition d'origine, réglages des sources par défaut, tous
  // les effets (audio et vidéo) coupés. Les raccourcis clavier et les captures
  // d'écran en cours ne sont PAS touchés. Les tables de réglages sont des mémoires du
  // POSTE, partagées entre chaînes : on ne retire que les entrées des sources de CETTE
  // régie, pour ne pas dégrader les autres chaînes.
  const handleResetRegieToDefaults = useCallback(async () => {
    // Sources externes : plus aucune d'enregistrée, comme à la création de la chaîne —
    // flux HLS, caméras RTMP (leur clé est perdue), comptes Xtream, smartphones. Fait
    // AVANT le reste, pour que le deck par défaut ne se remplisse pas avec elles.
    if (channelPublicRef) {
      try {
        const { removedSourceIds } = await clearExternalSources({
          microserviceUrl: `${settings.serverUrl}/${channelPublicRef}/regie`,
          serverUrl: settings.serverUrl, streamPath: channelPublicRef,
        });
        removedSourceIds.forEach(id => reloadDepsRef.current.handleRemoveSource(id));
      } catch (e) { console.warn('[réinitialisation] sources externes non retirées :', e); }
      for (const d of remoteDevices) {
        try { await deleteDeviceNow(d.device_id); } catch (e) { console.warn('[réinitialisation] appareil', d.device_id, e); }
      }
    }

    // Mediaboard
    clearSlotsFromIndexedDB(imageSlots);
    clearSlotsFromIndexedDB(soundSlots);
    clearSlotsFromIndexedDB(jingleSlots);
    clearSceneSlotsFromIndexedDB(layoutSlots);
    setImageSlots(DEFAULT_IMAGE_SLOTS.map(sl => ({ ...sl })));
    setSoundSlots(DEFAULT_SOUND_SLOTS.map(sl => ({ ...sl })));
    setVideoOverlaySlots(DEFAULT_VIDEO_OVERLAY_SLOTS.map(sl => ({ ...sl })));
    setJingleSlots(DEFAULT_JINGLE_SLOTS.map(sl => ({ ...sl })));
    setScreenShareSlots(DEFAULT_SCREEN_SHARE_SLOTS.map(sl => ({ ...sl, config: { ...sl.config } })));
    setLayoutSlots(DEFAULT_LAYOUT_SLOTS.map(sl => ({ ...sl, background: { ...sl.background }, incrustations: [] })));
    try { localStorage.removeItem('mediartc_slot_filenames'); } catch {}
    setActiveImageIds(new Set());
    setActiveSoundIds(new Set());
    setActiveVideoOverlayIds(new Set());
    setActiveJingleId(null);
    setPoweredOffSlots(new Set());

    // Multiview : vues à leur place initiale
    setSlotConfigs(Array.from({ length: SLOT_COUNT }, () => ({ sourceId: null, customLabel: null })));
    slotConfigsUserEditedRef.current = false;

    // Réglages des sources de cette régie : défauts, effets coupés
    const scope = new Set<string>([
      ...videoSources.map(s => s.id),
      ...Array.from(audioEffectsMap.keys()),
      ...Array.from(audioEffectConfigsMap.keys()),
      ...Array.from(videoEffectsSettingsMap.keys()),
    ]);
    if (channelPublicRef) { scope.add(`hls-video-${channelPublicRef}`); scope.add(`hls-live-${channelPublicRef}`); }

    setSourceAudioConfigs(sourceAudioConfigs.filter(c => !scope.has(c.sourceId)));
    scope.forEach(id => updateSourceAudioConfig(id, {
      volume: 1, muted: false, volumeLeft: 1, volumeRight: 1, audioDeviceId: undefined,
    }));
    setSelectedAudioDeviceIds(prev => new Map(Array.from(prev.entries()).filter(([id]) => !scope.has(id))));
    setStereoVolumes(prev => new Map(Array.from(prev.entries()).filter(([id]) => !scope.has(id))));
    setSourceAliases(Object.fromEntries(Object.entries(sourceAliases).filter(([id]) => !scope.has(id))) as typeof sourceAliases);
    replaceChannelAudioEffects([]);
    destroyAllVideoEffectsPipelines(registry);
    setVideoEffectsSettingsMap(new Map());

    // Switch audio, réalisateur auto, encodage WHIP : comme à la création de la chaîne
    setAudioSwitchState({ mode: 'source', fixedAudioSourceId: '' });
    saveAudioSwitch(channelUuid, null);
    setAutoDirectorEnabledRef.current(false);
    applyAutoDirectorRef.current(DEFAULT_AUTO_DIRECTOR_SETTINGS);
    {
      const key = whipEncodingStorageKey(broadcastingChannel?.id ?? null);
      saveWhipEncoding(key, DEFAULT_WHIP_ENCODING);
      window.dispatchEvent(new CustomEvent(WHIP_ENCODING_RESTORED_EVENT, { detail: { key } }));
    }

    // Deck : disposition par défaut (webcams locales, Playlist…)
    resetDeckToDefaults();
  }, [
    imageSlots, soundSlots, jingleSlots, layoutSlots,
    setImageSlots, setSoundSlots, setVideoOverlaySlots, setJingleSlots, setScreenShareSlots, setLayoutSlots,
    setActiveImageIds, setActiveSoundIds, setActiveVideoOverlayIds, setActiveJingleId, setPoweredOffSlots,
    videoSources, audioEffectsMap, audioEffectConfigsMap, videoEffectsSettingsMap, channelPublicRef,
    replaceChannelAudioEffects, sourceAudioConfigs, setSourceAudioConfigs, updateSourceAudioConfig, sourceAliases, registry, resetDeckToDefaults,
    settings.serverUrl, remoteDevices, deleteDeviceNow, channelUuid, broadcastingChannel?.id,
  ]);

  // Réglages par source repris de la sauvegarde automatique du serveur APRÈS le
  // montage : on relit les trois tables de cette chaîne et on relance les effets
  // vidéo des sources présentes (les effets audio, MediaStreamContext les relit seul).
  const videoSourcesLatestRef = useRef(videoSources);
  useEffect(() => { videoSourcesLatestRef.current = videoSources; }, [videoSources]);
  useEffect(() => {
    const onRestored = (e: Event) => {
      const detail = (e as CustomEvent<DeckRestoredDetail>).detail;
      if (!detail?.settings || detail.uuid !== channelUuid) return;
      const readMap = <T,>(key: string): Map<string, T> | null => {
        try { const raw = localStorage.getItem(key); return raw ? new Map(JSON.parse(raw)) : null; } catch { return null; }
      };
      const video   = readMap<VideoEffectsSettings>(VIDEO_EFFECTS_SETTINGS_MAP_KEY);
      try {
        const raw = localStorage.getItem(SLOT_CONFIGS_SCOPED_KEY);
        if (raw) {
          const parsed = JSON.parse(raw) as MultiviewSlotConfig[];
          _setSlotConfigs(Array.from({ length: SLOT_COUNT }, (_, i) => parsed[i] ?? { sourceId: null, customLabel: null }));
        }
      } catch {}
      if (video) {
        _setVideoEffectsSettingsMap(video);
        video.forEach((settings, sourceId) => {
          if (!videoSourcesLatestRef.current.some(s => s.id === sourceId)) return;
          applyVideoEffectsSettings(registry, sourceId, settings).catch(err => console.warn('[deck restauré] effets vidéo', sourceId, err));
        });
      }
    };
    window.addEventListener(DECK_RESTORED_EVENT, onRestored);
    return () => window.removeEventListener(DECK_RESTORED_EVENT, onRestored);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channelUuid, registry]);

  // ── Niveaux par source ↔ sauvegarde automatique serveur ───────────────────────
  //
  // Niveaux stéréo, volume/muet et entrée audio de chaque source sont des tables du POSTE
  // (partagées par toutes les chaînes). On en recopie ici la part de CETTE chaîne sous des
  // clés propres (deckSettingsKeys) : c'est ce que lit la sauvegarde auto, et ce que la
  // reprise serveur dépose sur un autre poste. Jamais de liste vide écrite — une clé vide
  // empêcherait la reprise du serveur (« ce qui existe déjà ici fait foi »).
  useEffect(() => {
    if (!channelUuid) return;
    const ids  = deckScopeIds();
    const keys = deckSettingsKeys(channelUuid);
    const parts: Array<[string, unknown[]]> = [
      [keys.stereoVolumes,  Array.from(stereoVolumes.entries()).filter(([id]) => ids.has(id))],
      [keys.sourceAudio,    sourceAudioConfigs.filter(c => ids.has(c.sourceId)).map(({ sourceId, volume, muted, volumeLeft, volumeRight, audioDeviceId }) =>
                              ({ sourceId, volume, muted, volumeLeft, volumeRight, audioDeviceId }))],
      [keys.audioDeviceIds, Array.from(selectedAudioDeviceIds.entries()).filter(([id]) => ids.has(id))],
    ];
    let changed = false;
    for (const [key, list] of parts) {
      if (list.length === 0) continue;
      const json = JSON.stringify(list);
      try { if (localStorage.getItem(key) !== json) { localStorage.setItem(key, json); changed = true; } } catch {}
    }
    if (changed) notifyDeckSettings();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channelUuid, stereoVolumes, sourceAudioConfigs, selectedAudioDeviceIds, deckSlots, channelPublicRef]);

  // Au montage et après une reprise serveur : les niveaux de cette chaîne absents de ce poste
  // sont ajoutés aux tables du poste. Ce qui existe déjà ici n'est jamais écrasé.
  const sourceAudioConfigsLatestRef = useRef(sourceAudioConfigs);
  sourceAudioConfigsLatestRef.current = sourceAudioConfigs;
  useEffect(() => {
    if (!channelUuid) return;
    const keys = deckSettingsKeys(channelUuid);
    const read = (key: string): any[] | null => {
      try { const v = JSON.parse(localStorage.getItem(key) || 'null'); return Array.isArray(v) && v.length ? v : null; } catch { return null; }
    };
    const merge = () => {
      const sv = read(keys.stereoVolumes);
      if (sv) setStereoVolumes(prev => {
        const missing = sv.filter(([id]) => !prev.has(id));
        return missing.length ? new Map([...Array.from(prev.entries()), ...missing]) : prev;
      });
      const di = read(keys.audioDeviceIds);
      if (di) setSelectedAudioDeviceIds(prev => {
        const missing = di.filter(([id]) => !prev.has(id));
        return missing.length ? new Map([...Array.from(prev.entries()), ...missing]) : prev;
      });
      const sa = read(keys.sourceAudio) as Array<Partial<SourceAudioConfig> & { sourceId: string }> | null;
      if (sa) {
        const have = new Set(sourceAudioConfigsLatestRef.current.map(c => c.sourceId));
        const missing = sa.filter(c => c && typeof c.sourceId === 'string' && !have.has(c.sourceId))
                          .map(c => ({ volume: 1, muted: false, ...c }) as SourceAudioConfig);
        if (missing.length) setSourceAudioConfigs([...sourceAudioConfigsLatestRef.current, ...missing]);
      }
    };
    merge();
    const onRestored = (e: Event) => {
      const d = (e as CustomEvent<DeckRestoredDetail>).detail;
      if (d?.settings && d.uuid === channelUuid) merge();
    };
    window.addEventListener(DECK_RESTORED_EVENT, onRestored);
    return () => window.removeEventListener(DECK_RESTORED_EVENT, onRestored);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channelUuid]);

  const acquireOnce = useCallback(async (source: VideoSource, afterSet?: (stream: MediaStream) => void) => {
    if (activeSourceRef.current === source.id && currentStreamRef.current?.active) return;

    // Source à laquelle revenir si l'acquisition n'aboutit pas. Sans ça,
    // activeSourceRef garde l'id d'une source qui n'est jamais montée à l'antenne,
    // et handleDeckSwitch — qui commence par `if (activeSourceRef.current ===
    // source.id) return` — refuse ensuite tout nouveau clic sur cette case.
    const previousSourceId = activeSourceRef.current;
    // `restoreBorrow` est réaffecté plus bas, une fois l'ancien emprunt mis de
    // côté : les giveUp() qui précèdent n'ont rien à restaurer.
    let restoreBorrow: () => void = () => {};
    const giveUp = () => {
      acquiringRef.current = false;
      activeSourceRef.current = previousSourceId;
      setSelectedVideoSource(previousSourceId ?? '');
      restoreBorrow();
    };

    // Une caméra distante entre dans le deck dès le DÉBUT de sa négociation :
    // connectToDevice l'inscrit aussitôt dans remoteConnections avec `stream: null`,
    // et le flux n'est injecté qu'au premier ontrack — jusqu'à une vingtaine de
    // secondes plus tard (attente du gathering ICE, réponse du device, DTLS).
    //
    // Cliquer dessus dans cette fenêtre coupait l'antenne : clearCurrentStream()
    // plus bas relâche l'emprunt courant et vide le <video>, PUIS RemoteAcquirer
    // lève parce que rien n'a été injecté. La régie se retrouvait avec un final
    // noir, une alerte d'échec, et une case morte — d'où l'impression que
    // l'application plante quand on touche à un appareil en cours de connexion.
    //
    // Le test ne porte PAS sur `source.stream` : les VideoSource portées par les
    // cases du deck ne sont pas rafraîchies à chaque changement (cf.
    // sameVideoSourceList dans updateVideoSources), leur `stream` peut donc être
    // périmé dans les deux sens.
    //
    // Il ne porte pas non plus sur le seul registry, comme dans la première
    // version de ce garde-fou : `SourceRegistry.doRelease()` remet
    // `source.stream` à null pour TOUS les types, y compris `remote` — alors que
    // `RemoteAcquirer.release()` est volontairement vide, justement parce que le
    // flux ne lui appartient pas et continue de couler. Une caméra distante
    // passée à l'antenne puis quittée voyait donc son registry vidé, et tout clic
    // ultérieur était refusé avec un message faux (« sa connexion est en cours »)
    // alors qu'elle était connectée depuis longtemps. La ré-injection de
    // useSourceRegistrySync ne rattrape pas le coup : son effet ne se rejoue que
    // si `videoSources` ou `remoteConnections` change d'identité, et depuis
    // sameVideoSourceList `videoSources` garde la sienne.
    //
    // La source de vérité, c'est donc la connexion vivante. Et quand le registry
    // l'a oubliée, on la lui redonne au lieu d'attendre un re-rendu fortuit.
    if (source.type === 'remote') {
      const live = registry.getStream(source.id)
        ?? remoteConnectionsRef.current.get(source.id.replace(/^remote-/, ''))?.stream
        ?? null;
      if (!live) {
        giveUp();
        showAlert(
          "Cette caméra distante n'a pas encore de flux : sa connexion est en cours. "
          + 'Réessayez dans quelques secondes.',
          'important',
        );
        return;
      }
      if (!registry.getStream(source.id)) registry.injectStream(source.id, live);
    }

    acquiringRef.current = true;
    activeSourceRef.current = source.id;
    if (source.type === 'url' && hlsProber.getStatus(source.id) !== 'ok') { giveUp(); return; }

    // ── L'ancienne source reste à l'antenne jusqu'à la coupe ────────────────
    //
    // clearCurrentStream() relâchait l'emprunt de la source à l'antenne AVANT
    // d'acquérir la suivante. Or DeviceAcquirer.release() (et ScreenAcquirer)
    // appellent track.stop() dès que refCount tombe à 0 : la source encore
    // composée par la boucle canvas était donc coupée net, et le canvas peignait
    // du noir pendant toute l'acquisition de la nouvelle — le flash.
    //
    // On garde l'emprunt et on ne le relâche qu'une fois la coupe faite
    // (setFinalStream a rendu la main, donc startCanvasForStream a basculé le
    // double tampon). Si l'acquisition échoue, giveUp() le remet en place : la
    // source précédente reste à l'antenne au lieu de laisser un final noir.
    const previousBorrow = currentBorrowHandleRef.current;
    currentBorrowHandleRef.current = null;
    const releasePreviousBorrow = () => {
      if (!previousBorrow) return;
      try { previousBorrow.release(); } catch (e) { console.warn('[acquireAndSetStream] relâche ancien emprunt', e); }
    };
    restoreBorrow = () => { currentBorrowHandleRef.current = previousBorrow; };

    if (source.type === 'scene' && source.stream) {
      try { await setFinalStream(source.stream); afterSet?.(source.stream); }
      finally { releasePreviousBorrow(); acquiringRef.current = false; }
      return;
    }
    // Audio seul : une image noire, et le son de son entrée `audio-<id>`, que
    // initAudioMixer branche comme pour toute source à l'antenne. Aucun micro
    // ajouté ici : ce serait un second son, hors des réglages de l'emplacement.
    if (source.type === 'audio') {
      const image = audioOnlyVideoStream(source);
      try { await setFinalStream(image); afterSet?.(image); }
      finally { releasePreviousBorrow(); acquiringRef.current = false; }
      return;
    }
    let videoStream: MediaStream;
    try {
      if (source.type === 'device' || source.type === 'remote' || source.type === 'url' || source.type === 'screen') {
        const handle = await registry.borrow(source.id);
        videoStream = handle.stream;
        currentBorrowHandleRef.current = handle;
        // Diagnostic temporaire (15/09/2026) : la mini-vue reste noire sur Lovelight TV malgré
        // le contrôle .active ajouté dans borrow()/acquireInternal (SourceRegistry.ts). Ce log
        // dit si borrow() a rendu un flux frais (active=true) ou déjà mort à cet instant précis
        // — donc si le problème est dans le registre ou plus loin dans cette fonction.
        if (source.type === 'device') {
          console.warn(`[acquireAndSetStream] borrow("${source.id}") → stream.active=${videoStream.active}, tracks=`,
            videoStream.getVideoTracks().map(t => `readyState=${t.readyState},muted=${t.muted}`));
        }
        // Reporte le choix fait dans le Deck (écran/fenêtre/onglet choisi via getDisplayMedia)
        // vers le panel ConfigMedia, dont l'onglet "Écran" lit exclusivement screenShareStreams
        // (MediaStreamContext, une entrée par emplacement 'screen-N') — sans ça, il continuait
        // d'afficher "Aucun flux d'écran" même une fois la source sélectionnée et active sur le Deck.
        if (source.type === 'screen') {
          const acquiredStream = videoStream;
          setScreenShareStreams(prev => { const next = new Map(prev); next.set(source.id, acquiredStream); return next; });
          // Établit tout de suite un emprunt "preview" indépendant (refCount +1) pour cette
          // capture d'écran. Sans ça, passer à l'antenne une AUTRE copie d'écran juste après
          // relâche l'unique emprunt de celle-ci via clearCurrentStream() → refCount à 0 →
          // le registry stoppe réellement la capture (ScreenAcquirer.release/track.stop()),
          // alors qu'elle est toujours assignée à un emplacement du Deck. L'effet réactif
          // d'auto-emprunt des mini-vues (useLiveStreamSetup) fait la même chose mais de façon
          // asynchrone/différée — pas assez tôt si l'utilisateur enchaîne les sélections vite.
          try { await setupRef.current.startPreviewForSource(source); } catch {}
        }
      } else { throw new Error('Source invalide'); }
    } catch (err: any) {
      giveUp();
      if (err.name === 'NotAllowedError' || err.name === 'AbortError') return;
      if (source.type === 'url' && source.externalUrl) { hlsProber.retryProbe(source.id, source.externalUrl); return; }
      // Le détail technique (err complet, id, type) va en console — capté par le pipe de
      // journalisation distante (remoteLog_*.txt, cf. investigation du 410) — mais reste hors
      // de l'alerte : l'opérateur n'a pas besoin du VID:PID de la caméra ni du message brut du
      // navigateur, seulement de savoir QUELLE source a échoué.
      console.error(`Impossible de démarrer le flux (source "${source.label}", id ${source.id}, type ${source.type}):`, err);
      // Les webcams ajoutent souvent leur identifiant USB au nom natif ("HD Pro Webcam C920
      // (046d:082d)") : bruit pour l'opérateur, retiré ici uniquement pour l'affichage.
      const label = source.label.replace(/\s*\([0-9a-f]{4}:[0-9a-f]{4}\)\s*$/i, '');
      showAlert(
        source.type === 'screen' ? "Impossible de démarrer le partage d'écran." : `Impossible de démarrer "${label}".`,
        'important'
      );
      return;
    }
    let finalStream: MediaStream;
    if (source.type === 'url') {
      finalStream = videoStream;
    } else {
      let audioStream: MediaStream | null = null;
      if (selectedAudio && source.type !== 'remote' && source.type !== 'screen') {
        try { audioStream = await navigator.mediaDevices.getUserMedia({ audio: { deviceId: { exact: selectedAudio }, echoCancellation: true, noiseSuppression: true, autoGainControl: true } }); } catch {}
      }
      finalStream = new MediaStream([
        ...videoStream.getVideoTracks(),
        ...(source.type === 'remote' ? videoStream.getAudioTracks() : source.type === 'screen' ? videoStream.getAudioTracks() : audioStream ? audioStream.getAudioTracks() : []),
      ]);
    }
    try { await setFinalStream(finalStream); afterSet?.(finalStream); }
    finally { releasePreviousBorrow(); acquiringRef.current = false; }
  }, [hlsProber, setFinalStream, selectedAudio, showAlert, registry, setScreenShareStreams, setSelectedVideoSource, audioOnlyVideoStream]);

  // Une demande reçue pendant une acquisition n'est plus jetée : elle est mise en attente
  // (la dernière l'emporte) et jouée dès que la précédente a rendu la main.
  //
  // Avant, `if (acquiringRef.current) return;` l'avalait en silence. Or `handleDeckSwitch` a
  // déjà écrit `selectedVideoSource` : le deck allumait la case cliquée (F) pendant que
  // l'acquisition en cours (A, typiquement l'ouverture de la webcam au chargement d'une
  // chaîne éteinte) finissait sur le final. `activeSourceRef` valait alors A, et le clic
  // suivant sur A était refusé par la garde de `handleDeckSwitch` — jusqu'à ce qu'on clique F
  // pour resynchroniser. Aucun effet ne rattrapait le coup : celui de `selectedVideoSource`
  // ne se rejoue que si la valeur change, et elle n'avait pas bougé depuis le clic.
  const acquireAndSetStream = useCallback(async (source: VideoSource, afterSet?: (stream: MediaStream) => void) => {
    if (acquiringRef.current) { pendingAcquireRef.current = { source, afterSet }; return; }
    let job: { source: VideoSource; afterSet?: (stream: MediaStream) => void } | null = { source, afterSet };
    while (job) {
      await acquireOnce(job.source, job.afterSet);
      job = pendingAcquireRef.current;
      pendingAcquireRef.current = null;
    }
  }, [acquireOnce]);

  const handleRetryHls   = useCallback((sourceId: string, url: string) => { hlsProber.retryProbe(sourceId, url); }, [hlsProber]);

  /**
   * Renommage d'une source depuis le panneau : un alias n'est qu'un nom
   * d'affichage, il ne touche ni au flux ni à l'emprunt en cours. Trois endroits
   * le portent et doivent bouger ensemble — la liste des sources (d'où le
   * multiview tire ses libellés), les cases du deck qui en gardent une copie, et
   * le registre. `register` sur un identifiant connu ne fait que remplacer le
   * libellé : le stream reste le même objet, rien n'est réacquis.
   */
  const handleRenameSource = useCallback((sourceId: string, label: string) => {
    setVideoSources(prev => {
      if (!prev.some(s => s.id === sourceId && s.label !== label)) return prev;
      return prev.map(s => (s.id === sourceId ? { ...s, label } : s));
    });
    setup.renameDeckSlotSource(sourceId, label);
    const registered = registry.get(sourceId);
    if (registered) registry.register({ id: sourceId, label, config: registered.config });
  }, [setVideoSources, setup, registry]);

  // Relance d'un flux HLS/Xtream depuis le panneau des sources — le bouton de
  // chaque ligne. Un flux qui s'est cassé (manifest perdu, token CDN périmé,
  // serveur qui a redémarré) reste bloqué tant que personne ne jette l'acquisition
  // en cours : forceRelease détruit l'instance hls.js et son <video>, et la sonde
  // repart de zéro. Les mini-vues du deck se rebranchent d'elles-mêmes dès qu'elle
  // repasse au vert (effet plus haut) ; le flux à l'antenne, lui, ne serait repris
  // par personne — on le remet donc nous-mêmes, sans quoi la relance laisserait un
  // noir jusqu'au prochain clic de l'opérateur.
  const handleReloadSource = useCallback(async (sourceId: string) => {
    const source = videoSources.find(s => s.id === sourceId);
    if (!source) return;

    const wasOnAir = activeSourceRef.current === sourceId;

    disconnectAudioForSlot(sourceId);
    const previewHandle = setup.previewBorrowHandlesRef.current.get(sourceId);
    if (previewHandle) { try { previewHandle.release(); } catch {} setup.previewBorrowHandlesRef.current.delete(sourceId); }
    if (wasOnAir) clearCurrentStream();
    registry.forceRelease(sourceId);
    previewStreamsRef.current.delete(sourceId);
    setPreviewStreams(new Map(previewStreamsRef.current));

    // Une source éteinte par un Power Off reste désactivée dans le registre :
    // relancer vaut rallumer, sinon la sonde repasserait au vert sur un flux que
    // le registre refuse toujours d'acquérir.
    setPoweredOffSlots(prev => {
      if (!prev.has(sourceId)) return prev;
      const next = new Set(prev); next.delete(sourceId); return next;
    });
    registry.enableSource(sourceId);

    // ── Sources sans manifeste : webcam, écran, smartphone, scène ────────────
    // Une source « présente dans le deck mais dont la mini-vue reste noire » a une
    // acquisition que le registre croit vivante et une piste morte : caméra
    // confisquée par une autre application, machine sortie de veille, permission
    // révoquée en cours de route. Il n'y a rien à sonder ici, contrairement au HLS
    // — le forceRelease ci-dessus a jeté l'acquisition, l'auto-borrow du deck
    // refait la mini-vue dès que le registre émet, et l'antenne est remise ici
    // puisque personne d'autre ne s'en charge. Ajouté le 22/08/2026.
    if (source.type !== 'url' || !source.externalUrl) {
      if (wasOnAir) await acquireAndSetStream(source);
      return;
    }

    hlsProber.retryProbe(sourceId, source.externalUrl, { skipRange: !!source.xtream });

    // On tient la promesse ouverte le temps que la sonde tranche : c'est elle qui
    // fait tourner l'icône du bouton côté panneau. Au-delà de 20 s la sonde continue
    // ses tentatives en fond, mais l'opérateur a sa réponse — ce n'est pas revenu.
    const deadline = Date.now() + 20_000;
    while (Date.now() < deadline) {
      const status = hlsProber.getStatus(sourceId);
      if (status === 'ok') {
        if (wasOnAir) await acquireAndSetStream(source);
        return;
      }
      if (status === 'failed') return;
      await new Promise(r => setTimeout(r, 500));
    }
  }, [videoSources, disconnectAudioForSlot, setup, clearCurrentStream, registry, previewStreamsRef, setPreviewStreams, setPoweredOffSlots, hlsProber, acquireAndSetStream]);
  const handleDeckSwitch  = useCallback(async (source: VideoSource) => {
    if (activeSourceRef.current === source.id && currentStreamRef.current?.active) return;
    // « OBS, RTMP... » n'est pas sondée en continu : on la teste ICI, au clic. Sans cela
    // acquireAndSetStream refuserait en silence (statut jamais 'ok').
    if (source.type === 'url' && source.id.startsWith('hls-live-') && source.externalUrl
        && hlsProber.getStatus(source.id) !== 'ok') {
      const ok = await hlsProber.probeNow(source.id, source.externalUrl, { persistent: true });
      if (!ok) { showAlert("La source « OBS, RTMP... » ne publie pas pour le moment.", 'important'); return; }
    }
    setSelectedVideoSource(source.id); acquireAndSetStream(source);
  }, [acquireAndSetStream, setSelectedVideoSource, hlsProber, showAlert]);
  const handleDeckSwitchSource = useCallback((source: VideoSource) => { handleDeckSwitch(source); }, [handleDeckSwitch]);

  // ── Mini-deck des devices Réalisateur ──────────────────────────────────────
  //
  // Le device n'a aucune connaissance des sources de la régie : on lui envoie la
  // liste telle qu'elle est affichée sur le pupitre — même ordre, même
  // numérotation, mêmes libellés (alias et libellé personnalisé du slot compris),
  // pour qu'un opérateur devant le mini-deck et un autre devant la régie parlent
  // des mêmes boutons.
  //
  // Seules les cases réellement pourvues sont envoyées : buildVideoRow renvoie
  // SLOT_COUNT lignes, la plupart vides. `index` porte donc le rang d'origine et
  // non la position dans la liste transmise.
  const directorDeckPayload = useMemo(() => {
    const rows = buildVideoRow(deckSlots, channelUuid);
    const sources = rows.flatMap((row, i) => {
      if (!row.slot) return [];
      const custom = slotConfigs[i]?.customLabel?.trim();
      return [{
        id:    row.slot.id,
        label: custom || resolveSourceLabel(row.slot, sourceAliases),
        index: i,
      }];
    });
    // Une scène en final n'est pas une source du deck : le mini-deck n'affiche
    // alors aucun bouton actif, plutôt que d'en désigner un à tort.
    return { sources, finalId: activeLayoutId ? null : (selectedVideoSource || null) };
  }, [deckSlots, slotConfigs, sourceAliases, selectedVideoSource, activeLayoutId,
      channelUuid]);

  // ── Réalisateur auto ───────────────────────────────────────────────────────
  //
  // Candidats = les sources du deck, avec les libellés que voit l'opérateur (ceux
  // du mini-deck). Une source éteinte par Power Off ne peut pas prendre l'antenne.
  // Il ne décide que si ce poste tient la main (sinon deux régies se battraient
  // pour le final), que la régie est sous tension et qu'aucune scène n'est au final.
  const autoDirectorSourceIds = useMemo(
    // Audio seul exclu : le réalisateur suit le son le plus fort, il couperait sur du noir.
    () => directorDeckPayload.sources
      .filter(s => !poweredOffSlots.has(s.id) && deckSlots.find(d => d?.id === s.id)?.type !== 'audio')
      .map(s => s.id),
    [directorDeckPayload, poweredOffSlots, deckSlots]);
  const handleAutoDirectorSwitch = useCallback((sourceId: string) => {
    const source = deckSlots.find(s => s?.id === sourceId);
    if (source) handleDeckSwitch(source);
  }, [deckSlots, handleDeckSwitch]);
  const autoDirector = useAutoDirector({
    channelUuid,
    deckSourceIds: autoDirectorSourceIds,
    currentId: activeLayoutId ? null : (selectedVideoSource || null),
    canDecide: setup.controlState.isHolder && isRegiePowered && !activeLayoutId,
    onSwitch: handleAutoDirectorSwitch,
  });
  applyAutoDirectorRef.current = autoDirector.setSettings;
  setAutoDirectorEnabledRef.current = autoDirector.setEnabled;

  // Rediffusion à tout changement du deck, du final ou de la liste des porteurs
  // du rôle. Le payload sérialisé est comparé au dernier envoi de chaque device :
  // sans ce garde, le moindre re-render de la régie renverrait le deck entier,
  // et les effets qui recalculent deckSlots sont fréquents.
  const lastDirectorDeckRef = useRef<Map<string, string>>(new Map());
  useEffect(() => {
    const serialized = JSON.stringify(directorDeckPayload);
    directorDevices.forEach(deviceId => {
      if (lastDirectorDeckRef.current.get(deviceId) === serialized) return;
      lastDirectorDeckRef.current.set(deviceId, serialized);
      sendDirectorDeck(deviceId, directorDeckPayload.sources, directorDeckPayload.finalId);
    });
    // Un device qui perd le rôle est oublié, pour que le deck lui soit renvoyé
    // en entier s'il le récupère plus tard.
    lastDirectorDeckRef.current.forEach((_, deviceId) => {
      if (!directorDevices.has(deviceId)) lastDirectorDeckRef.current.delete(deviceId);
    });
    // Un device DÉCONNECTÉ est oublié pour la même raison : il a rechargé sa page
    // et perdu son deck, mais il garde son rôle — celui-ci vit désormais sur sa
    // fiche et non dans la session. Sans cet oubli, le garde ci-dessus conclurait
    // « déjà envoyé » et le réalisateur retrouverait un mini-deck vide.
    remoteDevices.forEach(d => {
      if (!d.is_connected) lastDirectorDeckRef.current.delete(d.device_id);
    });
  }, [directorDeckPayload, directorDevices, remoteDevices, sendDirectorDeck]);

  // ── Prise de main : réaffirmer ce que les téléphones affichent ─────────────
  //
  // `on_air` et `director_deck` sont émis AU CHANGEMENT, et le suivi de ce
  // changement est propre à chaque régie (`onAirDeviceIdRef`,
  // `lastDirectorDeckRef`). Du point de vue de la régie qui vient de prendre la
  // main, rien n'a changé : elle n'émet donc rien, et les téléphones gardent ce
  // que l'AUTRE régie y avait laissé — un voyant rouge sur une caméra qui n'est
  // plus à l'antenne, un mini-deck périmé. Rien ne les en sortait.
  //
  // On n'essaie pas de deviner ce que l'autre avait envoyé : on réaffirme l'état
  // complet, `active: false` compris. C'est le seul moyen d'éteindre un tally
  // dont on n'a jamais eu connaissance.
  //
  // Armé sur le front montant, exécuté quand la liste est là : au moment du
  // `control_granted`, le `loadDevices()` déclenché par le `device_list` qui suit
  // est encore en vol, et `remoteDevices` date de l'ouverture de l'onglet.
  const etaitPorteurRef   = useRef(setup.controlState.isHolder);
  const resyncTallyRef    = useRef(false);
  useEffect(() => {
    const isHolder = setup.controlState.isHolder;
    if (!etaitPorteurRef.current && isHolder) {
      resyncTallyRef.current = true;
      // Reprendre la main est une situation NEUVE : l'acquittement du bandeau
      // d'observation ne vaut plus. Sans ce réarmement, une régie qui reprend un
      // poste qu'elle avait déjà tenu retrouve la signature acquittée la fois
      // d'avant, et le bandeau ne revient jamais — au moment précis où savoir
      // qui l'observe compte le plus. Ce détecteur de front est le SEUL de ce
      // fichier : en poser un second le neutraliserait, le premier exécuté
      // consommant le front pour l'autre.
      setObservationAcquittee('');
    }
    etaitPorteurRef.current = isHolder;

    if (!resyncTallyRef.current || !isHolder) return;
    const connectes = remoteDevices.filter(d => d.is_connected);
    if (connectes.length === 0) return;

    const ws = signalingWsRef.current;
    // WS pas encore prête : on garde l'armement pour le rendu suivant plutôt que
    // de laisser le parc avec l'affichage de l'autre régie.
    if (ws?.readyState !== WebSocket.OPEN) return;
    resyncTallyRef.current = false;

    const onAirId = onAirDeviceIdRef.current;
    connectes.forEach(d => {
      ws.send(JSON.stringify({
        type: 'on_air', target: d.device_id, active: d.device_id === onAirId,
      }));
    });
    addSdpLogRegie({
      direction: 'sent', type: 'event',
      label: `📡 Prise de main — tally réaffirmé pour ${connectes.length} device(s)`
           + (onAirId ? ` (à l'antenne : ${onAirId})` : ' (aucun à l\'antenne)'),
    });

    // Le dédoublonnage du mini-deck conclurait « déjà envoyé » sur un deck que
    // c'est l'AUTRE régie qui avait poussé. On l'oublie, et on repousse en entier.
    lastDirectorDeckRef.current.clear();
    const serialized = JSON.stringify(directorDeckPayload);
    directorDevices.forEach(deviceId => {
      lastDirectorDeckRef.current.set(deviceId, serialized);
      sendDirectorDeck(deviceId, directorDeckPayload.sources, directorDeckPayload.finalId);
    });
  }, [
    setup.controlState.isHolder, remoteDevices, directorDevices, directorDeckPayload,
    sendDirectorDeck, signalingWsRef, addSdpLogRegie,
  ]);

  /**
   * Un device réclame une source en final depuis son mini-deck.
   *
   * Deux vérifications, parce que le microservice relaie sans contrôle et que
   * n'importe quel device authentifié peut donc émettre ce message : le device
   * doit porter le rôle, et la source demandée doit être présente dans le deck
   * courant. Sans quoi un device pourrait piloter l'antenne, ou y envoyer une
   * source retirée du pupitre entre-temps.
   */
  const handleDirectorTake = useCallback((deviceId: string, sourceId: string) => {
    if (!directorDevicesRef.current.has(deviceId)) {
      console.warn(`[DIRECTOR] director_take ignoré : ${deviceId} ne porte pas le rôle Réalisateur`);
      return;
    }
    const slot = deckSlotsRef.current.find(s => s?.id === sourceId);
    if (!slot) {
      console.warn(`[DIRECTOR] director_take ignoré : source ${sourceId} absente du deck`);
      return;
    }
    handleDeckSwitch(slot);
  }, [handleDeckSwitch, directorDevicesRef, deckSlotsRef]);

  // ── L'antenne demandée par un téléphone ──────────────────────────────────
  //
  // Le téléphone qui a créé le direct depuis l'app Kleon ne prend plus l'antenne
  // tout seul. Jusqu'au 12/09/2026 il publiait sur `webrtc_{uuid}` — le chemin
  // programme, celui où cette régie publie son canvas EN PERMANENCE dès qu'elle
  // pilote (cf. PERSISTENT_PROGRAM_WHIP) — puis basculait la source : la
  // playlist sautait sans que la régie ait rien demandé, MediaMTX évinçait l'un
  // des deux publieurs, et l'appareil n'apparaissait jamais ici en SFU puisqu'il
  // ne publiait pas sur SA voie.
  //
  // Il publie désormais sur sa voie comme n'importe quelle caméra, et DEMANDE.
  // Le microservice ne relaie qu'à la régie qui pilote : si le message arrive
  // ici, c'est à nous de passer en mode régie — l'appareil en final, puis le
  // démarrage habituel, celui du bouton « Live régie ». Le téléphone apprend
  // qu'il est à l'antenne par le `on_air` que la sélection du deck émet déjà.
  //
  // Les refs : ce qu'on attend (le flux de l'appareil, l'image en régie) arrive
  // APRÈS la demande — le téléphone envoie `live_request` sitôt sa publication
  // lancée, avant que notre WHEP ait ramené sa vidéo. On relit donc l'état au
  // fil de l'attente plutôt que la photo prise à la réception du message.
  const videoSourcesRef = useRef(videoSources);
  useEffect(() => { videoSourcesRef.current = videoSources; }, [videoSources]);
  const streamSourceRef = useRef(streamSource);
  useEffect(() => { streamSourceRef.current = streamSource; }, [streamSource]);
  const handleStartStreamingRef = useRef<() => void>(() => {});
  const handleStopStreamingRef  = useRef<() => Promise<void>>(async () => {});
  const liveRequestPendingRef   = useRef<string | null>(null);

  const handleLiveRequest = useCallback(async (deviceId: string, active: boolean, label: string) => {
    const sourceId = `remote-${deviceId}`;

    if (!active) {
      liveRequestPendingRef.current = null;
      if (streamSourceRef.current !== 'webrtc') return;
      addSdpLogRegie({ direction: 'info', type: 'event', label: `📡 ${label} rend l'antenne — retour à la source par défaut`, deviceId });
      showAlert(`${label} a mis fin au direct : la chaîne revient à sa programmation.`, 'normal');
      await handleStopStreamingRef.current();
      return;
    }

    // Deux demandes du même appareil (retentative côté téléphone) ne doivent
    // pas lancer deux démarrages ; une demande d'un AUTRE appareil remplace la
    // précédente — c'est la dernière volonté qui compte, comme sur le deck.
    liveRequestPendingRef.current = deviceId;
    const stillWanted = () => liveRequestPendingRef.current === deviceId;

    const waitFor = async (ready: () => boolean, timeoutMs: number): Promise<boolean> => {
      const deadline = Date.now() + timeoutMs;
      while (Date.now() < deadline) {
        if (!stillWanted()) return false;
        if (ready()) return true;
        await new Promise(r => setTimeout(r, 250));
      }
      return ready();
    };

    // 1. Son image. Le WHEP sur sa voie SFU est ouvert à `device_connected`,
    //    quelques instants avant ce message ; la piste vidéo suit dès que le
    //    téléphone publie pour de bon.
    const findSource = () => videoSourcesRef.current.find(s => s.id === sourceId && s.stream);
    if (!(await waitFor(() => !!findSource(), 20_000))) {
      if (!stillWanted()) return;
      liveRequestPendingRef.current = null;
      addSdpLogRegie({ direction: 'received', type: 'error', label: `⚠ ${label} demande l'antenne mais son image n'est pas arrivée en 20 s`, deviceId });
      showAlert(`${label} demande l'antenne, mais son image n'arrive pas en régie. Vérifiez la connexion de l'appareil, puis passez-le à l'antenne depuis le deck.`, 'important');
      return;
    }
    const source = findSource()!;

    // 2. En final. C'est ce geste qui envoie `on_air` au téléphone, et qui
    //    monte son image dans le canvas — donc dans le programme publié.
    addSdpLogRegie({ direction: 'info', type: 'event', label: `📡 ${label} demande l'antenne — passage en final`, deviceId });
    handleDeckSwitch(source);

    // 3. Le mode régie, si la chaîne n'y est pas déjà. Le démarrage a besoin
    //    d'une image montée (cf. startWebRTCStreamingImpl) : on lui laisse le
    //    temps d'arriver — c'est celle qu'on vient de choisir.
    if (streamSourceRef.current === 'webrtc') {
      liveRequestPendingRef.current = null;
      showAlert(`${label} est passé à l'antenne.`, 'normal');
      return;
    }
    if (!(await waitFor(() => !!currentStreamRef.current, 10_000))) {
      if (!stillWanted()) return;
      liveRequestPendingRef.current = null;
      addSdpLogRegie({ direction: 'received', type: 'error', label: `⚠ Image de ${label} non montée en régie : démarrage du direct impossible`, deviceId });
      showAlert(`L'image de ${label} ne s'est pas montée en régie : lancez le direct à la main une fois qu'elle apparaît.`, 'important');
      return;
    }
    if (!stillWanted()) return;
    liveRequestPendingRef.current = null;
    showAlert(`${label} demande l'antenne : la régie passe en direct avec cet appareil en final.`, 'normal');
    handleStartStreamingRef.current();
  }, [handleDeckSwitch, currentStreamRef, addSdpLogRegie, showAlert]);

  /**
   * `autoRetry` : recharge le manifeste en place au lieu de laisser la source en
   * erreur. Indispensable pour une caméra RTMP — sa playlist n'existe pas tant
   * que le cadreur distant n'a pas lancé son encodeur, et l'ordre normal des
   * choses est justement d'ajouter la caméra AVANT qu'elle publie. Sans lui, le
   * bouton du deck se fige sur l'erreur du premier 404 et ne revient jamais.
   */
  const handleAddHlsSource = useCallback((
    url    : string,
    xtream?: XtreamCredentials,
    label? : string,
    opts?  : { autoRetry?: boolean },
  ) => {
    const id = hlsSourceId(url, xtream);
    const derivedLabel = label ?? url.split('/').filter(Boolean).pop()?.replace('.m3u8', '') ?? 'HLS';
    const newSource: VideoSource = { id, label: derivedLabel, type: 'url', externalUrl: url, xtream };
    setVideoSources(prev => {
      if (prev.find(s => s.id === id)) return prev;
      return [...prev, newSource];
    });
    registry.register({ id, label: derivedLabel, config: { type: 'hls', url, xtream, autoRetry: opts?.autoRetry } });
    hlsProber.startProbe(id, url, { skipRange: !!xtream, persistent: !!opts?.autoRetry });
    // Rendu à l'appelant : le rechargement s'en sert pour reconnaître les
    // sources encore déclarées côté microservice. Recalculer la formule de
    // l'identifiant ailleurs, c'est se condamner à la voir diverger.
    return id;
  }, [setVideoSources, registry, hlsProber]);

  // Chargement d'ouverture. Le corps vit plus bas (reloadExternalSourcesRef) :
  // il a besoin de handleRemoveSource, et il est rejoué à chaque changement
  // annoncé par le microservice comme à la prise de main.
  useEffect(() => {
    if (!channelUuid) return;
    reloadExternalSourcesRef.current();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings.serverUrl, channelUuid]);


  const handleDeckPreviewWrapped = useCallback(async (source: VideoSource) => { setActiveLayoutPreviewId(null); handleDeckPreview(source); }, [handleDeckPreview]);

  const handleDeckCut = useCallback(() => {
    if (activeLayoutPreviewId) {
      const layoutSlot = layoutSlots.find(l => l.id === activeLayoutPreviewId);
      if (layoutSlot) {
        if (activeLayoutId && activeLayoutId !== activeLayoutPreviewId) { scenePipelineManager.deactivateScene(activeLayoutId); setActiveSceneLayoutIds(prev => prev.filter(id => id !== activeLayoutId)); }
        setActiveLayoutId(activeLayoutPreviewId); setActiveLayoutPreviewId(null); setDeckPreviewSourceId(null); return;
      }
    }
    if (!deckPreviewSourceId) return;
    const src = videoSources.find(s => s.id === deckPreviewSourceId); if (!src) return;
    if (activeLayoutId && src.type !== 'scene') {
      if (activeLayoutPreviewId !== activeLayoutId) { scenePipelineManager.deactivateScene(activeLayoutId); setActiveSceneLayoutIds(prev => prev.filter(id => id !== activeLayoutId)); setActiveLayoutId(null); }
    }
    handleDeckSwitch(src);
  }, [activeLayoutPreviewId, layoutSlots, activeLayoutId, deckPreviewSourceId, videoSources, handleDeckSwitch, setDeckPreviewSourceId, scenePipelineManager]);

  useEffect(() => { deckCutCallbackRef.current = handleDeckCut; }, [handleDeckCut]);

  // ── T-bar : fondu enchaîné preview → final ─────────────────────────────────
  // Le fondu n'est branché que pour une source simple (pas de scène/layout en
  // aperçu) : la boucle de composition ne sait mélanger que sourceVideoEl et
  // deckPreviewVideoRef, pas le rendu d'un layout. Sur une scène, le T-bar
  // retombe sur le cut instantané existant (handleDeckCut) — pas de fondu là.
  const [tbarEnchainementId, setTbarEnchainementId] = useState('fondu');
  // Config par enchaînement (ex. direction du Poussé/Recouvrement), gardée
  // même quand on change d'enchaînement puis qu'on y revient.
  const [tbarEnchainementConfigs, setTbarEnchainementConfigs] = useState<Record<string, unknown>>({});

  const activeTbarEnchainement = getEnchainementById(tbarEnchainementId);
  const activeTbarEnchainementConfig = tbarEnchainementConfigs[tbarEnchainementId] ?? activeTbarEnchainement.defaultConfig;

  const handleTbarEnchainementConfigChange = useCallback((config: unknown) => {
    setTbarEnchainementConfigs(prev => ({ ...prev, [tbarEnchainementId]: config }));
  }, [tbarEnchainementId]);

  const handleTbarProgress = useCallback((progress: number) => {
    const canFade = !activeLayoutPreviewId && !activeLayoutId && !!deckPreviewSourceId;
    setDeckTransitionProgress(
      canFade ? deckPreviewVideoRef.current : null,
      canFade ? activeTbarEnchainement : null,
      activeTbarEnchainementConfig,
      progress,
    );
  }, [activeLayoutPreviewId, activeLayoutId, deckPreviewSourceId, deckPreviewVideoRef, activeTbarEnchainement, activeTbarEnchainementConfig, setDeckTransitionProgress]);

  // ⚠️ Ne PAS couper le fondu ici : handleDeckCut → handleDeckSwitch →
  // acquireAndSetStream est ASYNCHRONE (acquisition de la source, puis
  // startCanvasForStream qui attend que le nouvel élément <video> soit prêt).
  // Couper immédiatement ferait retomber la boucle de composition sur
  // l'ANCIEN sourceVideoEl le temps que la vraie bascule aboutisse — un
  // flash retour en arrière, juste après avoir affiché 100% preview. Le
  // fondu reste donc figé à progress=1 (visuellement identique à ce que le
  // nouveau flux final affichera une fois basculé) ; il n'est nettoyé que
  // plus tard, quand une NOUVELLE preview est choisie (cf. l'effet plus bas)
  // — signe que la bascule précédente a eu largement le temps d'aboutir.
  const handleTbarCommit = useCallback(() => {
    handleDeckCut();
  }, [handleDeckCut]);

  const tbar = useTbarTransition({ onProgress: handleTbarProgress, onCommit: handleTbarCommit });

  // Nettoyage différé du fondu figé (cf. commentaire ci-dessus) : dès qu'une
  // nouvelle preview est sélectionnée, la bascule précédente a forcément eu
  // le temps d'aboutir — sûr de couper ici, contrairement à au commit.
  const prevDeckPreviewSourceIdRef = useRef(deckPreviewSourceId);
  useEffect(() => {
    if (deckPreviewSourceId !== prevDeckPreviewSourceIdRef.current) {
      prevDeckPreviewSourceIdRef.current = deckPreviewSourceId;
      setDeckTransitionProgress(null, null, undefined, 0);
    }
  }, [deckPreviewSourceId, setDeckTransitionProgress]);

  // LE RETOUR DE LA RÉGIE. C'est une adresse PUBLIQUE — elle passe par
  // cast.kleonpro.tv comme celle d'un spectateur — donc la référence, pas l'uuid.
  const monitorHlsUrl = channelPublicRef ? `${settings.serverUrl}/${channelPublicRef}/index.m3u8` : '';

  // Exécution réelle du démarrage — appelée directement si le multiview n'est pas
  // actif, ou après confirmation de LiveExportModal sinon (cf. handleStartStreaming).
  const startStreamingConfirmed = useCallback(async () => {
    if (isStartingStream || isStoppingStream) return;
    setIsStartingStream(true);
    setIsStreamButtonActive(true);
    const currentSource = broadcastingChannel?.streamSource ?? 'video';
    if (currentSource !== 'webrtc') previousStreamSourceRef.current = currentSource;
    try {
      if (!isStreaming) await startWebRTCStreaming();
      const uuid = requireChannelUuid();
      const webrtcHlsUrl = `${settings.serverUrl}/webrtc_${channelPublicRef}/index.m3u8`;
      addSdpLogRegie({ direction: 'info', type: 'event', label: `⏳ En attente du flux HLS WebRTC (${webrtcHlsUrl})…` });
      const available = await waitForHls(webrtcHlsUrl);
      if (!available) {
        addSdpLogRegie({ direction: 'received', type: 'error', label: '⚠️ Flux HLS WebRTC non détecté après 30s' });
        showAlert('Le flux WebRTC démarre mais le HLS n\'est pas encore disponible.', 'important');
        setIsStreamButtonActive(false);
        return;
      }
      if (broadcastingChannel?.id) {
        const result = await updateBroadcastingChannelStreamSource(broadcastingChannel.id, 'webrtc');
        addSdpLogRegie({ direction: result.code === 1 ? 'info' : 'received', type: result.code === 1 ? 'event' : 'error', label: result.code === 1 ? '✅ Chaîne basculée sur le flux WebRTC' : `⚠️ ${result.comment}` });
        if (result.code === 1) {
          onConfirmStreamSource?.('webrtc');
          autoRecordPendingRef.current = true;
          // Annonce sur les réseaux (2026-10-17) : la bascule vient peut-être de la
          // lancer côté serveur. Une réussite s'inscrit au journal ; un échec ouvre
          // une alerte, pour que le réalisateur sache que Facebook ou Instagram n'a
          // rien reçu (le direct, lui, n'est pas touché).
          const announceAdminId = authentication.admin?.id;
          if (announceAdminId) {
            followLiveAnnounce(announceAdminId, broadcastingChannel.id, new Date(), done => {
              const lines = Object.entries(done.networks).map(([code, r]) =>
                `${code === 'facebook' ? 'Facebook' : 'Instagram'} : ${r?.ok ? 'publiée' : `refusée (${r?.error ?? 'raison inconnue'})`}`);
              const failed = Object.values(done.networks).some(r => r && !r.ok);
              addSdpLogRegie({ direction: failed ? 'received' : 'info', type: failed ? 'error' : 'event', label: `📣 Annonce du direct — ${lines.join(' · ')}` });
              if (failed) showAlert(`L'annonce du direct n'est pas partie partout. ${lines.join(' — ')}. Voir l'onglet Configuration de la chaîne.`);
            });
          }
        } else {
          // Le flux WebRTC est bien démarré côté navigateur (waitForHls a réussi juste
          // avant), mais le backend a refusé de basculer streamSource — sans ce garde-fou,
          // le sélecteur de source restait affiché sur l'ancienne source (rien ne confirme
          // le changement) alors que le bouton "Direct" affichait déjà l'état démarré.
          setIsStreamButtonActive(false);
          showAlert(`La chaîne n'a pas basculé sur le flux WebRTC : ${result.comment}`, 'important');
        }
      }
    } catch (err: any) {
      // Sans ce catch, une exception dans startWebRTCStreaming()/waitForHls() (négociation
      // WHIP, ICE, etc.) mourait silencieusement : aucune alerte, le sélecteur de source
      // restait sur l'ancienne valeur (le code n'atteignait jamais l'appel backend), et
      // isStreamButtonActive restait bloqué à true (déjà positionné en optimiste plus haut).
      setIsStreamButtonActive(false);
      addSdpLogRegie({ direction: 'received', type: 'error', label: `❌ Démarrage du direct échoué : ${err?.message ?? err}` });
      showAlert(`Impossible de démarrer le direct : ${err?.message ?? err}`, 'important');
    } finally { setIsStartingStream(false); }
  }, [isStartingStream, isStoppingStream, isStreaming, broadcastingChannel, settings.serverUrl, channelUuid, startWebRTCStreaming, addSdpLogRegie, showAlert, onConfirmStreamSource, authentication]);

  // Clic sur « Live régie ». Multiview actif → comme Rec, on demande d'abord quels
  // angles de la grille partent au public (LiveExportModal) ; le démarrage réel n'a
  // lieu qu'à la confirmation (handleLiveExportConfirm). Sinon, rien à choisir : on
  // démarre directement, comportement inchangé.
  const handleStartStreaming = useCallback(() => {
    if (isStartingStream || isStoppingStream) return;
    if (multiviewActive) { liveExportModalTriggerRef.current = 'start'; setShowLiveExportModal(true); return; }
    startStreamingConfirmed();
  }, [isStartingStream, isStoppingStream, multiviewActive, startStreamingConfirmed]);

  // Relance automatique de la régie (cf. regieRelaunchPendingRef) : même geste qu'un clic sur
  // « Live régie », une fois la chaîne active et une source vidéo acquise.
  useEffect(() => {
    if (!regieRelaunchPendingRef.current) return;
    if (broadcastingChannelStatus !== 'active') return;
    if (isStreaming || isStartingStream || isStoppingStream) return;
    if (!currentStream) return;
    regieRelaunchPendingRef.current = false;
    handleStartStreaming();
  }, [broadcastingChannelStatus, currentStream, isStreaming, isStartingStream, isStoppingStream, handleStartStreaming]);

  // Confirmation de LiveExportModal — le déclencheur (liveExportModalTriggerRef) dit
  // s'il faut démarrer la diffusion ou seulement retenir la sélection. Voir la remarque
  // sur ce ref plus haut : `isStreaming` ne peut PAS servir à le deviner ici.
  const handleLiveExportConfirm = useCallback((selectedIds: Set<string>) => {
    setShowLiveExportModal(false);
    setExportedMultiviewSourceIds(selectedIds);
    if (liveExportModalTriggerRef.current === 'start') startStreamingConfirmed();
  }, [startStreamingConfirmed]);

  // Bascule du bouton Multi-angle de la ControlBar (onMultiviewChange) : si la régie
  // diffuse déjà en 'webrtc' au moment où le multiview s'active, la grille vient
  // d'apparaître et rien n'a encore été choisi pour l'export — on redemande, comme au
  // démarrage. Désactiver le multiview, en revanche, ne demande rien : la grille
  // disparaît, multiviewAngleSlots retombe à [] de lui-même.
  const handleMultiviewChange = useCallback((value: boolean) => {
    setMultiviewActive(value);
    if (value && isStreaming && streamSource === 'webrtc') {
      liveExportModalTriggerRef.current = 'update';
      setShowLiveExportModal(true);
    }
  }, [isStreaming, streamSource]);

  const doStopAndSwitch = useCallback(async (targetSource: string, stopWebRTC = true) => {
    onStreamSourceChange?.(targetSource as StreamSource);
    if (broadcastingChannel?.id) { const result = await updateBroadcastingChannelStreamSource(broadcastingChannel.id, targetSource); addSdpLogRegie({ direction: result.code === 1 ? 'info' : 'received', type: result.code === 1 ? 'event' : 'error', label: result.code === 1 ? `✅ Chaîne basculée sur "${targetSource}"` : `⚠️ ${result.comment}` }); if (result.code === 1) onStreamSourceChange?.(targetSource as StreamSource); }
    if (stopWebRTC) stopStreamingImpl();
  }, [broadcastingChannel, addSdpLogRegie, stopStreamingImpl, onStreamSourceChange]);

  const handleStopStreaming = useCallback(async () => {
    if (isStoppingStream || isStartingStream) return;
    setIsStoppingStream(true);
    const uuid = channelUuid;
    if (!uuid) { setIsStoppingStream(false); return; }
    const fallbackSource  = defaultStreamSource ?? 'video';
    // Un REC tient le flux WebRTC — sauf celui du live, qui s'arrête avec lui.
    const liveOwnsRecording = isRecording && recordingSessionId !== null && recordingSessionId === liveRecordingSessionIdRef.current;
    const shouldStopWebRTC  = !isRecording || liveOwnsRecording;
    // REC du live arrêté AVANT la bascule (c'est lui qui retient le WebRTC), question APRÈS :
    // la fenêtre ne doit pas s'afficher sur un direct encore à l'antenne.
    const stopAndOfferPublication = async () => {
      autoRecordPendingRef.current = false;
      const liveSession = await finishLiveRecordingRef.current();
      await doStopAndSwitch(fallbackSource, shouldStopWebRTC);
      setIsStreamButtonActive(false);
      if (!liveSession) return;
      // Prise multi-angle : ses autres fichiers rendent la fiche Publier capable de multiview.
      const takeSessions = liveSession.takeId ? await refreshRecordingHistory() : null;
      setLiveRecordingAngleCount(takeSessions?.filter(s => s.takeId === liveSession.takeId && s.id !== liveSession.id && !!s.storagePath).length ?? 0);
      if (!liveSession.storagePath) {
        showAlert('L\'enregistrement de ce live n\'a pas été rangé dans votre espace : publication impossible pour l\'instant. Il reste disponible dans Stockage.', 'important', { width: END_OF_LIVE_DIALOG_WIDTH });
        return;
      }
      // Taille et durée quand le serveur les connaît déjà : elles disent ce que pèse le
      // fichier qu'on garde ou qu'on publie.
      const facts = [
        liveSession.stoppedAt ? computeDuration(liveSession) : null,
        liveSession.sizeBytes ? formatFileSize(liveSession.sizeBytes) : null,
      ].filter(x => x && x !== '—').join(' · ');
      showConfirm(
        <span>
          <strong>Le live est terminé et son enregistrement est prêt{facts ? ` (${facts})` : ''}.</strong><br />
          Voulez-vous le publier maintenant en tant que vidéo ?
        </span>,
        () => setLiveRecordingToPublish(liveSession),
        {
          validButtonText : 'Oui, publier',
          cancelButtonText: 'Non, plus tard',
          // La fenêtre fait 300 px par défaut : trop étroit pour ces deux messages.
          width: END_OF_LIVE_DIALOG_WIDTH,
          // « Non » (2026-10-24) : dire où est passé le fichier et ce qu'il coûte.
          onCancel: () => showAlert(
            <span>
              <strong>L'enregistrement est conservé dans votre espace de stockage.</strong><br />
              Vous le retrouverez à tout moment avec le bouton <strong>Stockage</strong> de la régie : vous pourrez
              alors le publier, le télécharger ou le renommer.<br /><br />
              Il occupe de la place dans votre quota{facts ? ` (${facts})` : ''} : supprimez-le depuis Stockage
              dès qu'il ne vous sert plus, pour libérer de l'espace.
            </span>,
            undefined,
            { width: END_OF_LIVE_DIALOG_WIDTH },
          ),
        });
    };
    try {
      const fallbackHlsUrl = fallbackSource === 'rtmp' ? `${settings.serverUrl}/live_${channelPublicRef}/index.m3u8` : `${settings.serverUrl}/video_${channelPublicRef}/index.m3u8`;
      addSdpLogRegie({ direction: 'info', type: 'event', label: `⏳ Vérification flux de secours (${fallbackSource})…` });
      const fallbackAvailable = await probeHls(fallbackHlsUrl);
      if (!fallbackAvailable) {
        setIsStoppingStream(false);
        showConfirm(`Le flux de secours (${fallbackSource}) ne semble pas disponible. Arrêter quand même ?`, async () => {
          setIsStoppingStream(true);
          try {
            await stopAndOfferPublication();
          } catch (err: any) {
            // Même trou que le try principal ci-dessous : sans ce catch, un échec ici
            // (confirmation "arrêter quand même ?") mourait silencieusement.
            addSdpLogRegie({ direction: 'received', type: 'error', label: `❌ Arrêt du direct échoué : ${err?.message ?? err}` });
            showAlert(`Impossible d'arrêter le direct : ${err?.message ?? err}`, 'important');
          } finally {
            setIsStoppingStream(false);
          }
        });
        return;
      }
      await stopAndOfferPublication();
    } catch (err: any) {
      // Sans ce catch, une exception dans probeHls()/doStopAndSwitch() mourait
      // silencieusement : isStreamButtonActive restait bloqué, aucune alerte visible
      // (même défaut que handleStartStreaming, cf. le bug du sélecteur de source figé).
      addSdpLogRegie({ direction: 'received', type: 'error', label: `❌ Arrêt du direct échoué : ${err?.message ?? err}` });
      showAlert(`Impossible d'arrêter le direct : ${err?.message ?? err}`, 'important');
    } finally { setIsStoppingStream(false); }
  }, [isStoppingStream, isStartingStream, isRecording, recordingSessionId, broadcastingChannel, settings.serverUrl, channelUuid, defaultStreamSource, addSdpLogRegie, showConfirm, doStopAndSwitch, showAlert]);

  // ── Historique des enregistrements (estimation quota disque restant) ────────
  // Chargé au montage / changement de chaîne, puis rafraîchi après chaque stop
  // pour intégrer la session qui vient de se terminer dans la moyenne empirique.
  const refreshRecordingHistory = useCallback(async (): Promise<RecordingSession[] | null> => {
    const uuid = channelUuid;
    if (!uuid) return null;
    try {
      const sessions = await getRecordingSessions(uuid);
      setRecordingHistory(sessions);
      return sessions;
    } catch {
      // best-effort — l'estimation retombe sur le débit configuré si l'historique manque
      return null;
    }
  }, [broadcastingChannel, channelUuid]);

  useEffect(() => { refreshRecordingHistory(); }, [refreshRecordingHistory]);

  // Estimation statique : quota restant AVANT la session en cours (n'évolue pas tant
  // que l'enregistrement tourne — admin.quotaUsed n'est recrédité qu'au stop).
  const remainingRecordingSec = useMemo(() => {
    const admin = authentication.admin;
    if (!admin) return null;
    const bytesPerSecond = empiricalBytesPerSecond(recordingHistory, admin.id)
      ?? configuredBytesPerSecond(settings.videoBitrate, settings.audioBitrate);
    return estimateRemainingRecordingSeconds(admin.quotaDisk, admin.quotaUsed, bytesPerSecond);
  }, [authentication.admin, recordingHistory, settings.videoBitrate, settings.audioBitrate]);

  // Snapshot de remainingRecordingSec au moment où l'enregistrement démarre.
  const remainingAtRecordingStartRef = useRef<number | null>(null);

  // Angles publiés pour la prise en cours : une publication WHIP et un emprunt de source
  // par angle. Dans une ref et non un état — rien ne les affiche, et le rendu ne doit pas
  // dépendre d'objets WebRTC. Les PATHS correspondants sont détruits côté serveur par
  // /recording/stop ; ici on ne referme que ce que le navigateur tient.
  const anglePublicationsRef = useRef<AnglePublication[]>([]);
  const angleBorrowsRef      = useRef<BorrowHandle[]>([]);

  /**
   * Referme tout ce que la régie tient pour les angles de la prise en cours : publications
   * WHIP puis emprunts au registry. Idempotent, et volontairement insensible aux erreurs —
   * un angle qu'on n'arrive pas à refermer proprement ne doit jamais empêcher l'arrêt de
   * l'enregistrement.
   */
  const releaseAnglePublications = useCallback(async () => {
    const publications = anglePublicationsRef.current;
    const borrows      = angleBorrowsRef.current;
    anglePublicationsRef.current = [];
    angleBorrowsRef.current      = [];

    for (const publication of publications) {
      try { await closeAnglePublication(publication); }
      catch (e) { console.warn('[angles] Fermeture de la publication échouée', publication.angleId, e); }
    }
    for (const borrow of borrows) {
      try { borrow.release(); }
      catch (e) { console.warn('[angles] Libération de la source empruntée échouée', e); }
    }
  }, []);

  // Compte à rebours interne (pour l'arrêt automatique au quota, cf. useEffect plus bas) —
  // PAS affiché tel quel : le bouton affiche la valeur figée au démarrage (displayRemainingRecordingSec),
  // sur demande explicite pour ne pas faire défiler ce chiffre pendant l'enregistrement.
  const liveRemainingRecordingSec = (isRecording && remainingAtRecordingStartRef.current !== null)
    ? Math.max(0, remainingAtRecordingStartRef.current - recordingElapsedSec)
    : remainingRecordingSec;

  // Valeur affichée dans le bouton REC : figée à l'estimation prise au démarrage, ne
  // décompte pas seconde par seconde pendant l'enregistrement.
  const displayRemainingRecordingSec = isRecording
    ? remainingAtRecordingStartRef.current
    : remainingRecordingSec;

  // Démarrage effectif — appelé uniquement après validation explicite de l'opérateur
  // dans la boîte "Enregistrement d'une session" (cf. handleStartRecording ci-dessous),
  // qui fournit le nom de la session et, en multi-angle, la liste des angles.
  const startRecordingConfirmed = useCallback(async (payload: RecordingSessionPayload, origin: 'manual' | 'live' = 'manual') => {
    if (isTogglingRecording || isRecording) return;
    const adminId = authentication.admin?.id;
    if (!adminId) { showAlert('Admin non identifié — impossible de démarrer l\'enregistrement.', 'important'); return; }
    setIsTogglingRecording(true); const startedWebRTC = !isStreaming;
    const uuid = channelUuid; const channelName = broadcastingChannel?.name ?? 'Session';
    let result: { sessionId: number; filename: string } | null = null;
    try {
      // Provisionne la chaîne (paths MediaMtx video_/live_/webrtc_UUID + container +
      // HAProxy si elle était inactive) AVANT toute négociation WebRTC — pour que
      // webrtc_UUID existe déjà quand la publication WHIP ci-dessous tente de s'y
      // connecter. No-op quasi instantané si la chaîne est déjà active/restricted.
      await apiEnsureChannelProvisionedForRecording(requireChannelUuid());

      if (startedWebRTC) await startWebRTCStreaming();

      // ⚠️ ON VÉRIFIE MÊME QUAND LA RÉGIE SE CROIT DÉJÀ EN DIFFUSION.
      //
      // Cette vérification vivait dans le `if (startedWebRTC)` ci-dessus : dès que la
      // régie pensait diffuser, l'enregistrement partait sans le moindre contrôle. Or
      // se croire en diffusion ne prouve rien. Deux prises perdues le 2026-09-03 :
      // l'une avec une publication WHIP morte depuis une heure à l'insu de la régie,
      // l'autre publiant sur `webrtc_<référence publique>` faute de traduction par le
      // proxy. Dans les deux cas le programme est sorti en fichier de 0 OCTET — et les
      // angles, eux, avaient leurs fichiers, ce qui rendait la panne illisible.
      //
      // Lecture HLS : c'est une adresse. `webrtc_<uuid>` rend 410 depuis la fermeture
      // de la voie live — l'attente échouerait toujours.
      const webrtcHlsUrl = `${settings.serverUrl}/webrtc_${requireChannelAddress()}/index.m3u8`;
      addSdpLogRegie({ direction: 'info', type: 'event', label: `⏳ En attente flux WebRTC avant enregistrement…` });
      // Un flux qu'on vient de démarrer a besoin de sa minute ; un flux censé être
      // DÉJÀ en l'air répond du premier coup ou ne répondra pas. Attendre 30 s dans
      // ce second cas ne ferait que retarder un refus certain.
      const available = await waitForHls(webrtcHlsUrl, startedWebRTC ? PROBE_TIMEOUT_MS : 6_000);
      if (!available) {
        addSdpLogRegie({ direction: 'received', type: 'error', label: '⚠️ Flux WebRTC non détecté — enregistrement annulé' });
        showAlert('Flux du programme non détecté — la régie ne diffuse pas. Enregistrement annulé.', 'important');
        // ⚠️ On ne coupe QUE ce qu'on vient d'allumer. Couper ici une diffusion que la
        // régie tenait déjà, sur la foi d'une sonde en échec, mettrait la chaîne à
        // l'antenne noire pour un enregistrement raté.
        if (startedWebRTC) stopStreamingImpl();
        return;
      }

      // ── Angles : path éphémère + publication WHIP, avant le start ──────────────
      // L'ordre est contraint : MediaMTX n'écrit aucun fichier sur un path sans publisher,
      // et sans fichier créé le manager ne peut pas mémoriser quoi renommer au stop. Un
      // angle qui échoue est ABANDONNÉ, jamais bloquant : le programme prime toujours, et
      // une caméra capricieuse ne doit pas faire perdre la prise entière.
      // EN PARALLÈLE, pas l'un après l'autre : chaque angle coûte un rassemblement ICE et
      // l'attente du premier flux. Enchaînés, trois angles retardaient le démarrage de plus
      // de quinze secondes — autant de programme non enregistré.
      const angleResults = await Promise.all(payload.angles.map(async angle => {
        // Source déjà dans MediaMTX : rien à emprunter, rien à créer, rien à publier.
        // Le manager pose simplement `record` sur le path d'origine et ne le détruira pas.
        if (angle.existingPath) {
          addSdpLogRegie({ direction: 'info', type: 'event', label: `   ↳ angle « ${angle.sourceName} » enregistré à la source (${angle.existingPath}) — aucun flux montant` });
          return { angle, publication: null, borrow: null };
        }

        let borrow: BorrowHandle | null = null;
        let pathCreated = false;
        try {
          borrow = await registry.borrow(angle.sourceId);

          // Une caméra distante peut avoir un flux emprunté et pourtant ne rien livrer :
          // sa connexion montante se reconstruit. On le demande AVANT de créer le path,
          // sinon l'échec coûte 8 s et laisse un path à détruire — et son message accuse
          // la publication au lieu de l'appareil.
          if (angle.sourceId.startsWith('remote-')) {
            const souci = await probeInboundVideo(angle.sourceId.slice('remote-'.length));
            if (souci) throw new Error(`flux entrant muet — ${souci}`);
          }

          await createRecordingAnglePath(requireChannelUuid(), angle.angleId);
          pathCreated = true;
          // ⚠️ L'URL DE PUBLICATION SE COMPOSE ICI, SUR L'ADRESSE PUBLIQUE.
          //
          // Le `whipUrl` que renvoie l'API porte l'UUID INTERNE — le gestionnaire de
          // MediaMTX ne connaît que lui. Publier dessus marchait par accident : le proxy
          // ne traduit que ce qu'il trouve dans sa carte, laisse passer le reste tel quel,
          // et MediaMTX crée alors le path à la volée. C'est la même mécanique qui a mis
          // un programme entier sur un path fantôme le 2026-09-03.
          //
          // Le programme se publie déjà sur `requireChannelAddress()` (cf. startWhipImpl) ;
          // les angles suivent la même règle, sans quoi le refus posé côté proxy sur les
          // identifiants inconnus les rejetterait.
          const whipUrl = `${settings.serverUrl}/${requireChannelAddress()}/webrtc/src/${encodeURIComponent(angle.angleId)}/whip`;
          // Une caméra LOCALE n'a pas d'audio dans son flux : `DeviceAcquirer`
          // ouvre getUserMedia en `audio: false`, le micro vivant à part dans le
          // mixer. On va donc chercher celui que l'opérateur a associé à cette
          // source — entrée `audio-<id>` du registre audio — sans quoi l'angle
          // d'une webcam sort muet alors que le programme, lui, a le son.
          const pisteAudioAssociee = audioSourceRegistry
            .getStream(`audio-${angle.sourceId}`)?.getAudioTracks()[0] ?? null;
          // Le dernier argument n'est que diagnostique : sur une caméra distante il
          // permet au message d'échec de dire ce que le flux ENTRANT livrait pendant
          // que l'angle n'émettait rien. `null` pour une source locale, qui n'a pas de
          // connexion entrante à interroger.
          const publication = await publishAngle(
            angle.angleId, whipUrl, borrow.stream, pisteAudioAssociee,
            angle.sourceId.startsWith('remote-') ? angle.sourceId.slice('remote-'.length) : null,
          );
          return { angle, publication, borrow };
        } catch (err: any) {
          if (borrow) { try { borrow.release(); } catch { /* déjà relâché */ } }
          // Le path n'est détruit ici que si la publication a échoué : passé le start,
          // c'est /recording/stop qui s'en charge, seul à connaître la composition de la session.
          if (pathCreated) await deleteRecordingAnglePath(requireChannelUuid(), angle.angleId);
          // En console AUSSI, donc dans les logs distants : le panneau SDP disparaît
          // avec l'onglet, et c'est précisément ce qu'il faut relire quand la prise
          // rend moins de fichiers que de sources. Le 25/08/2026, il a fallu
          // descendre dans les journaux de MediaMTX pour apprendre lequel manquait.
          console.warn(`[REC] ⚠️ Angle « ${angle.sourceName} » (${angle.angleId}, source ${angle.sourceId}) abandonné : ${err.message}`);
          addSdpLogRegie({ direction: 'received', type: 'error', label: `⚠️ Angle « ${angle.sourceName} » abandonné : ${err.message}` });
          return null;
        }
      }));

      // L'ordre des angles suit celui du deck : Promise.all le préserve, contrairement à
      // une résolution au fil de l'eau.
      const readyAngles: typeof payload.angles = [];
      for (const result of angleResults) {
        if (!result) continue;
        // Un angle enregistré à la source n'a ni publication ni emprunt à refermer.
        if (result.publication) {
          anglePublicationsRef.current.push(result.publication);
          addSdpLogRegie({ direction: 'info', type: 'event', label: `   ↳ angle « ${result.angle.sourceName} » publié → ${result.angle.filename}.mp4` });
        }
        if (result.borrow) angleBorrowsRef.current.push(result.borrow);
        readyAngles.push(result.angle);
      }

      // apiStartRecording APRÈS confirmation du flux : le PATCH record:true de
      // mediamtx-manager part alors que MediaMTX reçoit déjà des données WebRTC, au lieu
      // de démarrer en même temps que la négociation — sinon la fenêtre de résolution de
      // 5s côté recording.py est systématiquement manquée à froid (fichier jamais
      // renommé, et les premières secondes de la session ne sont pas capturées).
      result = await apiStartRecording({
        channelUuid : requireChannelUuid(),
        channelName,
        filename    : payload.filename,
        adminId,
        format      : 'mp4',
        sessionName : payload.sessionName,
        angles      : readyAngles,
      });

      // Le quota restant est estimé pour UN flux : la prise en écrit un par angle retenu
      // en plus du programme, tous en même temps. C'est cette valeur ramenée qui pilote
      // l'affichage du bouton REC et l'arrêt automatique au quota — sinon un multi-angle
      // à trois caméras promettrait quatre fois le temps réellement disponible.
      remainingAtRecordingStartRef.current = remainingSecondsForStreams(remainingRecordingSec, 1 + readyAngles.length);
      setIsRecording(true); setRecordingStartedAt(Date.now()); setRecordingSessionId(result.sessionId);
      if (origin === 'live') liveRecordingSessionIdRef.current = result.sessionId;
      addSdpLogRegie({ direction: 'info', type: 'event', label: `⏺ Enregistrement ${origin === 'live' ? 'automatique du live ' : ''}démarré — ${result.filename}.mp4 (#${result.sessionId})` });
      if (readyAngles.length) {
        addSdpLogRegie({ direction: 'info', type: 'event', label: `⏺ ${readyAngles.length} angle(s) enregistré(s) en parallèle du programme` });
      }
    } catch (err: any) {
      if (startedWebRTC) stopStreamingImpl();
      if (result && uuid) { await apiStopRecording({ channelUuid: uuid, sessionId: result.sessionId }).catch(() => {}); }
      // Les angles déjà publiés doivent être refermés : sans ça, leurs publishers
      // resteraient accrochés à MediaMTX et bloqueraient la prise suivante sur les mêmes
      // paths. Si le start n'a jamais abouti, leurs paths ne seront détruits par aucun
      // stop — on les nettoie à la main.
      const orphanAngles = anglePublicationsRef.current.map(p => p.angleId);
      await releaseAnglePublications();
      if (!result && uuid) {
        for (const angleId of orphanAngles) await deleteRecordingAnglePath(uuid, angleId);
      }
      // Quota de durée d'enregistrement épuisé (code -7 ChannelRecordingSessionStart,
      // relayé tel quel par apiStartRecording) : même geste que le quota de chaînes
      // (BroadcastingChannelSheet) — un bouton d'achat sur l'alerte plutôt qu'un
      // renvoi silencieux à l'exploitant.
      if (err.message.includes('durée d\'enregistrement')) {
        showAlert(`Erreur enregistrement : ${err.message}`, 'important', {
          extraButtonText: BUY_RECORDING_DURATION_LABEL,
          onExtra: () => openBuyRecordingDuration(showAlert),
        });
      } else {
        showAlert(`Erreur enregistrement : ${err.message}`, 'important');
      }
      addSdpLogRegie({ direction: 'received', type: 'error', label: `❌ ${err.message}` });
    }
    finally { setIsTogglingRecording(false); }
  }, [isTogglingRecording, isRecording, isStreaming, broadcastingChannel, settings.serverUrl, channelUuid, startWebRTCStreaming, stopStreamingImpl, addSdpLogRegie, showAlert, authentication.admin, remainingRecordingSec, registry, releaseAnglePublications]);

  // Clic sur Rec : contrôles préalables, puis ouverture de la boîte "Enregistrement
  // d'une session". L'enregistrement ne démarre qu'après « Lancer l'enregistrement ».
  const handleStartRecording = useCallback(() => {
    if (isTogglingRecording || isRecording) return;
    if (!currentStream && !selectedVideoSource) { showAlert('Sélectionnez une source vidéo avant de démarrer l\'enregistrement.', 'important'); return; }
    if (!authentication.admin?.id) { showAlert('Admin non identifié — impossible de démarrer l\'enregistrement.', 'important'); return; }
    if (remainingRecordingSec !== null && remainingRecordingSec <= 0) { showAlert('Quota disque atteint — impossible de démarrer un enregistrement.', 'important'); return; }
    setShowRecordingModal(true);
  }, [isTogglingRecording, isRecording, currentStream, selectedVideoSource, authentication.admin, remainingRecordingSec, showAlert]);

  // Validation de la boîte : on la ferme avant de lancer, pour que le bouton Rec passe
  // aussitôt en « Un instant… » plutôt que de laisser la fenêtre à l'écran pendant la
  // négociation WebRTC (qui peut prendre plusieurs secondes).
  const handleRecordingModalStart = useCallback((payload: RecordingSessionPayload) => {
    setShowRecordingModal(false);
    startRecordingConfirmed(payload);
  }, [startRecordingConfirmed]);

  // Nom d'enregistrement par défaut, figé à l'ouverture de la boîte : buildRecordingFilename
  // est horodaté à la seconde, le recalculer à chaque rendu ferait glisser le placeholder
  // (et les noms d'angles qui en dérivent) pendant que l'opérateur tape.
  const defaultRecordingName = useMemo(
    () => buildRecordingFilename(broadcastingChannel?.name ?? 'Session'),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [showRecordingModal, broadcastingChannel?.name],
  );

  // Sources du deck proposées en multi-angle : dédoublonnées (un même flux peut occuper
  // plusieurs emplacements) et nommées avec l'alias de l'opérateur quand il y en a un.
  const recordableDeckSources = useMemo<RecordingSessionSource[]>(() => {
    // Sources qui viennent DÉJÀ de notre MediaMTX : la playlist de la chaîne et son entrée
    // OBS/RTMP sont les deux flux HLS que la régie tire de `video_{uuid}` et `live_{uuid}`
    // (cf. DeckV2, lignes hls-video / hls-live). Les enregistrer à la source évite de les
    // renvoyer au serveur d'où ils sortent — et évite au passage la capture d'un <video>,
    // qui est justement ce qui a échoué sur la Playlist lors du premier essai.
    // ⚠️ CLÉ et VALEUR ne portent PAS le même identifiant. La clé est un
    // identifiant de source du DECK, bâti sur l'adresse publique ; la valeur
    // est un vrai NOM DE PATH MediaMTX, envoyé au gestionnaire d'enregistrement.
    const nativePaths: Record<string, string> = (channelUuid && channelPublicRef) ? {
      [`hls-video-${channelPublicRef}`]: `video_${channelUuid}`,
      [`hls-live-${channelPublicRef}`]:  `live_${channelUuid}`,
    } : {};

    // Même raisonnement pour un appareil distant en SFU : il publie LUI-MÊME en WHIP
    // sur `src_{uuid}_{device}`, le path existe donc déjà côté MediaMTX et l'angle n'a
    // rien à republier — le manager pose `record: true` dessus. C'est ce qui règle
    // l'angle d'une caméra distante qui ne s'enregistrait pas : plus de piste distante
    // rattachée à un sender, donc plus d'encodeur qui refuse de naître.
    //
    // Garde indispensable : ne renvoyer le path QUE si le SFU est le transport en cours
    // et que le device n'est pas retombé en P2P. Un appareil replié garde son bloc `sfu`
    // en mémoire alors que plus personne ne publie sur ce path — on enregistrerait un
    // path vide, et le renommage échouerait à l'arrêt.
    const devicePath = (slotId: string): string | undefined => {
      if (settings.deviceTransport !== 'sfu')  return undefined;
      if (!slotId.startsWith('remote-'))       return undefined;
      const deviceId = slotId.slice('remote-'.length);
      if (isSfuUnavailable(deviceId))          return undefined;
      return getDeviceSfuInfo(deviceId)?.path;
    };

    // Et une caméra RTMP additionnelle : le cadreur publie sur `rtmp_{uuid}{5}`, la
    // régie ne fait que relire ce path en HLS. Il est là avant elle et lui survit —
    // même statut que `live_{uuid}`, donc rien à republier non plus. Sans ça, l'angle
    // repartait par le chemin coûteux : capture d'un <video> HLS puis WHIP, celui-là
    // même qui avait fait perdre la Playlist au premier essai.
    //
    // Le path se lit dans le premier segment de l'URL de la source, et la forme exacte
    // de la clé est vérifiée ici — préfixe `rtmp_{uuid}` PLUS cinq caractères
    // alphanumériques, comme `owns_key` côté microservice et `_is_rtmp_input_path`
    // côté mediamtx-manager. Le préfixe seul ne suffirait pas, et un path refusé par le
    // manager ne fait pas échouer la prise : l'angle perd juste son fichier, en silence.
    const rtmpInputPath = (slot: VideoSource): string | undefined => {
      if (!channelUuid || slot.type !== 'url' || !slot.externalUrl) return undefined;
      let segment = '';
      try {
        segment = new URL(slot.externalUrl, window.location.href).pathname.split('/').filter(Boolean)[0] ?? '';
      } catch { return undefined; }
      const prefix = `rtmp_${channelUuid}`;
      if (!segment.startsWith(prefix)) return undefined;
      const suffix = segment.slice(prefix.length);
      return suffix.length === 5 && /^[a-z0-9]+$/i.test(suffix) ? segment : undefined;
    };

    const seen = new Set<string>();
    const list: RecordingSessionSource[] = [];
    for (const slot of deckSlots) {
      if (!slot || seen.has(slot.id)) continue;
      // Audio seul : pas d'image, donc pas d'angle. Son son part avec le programme.
      if (slot.type === 'audio') continue;
      seen.add(slot.id);
      list.push({
        id:           slot.id,
        label:        resolveSourceLabel(slot, sourceAliases),
        mediamtxPath: nativePaths[slot.id] ?? devicePath(slot.id) ?? rtmpInputPath(slot),
      });
    }
    return list;
    // `showRecordingModal`/`showLiveExportModal` sont des dépendances volontaires : le
    // registre SFU (api_whep) est un module, pas un état React — il se remplit à la
    // connexion du device sans rien faire retomber ici. Relire la liste à l'ouverture
    // de l'une ou l'autre boîte garantit qu'on part du transport réellement en place.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deckSlots, sourceAliases, channelUuid, settings.deviceTransport, showRecordingModal, showLiveExportModal]);

  // ── Multiview live : publier les angles locaux, exposer le mapping ─────────────
  //
  // Contrairement aux angles d'enregistrement (ci-dessus), calés sur le cycle de vie
  // d'une session, ceux-ci vivent tant que « multiview actif ET source = régie » est
  // vrai — un enregistrement simultané réutilise le même path déjà publié (même
  // optimisation « à la source »). Réutilise recordableDeckSources pour savoir quelles
  // sources sont DÉJÀ un path MediaMTX (rien à republier) : même détection que
  // l'enregistrement, pas de raison qu'elle diverge.
  //
  // ⚠️ Pas de reconnexion automatique, comme publishAngle() n'en fait jamais : une
  // publication qui tombe reste tombée jusqu'au prochain changement de slotConfigs.
  // Acceptable pour un enregistrement (fichier tronqué) ; pour un spectateur, c'est un
  // angle figé indéfiniment. Limite connue, non traitée dans ce premier jet.
  const multiviewPublicationsRef = useRef<Map<string, AnglePublication>>(new Map());
  const multiviewBorrowsRef      = useRef<Map<string, BorrowHandle>>(new Map());
  // Audio que porte chaque angle publié par la régie ('program' ou 'own') : quand il ne correspond plus
  // à ce que demande le Switch audio, l'angle est republié (on ne peut pas lui ajouter une piste audio
  // sans renégocier, et un angle publié sans audio n'a pas de m-line audio).
  const multiviewAudioModesRef   = useRef<Map<string, AngleAudioMode>>(new Map());
  const multiviewSavedRef        = useRef<string>('');
  const multiviewSaveTimerRef    = useRef<ReturnType<typeof setTimeout> | null>(null);

  const multiviewAngleId = useCallback((sourceId: string): string =>
    ('mv' + sourceId.replace(/[^A-Za-z0-9-]/g, '')).slice(0, 40), []);

  // Angles actuellement dans la grille multiview, indépendamment de toute sélection —
  // c'est la liste que LiveExportModal propose de cocher/décocher. `mediamtxPath` sert
  // au badge « déjà en ligne », même calcul que recordableDeckSources.
  const multiviewGridSources = useMemo<RecordingSessionSource[]>(() => {
    const byId = new Map(recordableDeckSources.map(s => [s.id, s]));
    const seen = new Set<string>();
    const list: RecordingSessionSource[] = [];
    for (const cfg of slotConfigs) {
      if (!cfg.sourceId || seen.has(cfg.sourceId)) continue;
      seen.add(cfg.sourceId);
      const src = videoSources.find(s => s.id === cfg.sourceId);
      list.push({
        id:           cfg.sourceId,
        label:        cfg.customLabel?.trim() || (src ? resolveSourceLabel(src, sourceAliases) : cfg.sourceId),
        mediamtxPath: byId.get(cfg.sourceId)?.mediamtxPath,
      });
    }
    return list;
  }, [slotConfigs, recordableDeckSources, videoSources, sourceAliases]);

  // Angles à exposer publiquement : la grille, restreinte à ce que la régie a confirmé
  // dans LiveExportModal (voir showLiveExportModal). `null` tant qu'aucune confirmation
  // n'a eu lieu pour cette diffusion — rien n'est exposé par défaut.
  const multiviewAngleSlots = useMemo<{ n: number; sourceId: string; mediamtxPath?: string; label: string }[]>(() => {
    if (!multiviewActive || streamSource !== 'webrtc' || !exportedMultiviewSourceIds) return [];
    let n = 0;
    const list: { n: number; sourceId: string; mediamtxPath?: string; label: string }[] = [];
    for (const source of multiviewGridSources) {
      if (!exportedMultiviewSourceIds.has(source.id)) continue;
      n += 1;
      list.push({ n, sourceId: source.id, mediamtxPath: source.mediamtxPath, label: source.label });
    }
    return list;
  }, [multiviewActive, streamSource, multiviewGridSources, exportedMultiviewSourceIds]);

  // REC automatique du live (cf. autoRecordPendingRef) : sans fenêtre, nom par défaut.
  // En multiview, toutes les sources du deck sont enregistrées avec le programme, pour que
  // le direct puisse être publié en multiview (2026-10-31). Un REC manuel déjà en cours
  // garde la main. Quota épuisé : le live reste à l'antenne, on prévient seulement.
  const autoRecordWaitingRef = useRef(false);
  useEffect(() => {
    if (!autoRecordPendingRef.current || autoRecordWaitingRef.current) return;
    if (!isStreaming || !isStreamButtonActive || isStartingStream || isTogglingRecording) return;
    if (isRecording) { autoRecordPendingRef.current = false; return; }
    if (remainingRecordingSec !== null && remainingRecordingSec <= 0) {
      autoRecordPendingRef.current = false;
      showAlert('Quota disque atteint : ce live ne sera pas enregistré.', 'important');
      return;
    }
    const name = buildRecordingFilename(broadcastingChannel?.name ?? 'Session');
    if (!multiviewActive) {
      autoRecordPendingRef.current = false;
      startRecordingConfirmed({ sessionName: name, filename: name, angles: [] }, 'live');
      return;
    }
    // Une caméra locale déjà publiée pour le multiview s'enregistre sur ce path-là : la
    // republier doublerait son encodage et son débit montant, en plein direct. Ces
    // publications partent avec le live, on leur laisse donc quelques secondes.
    autoRecordWaitingRef.current = true;
    setIsTogglingRecording(true);
    (async () => {
      const deadline = Date.now() + AUTO_RECORD_ANGLES_WAIT_MS;
      while (Date.now() < deadline && autoRecordPendingRef.current
             && multiviewAngleSlots.some(s => !s.mediamtxPath && !multiviewPublicationsRef.current.has(s.sourceId))) {
        await new Promise(resolve => setTimeout(resolve, 250));
      }
      autoRecordWaitingRef.current = false;
      setIsTogglingRecording(false);
      // Le live s'est arrêté pendant l'attente (handleStopStreaming a tout désarmé).
      if (!autoRecordPendingRef.current) return;
      autoRecordPendingRef.current = false;
      const sources = recordableDeckSources.map(source => ({
        ...source,
        mediamtxPath: source.mediamtxPath ?? (channelUuid && multiviewPublicationsRef.current.has(source.id)
          ? `src_${channelUuid}_${multiviewAngleId(source.id)}`
          : undefined),
      }));
      startRecordingConfirmed({
        sessionName: name,
        filename   : sources.length ? buildFinalMixFilename(name) : name,
        angles     : buildRecordingAngles(sources, name),
      }, 'live');
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStreaming, isStreamButtonActive, isStartingStream, isTogglingRecording, isRecording, remainingRecordingSec, broadcastingChannel?.name, startRecordingConfirmed, showAlert]);

  // Les angles de téléphones pour lesquels le serveur doit fabriquer un jumeau « audio du programme ».
  const phoneAngleIds = useMemo(() => nativeAngleIds(multiviewAngleSlots, channelUuid ?? ""), [multiviewAngleSlots, channelUuid]);
  useAngleAudioOrder(angleAudio, setup.controlState.isHolder, signalingWsRef, phoneAngleIds);
  // Hors du mode « programme » le serveur n'annonce plus rien ; on oublie aussi ce qu'il annonçait.
  useEffect(() => { if (angleAudio !== 'program') setAngleAudioReady(prev => (prev.length ? [] : prev)); }, [angleAudio]);

  useEffect(() => {
    let cancelled = false;
    const uuid = channelUuid;

    const reconcile = async () => {
      const publications = multiviewPublicationsRef.current;
      const borrows      = multiviewBorrowsRef.current;

      // Sources qui n'ont plus leur place ici : slot vidé, source repassée native, ou
      // multiview/source régie désactivés (multiviewAngleSlots vaut alors []).
      const wanted = new Set(multiviewAngleSlots.filter(s => !s.mediamtxPath).map(s => s.sourceId));

      // L'audio que doivent porter les angles : celui du programme en Switch audio « Fixe » (et si sa piste
      // est vivante), sinon celui de leur source. Un angle déjà publié avec l'autre audio est refermé
      // ci-dessous et republié plus bas, comme une source qui reviendrait.
      const audioVoulu = pickAngleAudio(angleAudio, mixer.getOutputStream()?.getAudioTracks()[0] ?? null);

      for (const sourceId of Array.from(publications.keys())) {
        if (wanted.has(sourceId)
            && !angleNeedsRepublish(multiviewAudioModesRef.current.get(sourceId), audioVoulu.effectiveMode)) continue;
        const publication = publications.get(sourceId)!;
        publications.delete(sourceId);
        multiviewAudioModesRef.current.delete(sourceId);
        try { await closeAnglePublication(publication); }
        catch (e) { console.warn('[multiview] Fermeture angle échouée', sourceId, e); }
        if (uuid) await deleteRecordingAnglePath(uuid, multiviewAngleId(sourceId));
        const borrow = borrows.get(sourceId);
        if (borrow) { borrows.delete(sourceId); try { borrow.release(); } catch { /* déjà relâché */ } }
      }

      if (!uuid) return;

      // Nouvelles sources locales à publier — en parallèle, comme pour l'enregistrement :
      // chaque angle coûte un rassemblement ICE, les enchaîner retarderait l'exposition
      // de tous les suivants.
      const toPublish = multiviewAngleSlots.filter(
        s => !s.mediamtxPath && !publications.has(s.sourceId),
      );
      await Promise.all(toPublish.map(async slot => {
        const angleId = multiviewAngleId(slot.sourceId);
        let borrow: BorrowHandle | null = null;
        let pathCreated = false;
        try {
          borrow = await registry.borrow(slot.sourceId);
          if (slot.sourceId.startsWith('remote-')) {
            const souci = await probeInboundVideo(slot.sourceId.slice('remote-'.length));
            if (souci) throw new Error(`flux entrant muet — ${souci}`);
          }
          await createRecordingAnglePath(uuid, angleId);
          pathCreated = true;
          const whipUrl = `${settings.serverUrl}/${requireChannelAddress()}/webrtc/src/${encodeURIComponent(angleId)}/whip`;
          const pisteAudioAssociee = audioSourceRegistry
            .getStream(`audio-${slot.sourceId}`)?.getAudioTracks()[0] ?? null;
          const publication = await publishAngle(
            angleId, whipUrl, borrow.stream, pisteAudioAssociee,
            slot.sourceId.startsWith('remote-') ? slot.sourceId.slice('remote-'.length) : null,
            audioVoulu.forced,
          );
          if (cancelled) { await closeAnglePublication(publication); borrow.release(); return; }
          multiviewPublicationsRef.current.set(slot.sourceId, publication);
          multiviewAudioModesRef.current.set(slot.sourceId, audioVoulu.effectiveMode);
          multiviewBorrowsRef.current.set(slot.sourceId, borrow);
        } catch (err: any) {
          if (borrow) { try { borrow.release(); } catch { /* déjà relâché */ } }
          if (pathCreated) await deleteRecordingAnglePath(uuid, angleId);
          console.warn(`[multiview] Angle ${slot.sourceId} abandonné : ${err.message}`);
        }
      }));

      if (cancelled) return;

      // Ce qu'on expose réellement : les sources natives telles quelles, les locales
      // republiées avec le path qu'on vient d'obtenir — jamais une entrée sans path
      // résolu, sinon HAProxy recevrait un mapping pointant vers rien.
      const resolved = multiviewAngleSlots
        .map(slot => ({
          n:     slot.n,
          path:  slot.mediamtxPath ? resolveAnglePath(slot.mediamtxPath, uuid, angleAudio, new Set(angleAudioReady)) : (
            multiviewPublicationsRef.current.has(slot.sourceId)
              ? `src_${uuid}_${multiviewAngleId(slot.sourceId)}`
              : undefined
          ),
          label: slot.label,
        }))
        .filter((a): a is { n: number; path: string; label: string } => !!a.path);

      const payload = JSON.stringify(resolved);
      if (payload === multiviewSavedRef.current) return;
      multiviewSavedRef.current = payload;

      if (multiviewSaveTimerRef.current) clearTimeout(multiviewSaveTimerRef.current);
      const channelId = broadcastingChannel?.id;
      multiviewSaveTimerRef.current = setTimeout(() => {
        if (channelId) updateBroadcastingChannelMultiviewAngles(channelId, resolved);
      }, 800);
    };

    reconcile();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [multiviewAngleSlots, channelUuid, angleAudio, angleAudioReady]);

  // Referme tout à la désactivation de la chaîne / au démontage — sans quoi les
  // publishers WHIP des angles republiés resteraient accrochés à MediaMTX.
  useEffect(() => {
    return () => {
      if (multiviewSaveTimerRef.current) clearTimeout(multiviewSaveTimerRef.current);
      const publications = multiviewPublicationsRef.current;
      const borrows      = multiviewBorrowsRef.current;
      multiviewPublicationsRef.current = new Map();
      multiviewBorrowsRef.current      = new Map();
      multiviewAudioModesRef.current   = new Map();
      publications.forEach(p => { closeAnglePublication(p).catch(() => {}); });
      // Même garde que releaseChannelScopedSources / le nettoyage des mini-vues : un angle
      // multiview peut très bien être la webcam locale ou un partage d'écran, qu'il ne faut
      // pas couper au changement de chaîne.
      borrows.forEach((b, sourceId) => {
        const type = registry.get(sourceId)?.type;
        if (type && PERSISTENT_SOURCE_TYPES.includes(type)) return;
        try { b.release(); } catch { /* déjà relâché */ }
      });
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Cette source livre-t-elle quelque chose, là, maintenant ? Renvoie la raison quand
   * non, `null` quand tout va bien. Appelée en boucle par la fenêtre d'enregistrement
   * tant qu'elle est ouverte : une caméra distante peut se rétablir (ou tomber) entre
   * l'ouverture de la fenêtre et le clic sur « Lancer ».
   *
   * Seules les caméras distantes sont interrogées. Les autres acquéreurs savent acquérir
   * à la demande — une source HLS ou une webcam pas encore acquise n'a rien d'anormal, la
   * signaler ferait du bruit pour rien.
   */
  const sourceHealth = useCallback(async (sourceId: string): Promise<string | null> => {
    if (!sourceId.startsWith('remote-')) return null;

    const stream = registry.getStream(sourceId);
    if (!stream) return 'aucun flux reçu';

    const piste = stream.getVideoTracks()[0];
    if (!piste)                      return 'flux sans vidéo';
    if (piste.readyState !== 'live') return 'flux interrompu';

    return inspectInboundVideo(sourceId.slice('remote-'.length));
  }, [registry]);

  const handleStopRecording = useCallback(async (reason: 'manual' | 'quota' | 'server_limit' = 'manual'): Promise<RecordingSession | null> => {
    if (isTogglingRecording || !isRecording) return null;
    setIsTogglingRecording(true);
    const uuid = channelUuid;
    const stoppedSessionId = recordingSessionId;
    try {
      // 'server_limit' : admin-api a DÉJÀ arrêté l'enregistrement de son propre chef
      // (quota de durée cumulée épuisé, cf. checkRecordingDurationCheckpoint) avant
      // même que ce message n'arrive — un second /recording/stop porterait sur une
      // session déjà fermée. On ne fait ici que resynchroniser l'état local.
      if (uuid && reason !== 'server_limit') { await apiStopRecording({ channelUuid: uuid, sessionId: stoppedSessionId ?? 0 }); }
      // Après le stop, pas avant : c'est /recording/stop qui coupe l'enregistrement des
      // paths d'angles puis les détruit. Refermer les publications d'abord tronquerait les
      // fichiers d'angles quelques instants avant celui du programme.
      await releaseAnglePublications();
      setIsRecording(false); setRecordingStartedAt(null); setRecordingSessionId(null);
      remainingAtRecordingStartRef.current = null;
      if (reason === 'quota') {
        addSdpLogRegie({ direction: 'info', type: 'event', label: '⏹ Quota disque atteint — enregistrement arrêté automatiquement' });
        showAlert('Quota disque atteint : l\'enregistrement a été arrêté automatiquement.', 'important');
      } else if (reason === 'server_limit') {
        addSdpLogRegie({ direction: 'info', type: 'event', label: '⏹ Quota de durée d\'enregistrement atteint — arrêté automatiquement par le serveur' });
      } else {
        addSdpLogRegie({ direction: 'info', type: 'event', label: `⏹ Enregistrement arrêté` });
      }
      if (!isStreamButtonActive) stopStreamingImpl();

      // Rafraîchit l'historique (taille/durée réelles du fichier fini, cf. ffprobe
      // côté mediamtx-manager) et crédite localement le quota admin — évite un
      // aller-retour getAdminInfosByEmail (geo-IP inclus) juste pour un compteur d'octets.
      const sessions = await refreshRecordingHistory();
      const finished = sessions?.find(s => s.id === stoppedSessionId);
      if (finished?.sizeBytes && authentication.admin) {
        dispatch(setAdmin({ ...authentication.admin, quotaUsed: authentication.admin.quotaUsed + finished.sizeBytes }));
      }
      return finished ?? null;
    } catch (err: any) { await releaseAnglePublications(); setIsRecording(false); setRecordingStartedAt(null); setRecordingSessionId(null); remainingAtRecordingStartRef.current = null; if (!isStreamButtonActive) stopStreamingImpl(); addSdpLogRegie({ direction: 'received', type: 'error', label: `❌ ${err.message}` }); return null; }
    finally { setIsTogglingRecording(false); }
  }, [isTogglingRecording, isRecording, isStreamButtonActive, broadcastingChannel, channelUuid, recordingSessionId, addSdpLogRegie, stopStreamingImpl, refreshRecordingHistory, authentication.admin, dispatch, showAlert, releaseAnglePublications]);

  // Arrêt automatique dès que le compte à rebours en direct atteint 0 — évite que
  // MediaMTX continue d'écrire au-delà du quota disque alloué à l'admin.
  useEffect(() => {
    if (isRecording && liveRemainingRecordingSec !== null && liveRemainingRecordingSec <= 0) {
      handleStopRecording('quota');
    }
  }, [isRecording, liveRemainingRecordingSec, handleStopRecording]);

  // Resynchronisation quand c'est admin-api qui a arrêté l'enregistrement de son
  // propre chef (quota de durée cumulée épuisé, notifié par duration_notice via
  // useLiveStreamSetup). Par ref car peuplé ici, après la définition de
  // handleStopRecording — même pattern que deckCutCallbackRef.
  useEffect(() => { recordingLimitReachedRef.current = () => { handleStopRecording('server_limit'); }; }, [handleStopRecording]);

  // Fin du REC du live, appelée par handleStopStreaming (cf. finishLiveRecordingRef) : arrête
  // la session si elle tourne encore, sinon la retrouve dans l'historique, et la rend —
  // `null` quand ce live n'avait rien lancé.
  useEffect(() => {
    finishLiveRecordingRef.current = async () => {
      const sessionId = liveRecordingSessionIdRef.current;
      liveRecordingSessionIdRef.current = null;
      if (sessionId === null) return null;
      if (isRecording && recordingSessionId === sessionId) return handleStopRecording();
      const sessions = await refreshRecordingHistory();
      return sessions?.find(s => s.id === sessionId) ?? null;
    };
  }, [isRecording, recordingSessionId, handleStopRecording, refreshRecordingHistory]);

  const handleSlotPowerToggle = useCallback(async (sourceId: string, currentlyOff: boolean) => {
    if (currentlyOff) {
      setPoweredOffSlots(prev => { const next = new Set(prev); next.delete(sourceId); return next; });
      const source = videoSources.find(s => s.id === sourceId); if (!source) return;
      if (source.type === 'device' || source.type === 'screen') { try { const handle = await registry.borrow(sourceId); previewStreamsRef.current.set(sourceId, handle.stream); setPreviewStreams(new Map(previewStreamsRef.current)); } catch (e) { console.warn('[Power]', sourceId, e); } }
      if (source.type === 'url' && source.externalUrl) { registry.enableSource(sourceId); hlsProber.retryProbe(sourceId, source.externalUrl); }
    } else {
      setPoweredOffSlots(prev => new Set(Array.from(prev).concat(sourceId)));
      const source = videoSources.find(s => s.id === sourceId); if (!source) return;
      if (source.type === 'device') { registry.forceRelease(sourceId); previewStreamsRef.current.delete(sourceId); setPreviewStreams(new Map(previewStreamsRef.current)); }
      else if (source.type === 'screen') { registry.forceRelease(sourceId); setScreenShareStreams(prev => { const next = new Map(prev); next.delete(sourceId); return next; }); previewStreamsRef.current.delete(sourceId); setPreviewStreams(new Map(previewStreamsRef.current)); }
      else if (source.type === 'remote') { const deviceId = sourceId.replace('remote-', ''); const conn = remoteConnections.get(deviceId); if (conn) { conn.peerConnection.close(); setRemoteConnections(prev => { const next = new Map(prev); next.delete(deviceId); return next; }); } registry.forceRelease(sourceId); previewStreamsRef.current.delete(sourceId); setPreviewStreams(new Map(previewStreamsRef.current)); }
      else if (source.type === 'url') {
        disconnectAudioForSlot(sourceId);
        registry.disableSource(sourceId);
        const previewHandle = setup.previewBorrowHandlesRef.current.get(sourceId);
        if (previewHandle) { try { previewHandle.release(); } catch {} setup.previewBorrowHandlesRef.current.delete(sourceId); }
        registry.forceRelease(sourceId);
        hlsProber.forceStatus(sourceId, 'failed');
        previewStreamsRef.current.delete(sourceId);
        setPreviewStreams(new Map(previewStreamsRef.current));
      }
      if (deckPreviewSourceId === sourceId) { setDeckPreviewSourceId(null); if (deckPreviewVideoRef.current) { deckPreviewVideoRef.current.pause(); deckPreviewVideoRef.current.srcObject = null; } }
      if (selectedVideoSource === sourceId) { setSelectedVideoSource(''); activeSourceRef.current = null; clearCurrentStream(); if (canvasPreviewRef.current) { canvasPreviewRef.current.pause(); canvasPreviewRef.current.srcObject = null; } stopCompositingLoop(); }
      if (source.type === 'remote') {
        const deviceId = sourceId.replace('remote-', '');
        cleanupIntercommForDevice(deviceId);
      }
    }
  }, [videoSources, registry, remoteConnections, setRemoteConnections, setScreenShareStreams, previewStreamsRef, setPreviewStreams, hlsProber, deckPreviewSourceId, setDeckPreviewSourceId, deckPreviewVideoRef, selectedVideoSource, setSelectedVideoSource, clearCurrentStream, canvasPreviewRef, stopCompositingLoop, disconnectAudioForSlot, cleanupIntercommForDevice]);

  // Même geste que l'opérateur (éteindre puis rallumer le slot Playlist), fait à sa place
  // quand la mire est rechargée. Sans effet si le slot est éteint ou absent.
  restartPlaylistAfterMireRef.current = () => {
    if (!channelPublicRef) return;
    const id = `hls-video-${channelPublicRef}`;
    if (poweredOffSlots.has(id) || !videoSources.some(s => s.id === id)) return;
    console.warn(`[MIRE] mire rechargée côté serveur — relance du lecteur ${id}`);
    void handleSlotPowerToggle(id, false)
      .then(() => new Promise<void>(r => setTimeout(r, 1500)))
      .then(() => handleSlotPowerToggle(id, true));
  };

  const handleToggleRegiePower = useCallback(async () => {
    if (isRegiePowered) {
      setIsRegiePowered(false);
      if (isRecording) { try { if (channelUuid) await apiStopRecording({ channelUuid, sessionId: recordingSessionId ?? 0 }); } catch {} setIsRecording(false); setRecordingStartedAt(null); setRecordingSessionId(null); }
      if (isStreaming) { stopStreamingImpl(); setIsStreamButtonActive(false); }
      const toOff: string[] = [];
      for (const slot of deckSlots) { if (slot && !poweredOffSlots.has(slot.id)) { toOff.push(slot.id); await handleSlotPowerToggle(slot.id, false); } }
      regieAutoOffSlotsRef.current = toOff;
      clearCurrentStream(); setSelectedVideoSource(''); activeSourceRef.current = null; setActiveLayoutId(null);
      closeAllRegieIntercomm(intercommMapRef, { addSdpLog: addSdpLogRegie });
      knownIntercommDevicesRef.current.clear();
    } else {
      setIsRegiePowered(true);
      for (const sourceId of regieAutoOffSlotsRef.current) await handleSlotPowerToggle(sourceId, true);
      regieAutoOffSlotsRef.current = [];
    }
  }, [isRegiePowered, isRecording, isStreaming, deckSlots, poweredOffSlots, broadcastingChannel, channelUuid, recordingSessionId, stopStreamingImpl, clearCurrentStream, handleSlotPowerToggle, setSelectedVideoSource, setActiveLayoutId, addSdpLogRegie]);

  const startStream = useCallback(async () => {
    const source = videoSources.find(s => s.id === selectedVideoSource); if (!source) return;
    await acquireAndSetStream(source, () => {
      if (streamDestination === 'master' && !isSlave) { slaveSettingsRef.current = { masterWsUrl: masterConfig.wsUrl, deviceId: masterConfig.deviceId, password: masterConfig.password, label: masterConfig.label }; setTimeout(() => startSlaveModeImpl(), 500); }
    });
  }, [videoSources, selectedVideoSource, acquireAndSetStream, streamDestination, isSlave, masterConfig]);

  const startStreamWithSource = useCallback(async (sourceId: string) => {
    const source = videoSources.find(s => s.id === sourceId); if (!source) return;
    await acquireAndSetStream(source);
  }, [videoSources, acquireAndSetStream]);

  // Correctif du 15/09/2026 : sur ruby, ce composant ne se remonte pas de façon fiable au
  // changement de chaîne (aucun log de montage n'apparaît jamais pour la chaîne CIBLE —
  // seulement, parfois, un montage/démontage attribué à l'ANCIENNE, sans rapport). `activeSourceRef`
  // et `currentStreamRef` restent donc ceux de la chaîne quittée. Sur Lovelight TV, le garde-fou de
  // `acquireAndSetStream` ("cette source est déjà la bonne, son flux est actif") voyait la webcam
  // encore considérée comme à l'antenne pour ESSAI et ne la réacquérait jamais pour la nouvelle
  // chaîne — la mini-vue restait donc noire sans qu'aucune acquisition n'ait même été tentée. On
  // force ici la réévaluation sur le seul signal fiable qu'une autre chaîne est maintenant affichée.
  const prevAcquireChannelUuidRef = useRef<string | null>(channelUuid);
  useEffect(() => {
    if (prevAcquireChannelUuidRef.current === channelUuid) return;
    prevAcquireChannelUuidRef.current = channelUuid;
    if (!channelUuid) return;
    activeSourceRef.current = null;
    startStream();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channelUuid, startStream]);

  // Relue à l'appel, pas figée dans les dépendances : une retentative réseau programmée
  // 5 s plus tôt doit voir la main telle qu'elle est MAINTENANT.
  const isHolderRef = useRef(setup.controlState.isHolder);
  isHolderRef.current = setup.controlState.isHolder;

  const startWebRTCStreamingImpl = useCallback(async (opts?: { silent?: boolean; retryDeadline?: number; networkRetry?: boolean }) => {
    if (!currentStreamRef.current) return;
    // Garde de dernier recours, pour TOUS les chemins (publication permanente, bouton,
    // retentative réseau, demande d'antenne d'un téléphone) : `webrtc_<uuid>` n'a qu'un
    // publieur, une régie qui n'a pas la main l'éjecterait de celle qui pilote — cf.
    // programPublishGate.ts (24/10/2026 : 60 éjections en 14 minutes).
    if (!canPublishProgram(isHolderRef.current)) {
      addSdpLogRegie({ direction: 'info', type: 'event', label: '⛔ Publication du programme refusée : cette régie ne pilote pas la chaîne' });
      return;
    }
    if (!channelActive) {
      // Chaîne éteinte : le container MediaMTX de la chaîne n'existe pas, donc le
      // POST WHIP échoue en TypeError "Failed to fetch" — un message qui ne dit
      // rien à l'utilisateur. On intercepte le cas AVANT le fetch pour afficher
      // la vraie cause plutôt que l'échec réseau qui en découle.
      addSdpLogRegie({ direction: 'received', type: 'error', label: `❌ La chaîne n'est pas activée` });
      // `silent` : la republication permanente (PERSISTENT_PROGRAM_WHIP) retente ici
      // automatiquement à chaque montage de source, sans action de l'opérateur — la
      // même alerte que sur l'onglet Régie (cf. BroadcastingChannelSheet.handleTabChange,
      // seule source fiable puisque cet onglet reste monté) ferait doublon à l'ouverture.
      if (!opts?.silent) showAlert(`La chaîne n'est pas activée. Activez la chaîne pour pouvoir utiliser la régie.`, 'important');
      stopStreamingImpl();
      return;
    }
    // `channelActive` (ci-dessus) est optimiste — il bascule au clic sur "Activer", bien
    // avant que le container/HAProxy/MediaMTX ne soient prêts (cf. channelStatus ==
    // 'activating', quelques secondes). Tenter le WHIP tout de suite dans cette fenêtre
    // rendait systématiquement "WHIP 410: Channel inactive or unknown" — une vraie
    // erreur en apparence, alors que la chaîne vient tout juste d'être activée. On
    // patiente ici au lieu d'échouer, et on retente tout seul jusqu'à ce que
    // channelStatus confirme la chaîne prête (ou qu'on abandonne après 20 s).
    if (broadcastingChannelStatus === 'activating' || broadcastingChannelStatus === 'activating_restricted') {
      const deadline = opts?.retryDeadline ?? (Date.now() + 20_000);
      if (Date.now() >= deadline) {
        addSdpLogRegie({ direction: 'received', type: 'error', label: `❌ La chaîne est toujours en cours d'activation après 20 s` });
        if (!opts?.silent) showAlert(`La chaîne met plus de temps que prévu à s'activer. Réessayez dans quelques instants.`, 'important');
        stopStreamingImpl();
        return;
      }
      if (!opts?.retryDeadline) addSdpLogRegie({ direction: 'info', type: 'event', label: `⏳ Chaîne en cours d'activation — connexion différée` });
      setTimeout(() => { startWebRTCStreamingRef.current?.({ ...opts, retryDeadline: deadline }); }, 1000);
      return;
    }
    if (!audioMixerDestRef.current) { const micTracks = currentStreamRef.current.getAudioTracks(); initAudioMixer(activeSourceRef.current, micTracks.length > 0 ? new MediaStream(micTracks) : null); }
    // `webrtc_signaling_backend` traduit le préfixe avant ses réécritures :
    // l'adresse arrive bien sur le path `webrtc_<uuid>` que MediaMTX expose.
    const whipUrl = `${settings.serverUrl}/${requireChannelAddress()}/webrtc/whip`;
    try { await apiStartWhipStreaming({ whipUrl, currentStream: currentStreamRef.current, canvasStreamRef, sourceVideoEl: getActiveSourceEl(), canvasEl: compositingCanvasRef.current, audioMixerDestRef, refs: buildSignalingRefs(), callbacks: buildSignalingCallbacks() as any, negotiatedWhipCodec, initCanvasStream, canvasPreviewEl: canvasPreviewRef.current }); }
    catch (err: any) {
      console.error('Erreur WHIP:', err);
      // `setRemoteDescription` reçoit la réponse WHIP après que la RTCPeerConnection a déjà
      // été fermée (stopStreamingImpl appelé entre-temps — arrêt manuel, ou notre propre
      // abandon après 20 s d'activation ci-dessus) : la négociation est simplement dépassée,
      // pas en échec. Sans ce filtre, l'opérateur voyait une erreur technique brute pour un
      // arrêt qu'il venait lui-même de demander.
      const superseded = err.name === 'InvalidStateError' && /signalingState.*'closed'/.test(err.message ?? '');
      // Coupure réseau pure : `fetch` échoue avant même de partir (pas de réponse HTTP, donc
      // pas de code d'erreur MediaMTX). C'est exactement ce que le WS de signalisation traverse
      // déjà en se reconnectant tout seul toutes les 3 s (cf. connectToSignalingServer) — le WHIP
      // doit faire pareil au lieu d'abandonner sur "TypeError: Failed to fetch", qui ne dit pas à
      // l'opérateur que ça va se rétablir. Constaté le 17/09/2026 : ~6 min de coupure réseau
      // réelle sur un poste, affichant ce message brut sans explication ni retentative.
      const networkLost = !superseded && err instanceof TypeError && /Failed to fetch|NetworkError/i.test(err.message ?? '');
      if (networkLost) {
        stopStreamingImpl();
        if (!opts?.networkRetry) {
          addSdpLogRegie({ direction: 'received', type: 'error', label: `📡 Connexion réseau perdue — reconnexion automatique en cours` });
          if (!opts?.silent) showAlert(`La connexion réseau a été perdue. La régie se reconnectera automatiquement dès son retour — inutile de relancer manuellement.`, 'important');
        } else {
          addSdpLogRegie({ direction: 'info', type: 'event', label: `📡 Toujours pas de réseau — nouvelle tentative` });
        }
        setTimeout(() => {
          // Relit l'intention COURANTE, pas celle figée à la création de cette retentative :
          // un arrêt manuel pendant la coupure ne doit pas ressusciter le WHIP à son retour.
          if (!isStreamButtonActiveRef.current) return;
          startWebRTCStreamingRef.current?.({ ...opts, silent: true, networkRetry: true });
        }, 5000);
        return;
      }
      // 410 « Channel inactive or unknown » : HAProxy ne connaît pas (ou plus) cette chaîne parmi
      // les chaînes actives — elle a été arrêtée (shutdown, désactivation) ou vient d'être
      // réactivée et la table n'est pas encore à jour (quelques secondes). Le texte brut du
      // serveur ne dit rien de tout cela à l'opérateur.
      const chainInactive = !superseded && /WHIP 410/.test(err.message ?? '') && /inactive or unknown/i.test(err.message ?? '');
      if (chainInactive) {
        addSdpLogRegie({ direction: 'received', type: 'error', label: `❌ Chaîne inactive ou inconnue du serveur (WHIP 410)` });
        showAlert(`Le serveur indique que la chaîne n'est pas active : elle a été arrêtée, ou vient d'être réactivée et n'est pas encore prête. Vérifiez qu'elle est activée, patientez quelques secondes, puis relancez la diffusion.`, 'important');
        stopStreamingImpl();
        return;
      }
      addSdpLogRegie({ direction: 'received', type: 'error', label: superseded ? `⚠ Négociation WHIP abandonnée (connexion déjà fermée)` : `❌ Erreur WHIP: ${err.message}` });
      if (!superseded) showAlert(`Erreur WHIP : ${err.message}`, 'important');
      stopStreamingImpl();
    }
  }, [settings.serverUrl, channelUuid, channelActive, broadcastingChannelStatus, buildSignalingRefs, buildSignalingCallbacks, negotiatedWhipCodec, initCanvasStream, initAudioMixer, getActiveSourceEl, addSdpLogRegie, showAlert, stopStreamingImpl]);

  useEffect(() => { startWebRTCStreamingRef.current = startWebRTCStreamingImpl; }, [startWebRTCStreamingImpl]);

  const stopWhipStatsMonitoring = useCallback(() => { apiStopWhipStatsMonitoring({ whipStatsIntervalRef }); }, []);

  const buildSlaveRefs = useCallback((): SlaveRefs => ({ slaveWsRef, slavePcRef, slaveIceBufRef, slaveRemoteDescSetRef, slaveShouldReconnectRef, slaveReconnectTimeoutRef, slaveSettingsRef, canvasStreamRef, currentStreamRef, audioMixerDestRef }), []);
  const buildSlaveCallbacks = useCallback((): SlaveCallbacks => ({ addSdpLog: addSdpLogRegie, setIsSlave, setSlaveState, initCanvasStream: (videoEl, fps) => initCanvasStream(videoEl, fps), getSourceVideoEl: () => getActiveSourceEl(), getCanvasPreviewEl: () => canvasPreviewRef.current }), [addSdpLogRegie, initCanvasStream, getActiveSourceEl]);
  const startSlaveModeImpl = useCallback(async () => { await apiStartSlaveMode(buildSlaveRefs(), buildSlaveCallbacks()); }, [buildSlaveRefs, buildSlaveCallbacks]);

  // ── handleSignalingMessageImpl — passe les callbacks intercomm natifs ─────
  // Plus de wrapper ws.onmessage : tout est routé ici directement.
  
  /*
  const handleSignalingMessageImpl = useCallback(async (message: any) => {
    await (await import('../../../backendServices/liveStreamManagment/api_signalingWs')).handleSignalingMessage(
      message,
      buildSignalingRefs(),
      buildSignalingCallbacks(),
      {
        connectToDevice          : (id) => setup.buildSignalingCallbacks().connectToDevice(id),
        handleRemoteOffer        : (id, offer, meta) => {
          return import('../../../backendServices/liveStreamManagment/api_webrtcPeerConnection')
            .then(m => m.handleRemoteOffer(id, offer, meta, buildSignalingRefs(), buildSignalingCallbacks(), remoteDevices, (rid) => setup.buildSignalingCallbacks().connectToDevice(rid)));
        },
        handleRemoteAnswer       : (id, answer) => {
          return import('../../../backendServices/liveStreamManagment/api_webrtcPeerConnection')
            .then(m => m.handleRemoteAnswer(id, answer, buildSignalingRefs(), buildSignalingCallbacks()));
        },
        handleRemoteIceCandidate : (id, c) => {
          return import('../../../backendServices/liveStreamManagment/api_webrtcPeerConnection')
            .then(m => m.handleRemoteIceCandidate(id, c, buildSignalingRefs()));
        },
        cleanupDeadConnection    : (id) => {
          closeReturnStreamToDevice(id, { addSdpLog: addSdpLogRegie });
          import('../../../backendServices/liveStreamManagment/api_webrtcBuffers')
            .then(m => m.cleanupDeadConnection(id, { iceCandidateBufferRef, remoteDescSetRef, remoteConnectionsRef }, { setRemoteConnections }));
        },
      },
      // ── Callbacks intercomm (5e paramètre) ────────────────────────────────
      {
        onDeviceConnected: (deviceId) => {
          initIntercommForDevice(deviceId);
          resendOnAirIfNeeded(deviceId);
        },

        onIntercommAnswer: async (deviceId, streamId, answer) => {
          await handleRegieIntercommAnswer(
            deviceId,
            streamId,
            answer,
            intercommMapRef,
            { addSdpLog: addSdpLogRegie },
          );
        },
        onIntercommIce: async (deviceId, streamId, candidate) => {
          await handleRegieIntercommIce(
            deviceId,
            streamId,
            candidate,
            intercommMapRef,
          );
        },
        onIntercommSpeaking: (deviceId, active) => {
          setIntercommSpeakingDevices(prev => {
            const next = new Set(prev);
            if (active) next.add(deviceId); else next.delete(deviceId);
            return next;
          });
          addSdpLogRegie({
            direction: 'received', type: 'event',
            label: `🎙 ${deviceId} intercomm_speaking: ${active ? 'ON' : 'OFF'}`,
          });
        },
		onDeviceDisconnected: (deviceId) => {

  console.log('[onDeviceDisconnected] APPELÉ pour', deviceId);  // ← ajouter
			
			
  const sourceId = `remote-${deviceId}`;
  cleanupIntercommForDevice(deviceId);
  setup.forgetSlotAssignment(sourceId);

  // Nettoyer le borrow handle preview
  const previewHandle = setup.previewBorrowHandlesRef.current.get(sourceId);
  if (previewHandle) {
    try { previewHandle.release(); } catch {}
    setup.previewBorrowHandlesRef.current.delete(sourceId);
  }
  setup.previewStreamsRef.current.delete(sourceId);
  setup.setPreviewStreams(new Map(setup.previewStreamsRef.current));

  // Si c'était la source active, reset
  if (activeSourceRef.current === sourceId) {
    activeSourceRef.current = null;
    setSelectedVideoSource('');
    clearCurrentStream();
  }

  // Nettoyer selectedAudioDeviceIds
  setSelectedAudioDeviceIds(prev => {
    const next = new Map(prev);
    next.delete(sourceId);
    return next;
  });
},
      },
    );
  }, [
    buildSignalingRefs, buildSignalingCallbacks, setup,
    remoteDevices, addSdpLogRegie,
    iceCandidateBufferRef, remoteDescSetRef, remoteConnectionsRef, setRemoteConnections,
    initIntercommForDevice, cleanupIntercommForDevice,
  ]);
*/


const handleSignalingMessageImpl = useCallback(async (message: any) => {
  await (await import('../../../backendServices/liveStreamManagment/api_signalingWs')).handleSignalingMessage(
    message,
    buildSignalingRefs(),
    buildSignalingCallbacks(),
    {
      connectToDevice: (id) => setupRef.current.buildSignalingCallbacks().connectToDevice(id),
      handleRemoteOffer: (id, offer, meta) => {
        return import('../../../backendServices/liveStreamManagment/api_webrtcPeerConnection')
          .then(m => m.handleRemoteOffer(id, offer, meta, buildSignalingRefs(), buildSignalingCallbacks(), remoteDevices, (rid) => setupRef.current.buildSignalingCallbacks().connectToDevice(rid)));
      },
      handleRemoteAnswer: (id, answer) => {
        return import('../../../backendServices/liveStreamManagment/api_webrtcPeerConnection')
          .then(m => m.handleRemoteAnswer(id, answer, buildSignalingRefs(), buildSignalingCallbacks()));
      },
      handleRemoteIceCandidate: (id, c) => {
        return import('../../../backendServices/liveStreamManagment/api_webrtcPeerConnection')
          .then(m => m.handleRemoteIceCandidate(id, c, buildSignalingRefs()));
      },
      cleanupDeadConnection: (id) => {
        closeReturnStreamToDevice(id, { addSdpLog: addSdpLogRegie });
        import('../../../backendServices/liveStreamManagment/api_webrtcBuffers')
          .then(m => m.cleanupDeadConnection(id, { iceCandidateBufferRef, remoteDescSetRef, remoteConnectionsRef }, { setRemoteConnections }));
      },
    },
    {
      onDeviceConnected: (deviceId) => {
        // L'appareil se réannonce — mais son IMAGE n'est pas encore là. La place
        // reste donc tenue : c'est l'arrivée de la piste vidéo qui la libérera. On
        // se contente de relancer le compte à rebours et de rouvrir la négociation.
        setupRef.current.renewDeviceAwaiting(deviceId);
        initIntercommForDevice(deviceId);
        resendOnAirIfNeeded(deviceId);
      },
      onIntercommAnswer: async (deviceId, streamId, answer) => {
        await handleRegieIntercommAnswer(deviceId, streamId, answer, intercommMapRef, { addSdpLog: addSdpLogRegie });
      },
      onIntercommIce: async (deviceId, streamId, candidate) => {
        await handleRegieIntercommIce(deviceId, streamId, candidate, intercommMapRef);
      },
      // Mini-deck : le device demande une source en final. Le contrôle du rôle
      // est fait dans handleDirectorTake, pas ici.
      onDirectorTake: (deviceId, sourceId) => {
        handleDirectorTake(deviceId, sourceId);
      },
      // Un téléphone créateur demande l'antenne : le microservice n'a relayé
      // qu'à la régie qui pilote, c'est donc à nous de passer en mode régie.
      onLiveRequest: (deviceId, active, label) => {
        void handleLiveRequest(deviceId, active, label);
      },
      onIntercommSpeaking: (deviceId, active) => {
        setIntercommSpeakingDevices(prev => {
          const next = new Set(prev);
          if (active) next.add(deviceId); else next.delete(deviceId);
          return next;
        });
        addSdpLogRegie({ direction: 'received', type: 'event', label: `🎙 ${deviceId} intercomm_speaking: ${active ? 'ON' : 'OFF'}` });
      },
      onDeviceDisconnected: (deviceId, reason) => {
        console.log('[onDeviceDisconnected] APPELÉ pour', deviceId, reason ?? '(sans motif)');
        const sourceId = `remote-${deviceId}`;
        cleanupIntercommForDevice(deviceId);

        // Un départ ANNONCÉ — clic sur Déco, déconnexion demandée par la régie,
        // suppression de l'appareil — ne se garde pas au chaud : personne ne
        // reviendra, et laisser une vignette « EN ATTENTE » dix minutes ferait
        // croire à l'opérateur qu'une caméra va se rallumer.
        //
        // Une socket tombée, elle, revient presque toujours : on tient sa place, et
        // c'est l'expiration du délai de grâce qui appellera `forgetSlotAssignment`.
        // Un téléphone qu'on déverrouille retrouve ainsi sa vignette là où
        // l'opérateur l'avait mise.
        if (reason && reason !== 'lost') {
          setupRef.current.clearDeviceAwaiting(deviceId);
          setupRef.current.releaseRemoteDeckSlot(deviceId);
        } else {
          setupRef.current.markDeviceAwaiting(deviceId);
        }

        const previewHandle = setupRef.current.previewBorrowHandlesRef.current.get(sourceId);
        if (previewHandle) {
          try { previewHandle.release(); } catch {}
          setupRef.current.previewBorrowHandlesRef.current.delete(sourceId);
        }
        setupRef.current.previewStreamsRef.current.delete(sourceId);
        setupRef.current.setPreviewStreams(new Map(setupRef.current.previewStreamsRef.current));

        if (activeSourceRef.current === sourceId) {
          activeSourceRef.current = null;
          setSelectedVideoSource('');
          clearCurrentStream();
        }

        setSelectedAudioDeviceIds(prev => {
          const next = new Map(prev);
          next.delete(sourceId);
          return next;
        });
      },
    },
  );
}, [
  buildSignalingRefs, buildSignalingCallbacks,
  remoteDevices, addSdpLogRegie,
  iceCandidateBufferRef, remoteDescSetRef, remoteConnectionsRef, setRemoteConnections,
  initIntercommForDevice, cleanupIntercommForDevice,
  clearCurrentStream, setSelectedVideoSource,
  handleDirectorTake, handleLiveRequest,
]);

useEffect(() => {
  setupRef.current.handleSignalingMessageRef.current = handleSignalingMessageImpl;
  //console.log ('[LiveStreaManager] setupRef.current.handleSignalingMessageRef.current=', setup.handleSignalingMessageRef.current)	
  
}, [handleSignalingMessageImpl]);





  useEffect(() => {
    if (isSlave && slavePcRef.current && slavePcRef.current.connectionState === 'connected' && canvasStreamRef.current) {
      const newVideoTrack = canvasStreamRef.current.getVideoTracks()[0]; if (!newVideoTrack) return;
      const sender = slavePcRef.current.getSenders().find(s => s.track?.kind === 'video');
      if (sender && sender.track?.id !== newVideoTrack.id) sender.replaceTrack(newVideoTrack).catch(() => {});
    }
  }, [canvasStreamRef.current?.id, isSlave]);

  useEffect(() => {
    const el = deckPreviewVideoRef.current; if (!el || !deckPreviewSourceId || activeLayoutPreviewId) return;
    const stream = previewStreams.get(deckPreviewSourceId);
    if (stream && el.srcObject !== stream) { el.srcObject = stream; el.play().catch(() => {}); }
  }, [deckPreviewSourceId, previewStreams, activeLayoutPreviewId]);

  // Filet pour le changement de document du multiview (onglet détaché ↔ régie) : les <video>
  // du preview et du flux final sont alors recréés, VIDES, et ces affectations ne se refont
  // qu'au changement de flux. Seul un élément sans flux est complété — jamais de réaffectation
  // d'un flux déjà posé (flash noir, cf. plus bas).
  useEffect(() => {
    const timer = window.setInterval(() => {
      const fin = canvasPreviewRef.current;
      if (fin && !fin.srcObject && canvasStreamRef.current) { fin.srcObject = canvasStreamRef.current; fin.play().catch(() => {}); }
      const prv = deckPreviewVideoRef.current;
      if (prv && !prv.srcObject && deckPreviewSourceId && !activeLayoutPreviewId) {
        const st = previewStreams.get(deckPreviewSourceId);
        if (st) { prv.srcObject = st; prv.play().catch(() => {}); }
      }
    }, 1000);
    return () => window.clearInterval(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deckPreviewSourceId, previewStreams, activeLayoutPreviewId]);

  useEffect(() => {
    if (!currentStream) return;
    const rafId = requestAnimationFrame(() => {
      // ⚠️ Réaffecter srcObject VIDE l'élément, même quand on y remet exactement le
      // même MediaStream : l'événement `emptied` part, readyState retombe à 0, la
      // taille à 0x0, et le <video> n'affiche plus rien jusqu'à la frame suivante.
      // Sur le moniteur final, ça se voyait comme un flash noir à chaque bascule
      // de source (doublé du cache `canvasHasSignal` de MultiviewPanelV2, qui
      // écoute justement `emptied`). Ne réaffecter que si le flux CHANGE.
      if (videoRef.current && videoRef.current.srcObject !== currentStream) {
        videoRef.current.srcObject = currentStream;
        videoRef.current.play().catch(() => {});
      }
      if (canvasPreviewRef.current && canvasStreamRef.current
          && canvasPreviewRef.current.srcObject !== canvasStreamRef.current) {
        canvasPreviewRef.current.srcObject = canvasStreamRef.current;
        canvasPreviewRef.current.play().catch(() => {});
      }
      if (!activeSourceRef.current && currentStream.active) activeSourceRef.current = selectedVideoSource;
    });
    return () => cancelAnimationFrame(rafId);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStream]);

  useEffect(() => {
    if (!selectedVideoSource || !videoSources.length) return;
    const source = videoSources.find(s => s.id === selectedVideoSource); if (!source) return;
    if (source.type === 'remote' && !source.stream) return;
    if (source.type === 'scene'  && !source.stream) return;
    if (source.type === 'url' && hlsProber.getStatus(source.id) !== 'ok') return;
    if (activeSourceRef.current === selectedVideoSource) return;
    if (currentStreamRef.current?.active && activeSourceRef.current === selectedVideoSource) { if (videoRef.current) videoRef.current.srcObject = currentStreamRef.current; return; }
    startStream();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVideoSource, selectedAudio, videoSources]);

  useEffect(() => {
    if (!selectedVideoSource?.startsWith('remote-')) return;
    const source = videoSources.find(s => s.id === selectedVideoSource);
    if (!source?.stream) return;
    if (activeSourceRef.current === selectedVideoSource && currentStream?.active) {
      const currentTracks = currentStreamRef.current?.getTracks() ?? [];
      const sourceTrackIds = new Set(source.stream.getTracks().map(t => t.id));
      const isSameStream = currentTracks.some(t => sourceTrackIds.has(t.id));
      if (isSameStream) return;
      activeSourceRef.current = null;
    }
    startStreamWithSource(selectedVideoSource);
  }, [remoteConnections, videoSources, selectedVideoSource]);

  // ── Notifie le device distant sélectionné qu'il passe en flux final (source active du deck) ──
  useEffect(() => {
    const nextOnAirDeviceId = selectedVideoSource?.startsWith('remote-')
      ? selectedVideoSource.replace('remote-', '')
      : null;

    const previousOnAirDeviceId = onAirDeviceIdRef.current;
    if (previousOnAirDeviceId === nextOnAirDeviceId) return;

    const ws = signalingWsRef.current;
    if (previousOnAirDeviceId && ws?.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ type: 'on_air', target: previousOnAirDeviceId, active: false }));
      addSdpLogRegie({ direction: 'sent', type: 'event', label: `📡 on_air OFF → ${previousOnAirDeviceId}` });
    }
    if (nextOnAirDeviceId && ws?.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ type: 'on_air', target: nextOnAirDeviceId, active: true }));
      addSdpLogRegie({ direction: 'sent', type: 'event', label: `📡 on_air ON → ${nextOnAirDeviceId}` });
    }
    onAirDeviceIdRef.current = nextOnAirDeviceId;
  }, [selectedVideoSource, signalingWsRef, addSdpLogRegie]);

  // Le mode "Fixe" coupe la source à l'antenne — SAUF quand la source fixe choisie
  // est justement celle qui est à l'antenne (ex. : playlist en final + playlist
  // choisie dans la liste). Tester le mode seul coupait ce cas : cet effet s'exécute
  // après l'effet Switch audio et écrasait le gain que celui-ci venait de rétablir.
  useEffect(() => {
    // Mode Mix : le niveau d'écoute de chaque voie est piloté par l'effet Mix.
    if (audioSwitchState.mode === 'mix') return;
    if (!activeSourceRef.current) return;
    const regId = `audio-${activeSourceRef.current}`;
    if (!audioSourceRegistry.has(regId)) return;
    if (resolveAudioSourceId() !== activeSourceRef.current) {
      audioSourceRegistry.setMonitorGain(regId, 0);
      return;
    }
    audioSourceRegistry.setMonitorGain(regId, finalMuted ? 0 : 1);
  }, [finalMuted, audioSourceRegistry, resolveAudioSourceId, audioSwitchState.mode]);

  // ── Publication permanente du programme ────────────────────────────────────
  //
  // `webrtc_{uuid}` est l'un des TROIS paths sources de la chaîne, aux côtés de
  // `video_{uuid}` et `live_{uuid}` : c'est `ffmpeg_feed` qui décide lequel tirer,
  // selon la source choisie. **Publier dessus ne met donc rien à l'antenne** —
  // c'est « source régie » qui met à l'antenne, pas la publication.
  //
  // D'où l'idée de publier en continu, dès qu'il y a une image à publier :
  //
  //   · le RETOUR par le serveur devient toujours disponible. Il lit ce même path
  //     en WHEP ; tant que la publication n'existait qu'en diffusion, un porteur
  //     de téléphone n'avait aucun retour hors antenne et rien ne le lui disait.
  //   · le passage à l'antenne devient immédiat : `handleStartStreaming` n'a plus
  //     à attendre `waitForHls` sur un path qui vient de naître.
  //   · un seul encodage sert TOUT le parc, là où le retour en direct demande une
  //     PeerConnection et un encodeur par téléphone.
  //
  // Le repli automatique du device reste en place et n'est pas rendu inutile par
  // ce drapeau : il couvre la régie qui n'a pas encore de source, celle qui vient
  // de fermer, et le cas où ce drapeau est remis à false.
  const PERSISTENT_PROGRAM_WHIP = true;

  /**
   * Vrai pendant l'établissement de la publication, et seulement la PREMIÈRE
   * fois : c'est le moment où la régie paraît muette sans rien expliquer. Les
   * renégociations suivantes se font sous les yeux d'un opérateur qui sait déjà
   * ce qui se passe — un voile à chacune serait du bruit.
   */
  const [programConnecting, setProgramConnecting] = useState(false);
  /** A-t-on déjà montré le voile ? Sert au voile, et à rien d'autre. */
  const programEverConnectedRef = useRef(false);
  /**
   * L'opérateur a-t-il fermé la connexion À LA MAIN ?
   *
   * Volontairement distinct de la ref ci-dessus, et il faut vraiment les deux :
   * confondues, une connexion tombée toute seule (réseau, serveur qui redémarre)
   * ne se rouvrirait jamais, puisqu'elle aurait été connectée une fois. Ici seule
   * une fermeture explicite empêche la reprise — une chute, elle, se rattrape.
   */
  const programClosedByUserRef = useRef(false);

  /** Vrai quand le voile décrit une REPRISE et non une première connexion. */
  const [programReconnexion, setProgramReconnexion] = useState(false);

  /**
   * Fermeture manuelle de la publication permanente.
   *
   * Elle refuse net quand la chaîne est à l'antenne SUR la régie : ce n'est pas
   * un excès de prudence, ce serait couper l'émission depuis un panneau de
   * diagnostic, et le bouton qui le fait ne dit pas qu'il le fait.
   */
  const closeProgramLink = useCallback(() => {
    if (streamSource === 'webrtc') {
      showAlert("La régie est à l'antenne : basculez la source avant de fermer la connexion.", 'important');
      return;
    }
    programClosedByUserRef.current = true;
    stopStreamingImpl();
    addSdpLogRegie({ direction: 'sent', type: 'event', label: '⏹ Connexion permanente au serveur fermée à la main' });
  }, [streamSource, stopStreamingImpl, showAlert, addSdpLogRegie]);

  /**
   * Rouvre la publication permanente que l'on a fermée à la main.
   *
   * Il n'y avait aucun chemin de retour : le drapeau posé par `closeProgramLink` n'est
   * jamais levé, et il fallait passer à l'antenne pour republier — ce qui rouvrait la
   * connexion mais laissait le drapeau armé, donc la publication permanente ne se
   * rétablissait plus une fois l'antenne coupée.
   *
   * Lever le drapeau ne suffit pas à repartir : c'est une ref, aucun effet ne s'en
   * réveille. On relance donc la publication ici, sous les mêmes gardes que l'effet —
   * sans image il n'y a rien à publier, et une régie qui ne pilote pas n'a rien à faire
   * sur `webrtc_{uuid}`, qui n'accepte qu'un seul publieur.
   */
  const reopenProgramLink = useCallback(() => {
    if (isStreaming) return;

    if (!currentStream) {
      showAlert("Aucune image en régie : la connexion s'ouvrira d'elle-même dès qu'une source sera montée.", 'important');
      return;
    }

    if (!channelUuid) return;

    if (!setup.controlState.isHolder) {
      showAlert("Cette régie ne pilote pas la chaîne : seule celle qui a la main publie le programme.", 'important');
      return;
    }

    programClosedByUserRef.current = false;
    if (!programEverConnectedRef.current) setProgramConnecting(true);
    addSdpLogRegie({ direction: 'sent', type: 'event', label: '▶ Connexion permanente au serveur rouverte à la main' });
    startWebRTCStreaming();
  }, [isStreaming, currentStream, channelUuid, setup.controlState.isHolder, startWebRTCStreaming, showAlert, addSdpLogRegie]);

  useEffect(() => {
    if (!PERSISTENT_PROGRAM_WHIP) return;
    // `currentStream` est le garde-fou naturel : sans image, `startWebRTCStreaming`
    // sort immédiatement de toute façon. On ne publie donc qu'une fois la première
    // source montée.
    if (!currentStream || !channelUuid || isStreaming) return;
    // Une fermeture à la main doit TENIR : sans ce garde, cet effet la défaisait
    // au rendu suivant et le bouton n'aurait servi à rien.
    if (programClosedByUserRef.current) return;
    // ET SEULEMENT SI L'ON PILOTE. `webrtc_{uuid}` n'a qu'un publieur : deux
    // régies ouvertes sur la même chaîne s'y disputeraient le programme, et une
    // régie spectatrice — qui ne commande rien — écraserait l'antenne de celle
    // qui pilote. Publier en permanence rendait ce risque permanent lui aussi.
    if (!setup.controlState.isHolder) return;
    addSdpLogRegie({
      direction: 'info', type: 'event',
      label: '📡 Publication permanente du programme (webrtc_' + channelUuid + ') — hors antenne tant que la source ne le désigne pas',
    });
    if (!programEverConnectedRef.current) setProgramConnecting(true);
    // silent: automatique, sans clic — l'alerte "chaîne pas activée" est portée par
    // l'entrée sur l'onglet Régie (BroadcastingChannelSheet.handleTabChange), pas ici.
    startWebRTCStreaming({ silent: true });
  }, [currentStream, channelUuid, isStreaming, setup.controlState.isHolder, startWebRTCStreaming, addSdpLogRegie]);

  // Le voile tombe dès que la publication est établie — ou renonce au bout de
  // 20 s. Une négociation WebRTC qui n'a pas abouti en vingt secondes n'aboutira
  // pas, et laisser un voile éternel serait pire que pas de voile du tout :
  // l'opérateur ne pourrait plus rien faire d'une régie par ailleurs utilisable.
  useEffect(() => {
    if (isStreaming) {
      programEverConnectedRef.current = true;
      setProgramConnecting(false);
      setProgramReconnexion(false);
      return;
    }
    if (!programConnecting) return;
    const abandon = setTimeout(() => {
      setProgramConnecting(false);
      addSdpLogRegie({
        direction: 'received', type: 'error',
        label: '⚠ Connexion au serveur non établie après 20 s — la régie reste utilisable, le retour des téléphones passera en direct',
      });
    }, 20_000);
    return () => clearTimeout(abandon);
  }, [isStreaming, programConnecting, addSdpLogRegie]);

  // ── Régie passée en arrière-plan ───────────────────────────────────────────
  //
  // La boucle de composition est un `setInterval` et NON un `requestAnimationFrame`,
  // choix délibéré : rAF est arrêté net sur un onglet caché. Un timer, lui, n'est
  // que ralenti — et une page qui capture et publie du média est largement exemptée
  // de ce ralentissement par les navigateurs.
  //
  // « Largement » n'est pas « toujours », et le canvas est désormais publié en
  // permanence : un onglet laissé en arrière-plan des heures est un cas nouveau.
  // On ne CORRIGE rien ici — on trace, parce qu'un retour figé sur les téléphones
  // et une régie qu'on croyait à l'antenne se ressemblent beaucoup, et que la
  // sonde de retard de `useCanvasPipeline` ne dit pas, elle, si l'onglet était
  // caché à ce moment-là.
  useEffect(() => {
    const onVisibility = () => {
      const cache = document.visibilityState === 'hidden';
      console.warn(
        `[Régie] Onglet ${cache ? 'MASQUÉ' : 'de nouveau visible'}`
        + (cache ? ' — la composition du canvas peut être ralentie par le navigateur ;'
                 + ' le programme publié et le retour des téléphones en dépendent.' : ''),
      );
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []);

  const forceWhipRenegotiation = useCallback(async () => {
    if (!currentStream) { showAlert('Aucun flux actif pour renégocier.', 'important'); return; }
    stopWhipStatsMonitoring(); stopStreamingImpl(); await new Promise(r => setTimeout(r, 300)); await startWebRTCStreamingImpl();
  }, [currentStream, stopWhipStatsMonitoring, stopStreamingImpl, startWebRTCStreamingImpl]);

  useEffect(() => {
    if (!isStreaming || hasCheckedStaleStreamRef.current) return;
    hasCheckedStaleStreamRef.current = true;
    const pc = peerConnectionRef.current;
    // `disconnected` n'est PAS dans cette liste, volontairement : c'est un état
    // transitoire qui se résorbe souvent seul (cf. le délai de grâce de 8 s dans
    // `startWhipStreaming`, api_whip.ts). Le compter comme mort ici — sans même
    // fermer `pc` — pouvait figer `isStreaming` à `false` pour le reste de la
    // session si ce contrôle, joué une seule fois, tombait pile sur un
    // décrochage passager : la connexion réelle continuait, mais plus rien ne
    // remettait `isStreaming` à `true`. Un `disconnected` qui ne se résorbe pas
    // reste pris en charge par `onconnectionstatechange` → `treatAsFailed()`,
    // qui ferme proprement la connexion avant de relancer.
    const isConnectionDead = !pc || pc.connectionState === 'closed' || pc.connectionState === 'failed';
    if (!isConnectionDead) return;
    setIsStreaming(false); setIsStreamButtonActive(false); clearCurrentStream();
    const fallback = defaultStreamSource ?? 'video';
    if (broadcastingChannel?.id) { updateBroadcastingChannelStreamSource(broadcastingChannel.id, fallback).then(result => { if (result.code === 1) onStreamSourceChange?.(fallback); }).catch(err => console.error('[stale-stream]', err)); }
    else { onStreamSourceChange?.(fallback); }
    // Quand la reprise est automatique, on n'ALERTE PAS : on MONTRE.
    //
    // Une boîte à fermer pose une question à laquelle elle ne répond pas — une
    // fois le OK cliqué, l'opérateur ne sait toujours pas si la connexion est
    // revenue, et il n'a rien à décider de toute façon. On réutilise donc
    // l'indicateur de la première connexion : il tourne tant que ça négocie et
    // disparaît quand c'est en place. Le voile EST la réponse.
    //
    // Remettre `programEverConnectedRef` à false est ce qui le fait réapparaître :
    // il ne se montre qu'à la première connexion d'une session, et une reprise
    // après rechargement en est une.
    const reprisePrevue = PERSISTENT_PROGRAM_WHIP && setup.controlState.isHolder;
    if (reprisePrevue) {
      programEverConnectedRef.current = false;
      setProgramReconnexion(true);
      return;
    }
    // Personne ne reprendra la main : là, il y a bien quelque chose à annoncer.
    showAlert("Suite à une déconnexion, vous n'êtes plus en mode régie", 'important');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStreaming]);

  const activeSourceLabel = videoSources.find(s => s.id === selectedVideoSource)?.label ?? 'Aucune source';
  const videoSlotLabelsOrdered = buildVideoRow(deckSlots, channelUuid).map(row => row.isHlsVideo ? (row.slot?.label ?? 'Playlist') : row.isHlsLive ? (row.slot?.label ?? 'OBD, RTMP...') : (row.slot?.label ?? null));

  const handleScreenReset = useCallback(async (slotId: string) => {
    // "Choisir une source" (ConfigMedia, onglet Écran, un bouton par emplacement 'screen-N') :
    // on relâche l'ancienne capture de cet emplacement puis on relance immédiatement
    // l'acquisition pour rouvrir le sélecteur écran/fenêtre/onglet du navigateur — un simple
    // forceRelease n'ouvrait jamais getDisplayMedia() lui-même.
    //
    // Ce bouton vit dans un panneau de CONFIGURATION (choisir/prévisualiser quelle fenêtre
    // capturer), pas de mise à l'antenne : il ne doit surtout pas appeler acquireAndSetStream,
    // qui se termine TOUJOURS par setFinalStream — sans quoi choisir une copie d'écran ici la
    // mettait en direct, même sans l'avoir jamais posée sur un emplacement du Deck. On fait
    // donc l'acquisition nous-mêmes (registry.borrow, comme le ferait acquireAndSetStream pour
    // ce type de source) puis on la confie à startPreviewForSource pour un emprunt durable, et
    // on relâche notre emprunt transitoire une fois celui-ci établi.
    registry.forceRelease(slotId);
    setScreenShareStreams(prev => { const next = new Map(prev); next.delete(slotId); return next; });
    const screenSource = videoSources.find(s => s.id === slotId);
    if (!screenSource) return;
    try {
      const handle = await registry.borrow(slotId);
      setScreenShareStreams(prev => { const next = new Map(prev); next.set(slotId, handle.stream); return next; });
      try { await setupRef.current.startPreviewForSource(screenSource); } catch {}
      try { handle.release(); } catch {}
    } catch (err: any) {
      if (err?.name === 'NotAllowedError' || err?.name === 'AbortError') return;
      console.error(`[handleScreenReset] acquisition "${slotId}" échouée :`, err);
      showAlert("Impossible de démarrer le partage d'écran.", 'important');
    }
  }, [registry, setScreenShareStreams, videoSources, showAlert]);

  useEffect(() => {
    onRegisterStart?.(handleStartStreaming);
    onRegisterStop?.(handleStopStreaming);
    // Les mêmes gestes, pour la demande d'antenne d'un téléphone : elle arrive
    // par la signalisation, hors de tout rendu, et doit tomber sur la version
    // courante (cf. handleLiveRequest).
    handleStartStreamingRef.current = handleStartStreaming;
    handleStopStreamingRef.current  = handleStopStreaming;
  }, [handleStartStreaming, handleStopStreaming]);

  const handleRemoveSource = useCallback((sourceId: string) => {
    disconnectAudioForSlot(sourceId);
    registry.forceRelease(sourceId);
    hlsProber.forceStatus(sourceId, 'failed');
    const previewHandle = setup.previewBorrowHandlesRef.current.get(sourceId);
    if (previewHandle) { try { previewHandle.release(); } catch {} setup.previewBorrowHandlesRef.current.delete(sourceId); }
    previewStreamsRef.current.delete(sourceId);
    setPreviewStreams(new Map(previewStreamsRef.current));
    if (deckPreviewSourceId === sourceId) { setDeckPreviewSourceId(null); if (deckPreviewVideoRef.current) { deckPreviewVideoRef.current.pause(); deckPreviewVideoRef.current.srcObject = null; } }
    if (selectedVideoSource === sourceId) { setSelectedVideoSource(''); activeSourceRef.current = null; clearCurrentStream(); if (canvasPreviewRef.current) { canvasPreviewRef.current.pause(); canvasPreviewRef.current.srcObject = null; } stopCompositingLoop(); }
    setVideoSources(prev => prev.filter(s => s.id !== sourceId));
    setup.deckSlots.forEach((slot, idx) => { if (slot?.id === sourceId) setup.handleDeckSlotChange(idx, null); });
  }, [disconnectAudioForSlot, registry, hlsProber, setup, previewStreamsRef, setPreviewStreams, deckPreviewSourceId, setDeckPreviewSourceId, deckPreviewVideoRef, selectedVideoSource, setSelectedVideoSource, clearCurrentStream, canvasPreviewRef, stopCompositingLoop, setVideoSources]);

  // ── Rechargement des sources externes de la chaîne ─────────────────────────
  //
  // Trois déclencheurs : l'ouverture de l'onglet, l'annonce `sources_changed` du
  // microservice (adressée à TOUTES les régies), et la prise de main.
  //
  // Ce qui était faux avant : la liste n'était lue qu'à l'ouverture. Une régie
  // ouverte avant qu'une caméra RTMP ne soit ajoutée ne la voyait jamais — pas
  // même au moment où la main lui revenait, où elle se mettait alors à piloter
  // sur une photo périmée.
  //
  // Tout passe par des refs : `handleRemoveSource` dépend de `setup`, et les
  // fonctions du contexte média changent d'identité à chaque rendu. En dépendre
  // ici rebâtirait la fonction sans fin.
  const reloadDepsRef = useRef({ handleAddHlsSource, handleRemoveSource, videoSources, serverUrl: settings.serverUrl, channelPublicRef, remoteDevices, handleRegisterDevice, showAlert, channelUuid, remapSourceIds });
  reloadDepsRef.current = { handleAddHlsSource, handleRemoveSource, videoSources, serverUrl: settings.serverUrl, channelPublicRef, remoteDevices, handleRegisterDevice, showAlert, channelUuid, remapSourceIds };

  reloadExternalSourcesRef.current = (reason?: string) => {
    // Le microservice a redémarré la mire : le lecteur HLS de la Playlist reste figé sur
    // la dernière image d'avant (constaté sur Ales TV le 2026-10-25). La liste des sources
    // n'a pas changé, seul le lecteur est à refaire.
    if (reason === 'mire-reloaded') { restartPlaylistAfterMireRef.current(); return; }
    const { handleAddHlsSource: ajouter, handleRemoveSource: retirer, videoSources: sourcesCourantes, serverUrl, channelPublicRef: adresse, remoteDevices: appareils, handleRegisterDevice: enregistrer, showAlert: alerter, channelUuid: uuidChaine, remapSourceIds: reporter } = reloadDepsRef.current;
    if (!adresse) return;
    const microserviceUrl = `${serverUrl}/${adresse}/regie`;
    import('../../../backendServices/liveStreamManagment/api_microservice_sources')
      .then(async ({ listAllHlsStreams, listXtreamAccounts, getXtreamAccount }) => {
        const [{ streams }, { accounts }] = await Promise.all([
          listAllHlsStreams(microserviceUrl),
          listXtreamAccounts(microserviceUrl),
        ]);
        // Le microservice répond : si une restauration attendait la chaîne, c'est maintenant.
        const enAttente = takePendingExternalSources(uuidChaine);
        if (enAttente) {
          try {
            const { idRemap, report } = await restoreExternalSources(enAttente, {
              microserviceUrl, serverUrl, streamPath: adresse, publicBaseUrl: serverUrl,
              existingDevices: appareils, registerDevice: enregistrer,
            });
            reporter(idRemap);
            const note = describeRestoreReport(report);
            alerter(`Sources externes de la sauvegarde recréées.${note ? `\n${note}` : ''}`, 'important');
          } catch (e) {
            console.warn('[SOURCES] restauration en attente échouée :', e);
            if (uuidChaine) savePendingExternalSources(uuidChaine, enAttente);
          }
          setTimeout(() => reloadExternalSourcesRef.current(), 0);   // relire la liste, maintenant complète
          return;
        }

        const accountsWithPassword = await Promise.all(
          accounts.map((a: any) =>
            getXtreamAccount(microserviceUrl, a.id, true)
              .then(({ account }: any) => account)
              .catch(() => a)
          )
        );

        // Les identifiants viennent de handleAddHlsSource lui-même : lui seul
        // connaît la formule, et l'ajout est sans effet si la source est déjà là.
        const declarees = new Set<string>();
        streams.forEach((s: any) => {
          if (s.type === 'xtream' && s.xtream_account_id && s.xtream_stream_id) {
            const account = accountsWithPassword.find((a: any) => a.id === s.xtream_account_id);
            // Passe par le proxy seulement si la cible est en HTTP clair —
            // cf. toProxiedUrl : une URL déjà en HTTPS y récolterait un 400.
            const proxiedUrl = toProxiedUrl(s.url, serverUrl);
            declarees.add(ajouter(proxiedUrl, { server: account?.server ?? '', username: account?.username ?? '', password: account?.password ?? '', streamId: s.xtream_stream_id }, s.label));
          } else {
            // Une caméra RTMP n'est jointe qu'au moment où son encodeur publie :
            // elle entre dans le deck en attente, pas en erreur.
            declarees.add(ajouter(s.url, undefined, s.label, { autoRetry: s.type === 'rtmp' }));
          }
        });

        // Et les disparues. Uniquement celles qui VIENNENT du microservice :
        // caméras locales, copies d'écran, scènes et flux de la chaîne portent
        // d'autres identifiants et ne le concernent pas.
        sourcesCourantes.forEach(src => {
          if (!src.id.startsWith('hls-custom-') && !src.id.startsWith('hls-xtream-')) return;
          if (declarees.has(src.id)) return;
          console.warn(`[SOURCES] ${src.id} n'est plus déclarée par le microservice — retirée du deck`);
          retirer(src.id);
        });
      })
      .catch(err => console.warn('[SOURCES] Erreur de rechargement des sources microservice:', err));
  };

  // ── Perte du contrôle : couper NOTRE intercomm ──────────────────────────────
  //
  // L'intercomm vit sur ses propres `stream_id` (`intercomm_regie_to_device` et
  // `intercomm_device_to_regie`), que la bascule du contrôle ne renégocie pas :
  // rien ne les fermait. L'opérateur qui venait de perdre la main restait donc
  // AUDIBLE DANS L'OREILLETTE des téléphones, jusqu'à ce que la nouvelle régie
  // ouvre le sien à la même adresse — ou jamais, si elle ne l'ouvre pas.
  //
  // On lit `isHolder` sur un front descendant : ne pas plus piloter, c'est ne
  // plus parler aux caméras. La reprise de l'intercomm par la nouvelle régie
  // suit son propre chemin (initRegieIntercommForDevice, au rattrapage).
  const wasHolderRef = useRef(setup.controlState.isHolder);

  // Perdre la main, c'est aussi cesser de publier le programme : la régie qui la reprend
  // publie à son tour sur le même path, et celle qui garderait sa connexion ouverte (ou la
  // rouvrirait) s'y disputerait l'antenne avec elle. Lu AVANT l'effet ci-dessous, qui met
  // `wasHolderRef` à jour : on a besoin de l'ancienne valeur.
  useEffect(() => {
    const action = programActionOnControlChange(wasHolderRef.current, setup.controlState.isHolder, isStreamingRef.current);
    if (action !== 'stop') return;
    addSdpLogRegie({ direction: 'info', type: 'event', label: '⏹ Contrôle perdu — publication du programme arrêtée (une seule régie publie)' });
    stopStreamingImplRef.current();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setup.controlState.isHolder]);

  useEffect(() => {
    const isHolder = setup.controlState.isHolder;
    const perteDeLaMain = wasHolderRef.current && !isHolder;
    wasHolderRef.current = isHolder;
    if (!perteDeLaMain) return;

    // Copie avant parcours : cleanupIntercommForDevice retire de ce même Set.
    const devices = Array.from(knownIntercommDevicesRef.current);
    if (devices.length === 0) return;
    addSdpLogRegie({
      direction: 'info', type: 'event',
      label: `🎙 Contrôle perdu — intercomm coupé pour ${devices.length} device(s)`,
    });
    devices.forEach(cleanupIntercommForDevice);
    knownIntercommDevicesRef.current.clear();
  }, [setup.controlState.isHolder, cleanupIntercommForDevice, addSdpLogRegie]);

  // ── Suppression / déconnexion d'un device distant : le hook coupe la session
  //    (force_disconnect + WHEP + buffers) et délègue ici le nettoyage de tout ce
  //    qui n'existe que dans ce composant — la source `remote-<id>` (deck, preview,
  //    audio, registry) et l'intercomm du device. ──
  useEffect(() => {
    setup.remoteDeviceTeardownRef.current = (deviceId: string) => {
      cleanupIntercommForDevice(deviceId);
      handleRemoveSource(`remote-${deviceId}`);
    };
  }, [setup.remoteDeviceTeardownRef, cleanupIntercommForDevice, handleRemoveSource]);

  // La perte du contrôle en cours de route est signalée par une alerte, déclenchée
  // directement depuis le gestionnaire de `control_revoked` (useLiveStreamSetup).
  // Elle ne passe volontairement PAS par un état React : le `control_state` que le
  // serveur diffuse juste après écraserait le drapeau avant que l'effet ne
  // s'exécute, et l'opérateur ne verrait rien.

  // ── Qui observe, et le bandeau qui le dit ────────────────────────────────
  //
  // Reprendre la main réarme l'acquittement. Sans cela, une régie qui reprend un
  // poste qu'elle avait déjà tenu retrouve la signature qu'elle avait acquittée
  // la fois d'avant, et le bandeau ne revient jamais : c'est exactement le
  // moment où l'opérateur a besoin de savoir qui l'observe.
  //
  // `peers` nomme chaque régie ; un microservice antérieur au 24/08/2026 ne
  // l'envoie pas, on retombe alors sur le décompte, qui est ce qu'on affichait
  // jusque-là. La signature sert à faire revenir le bandeau quand le peuplement
  // change : ce n'est plus la même information, l'acquittement précédent ne vaut
  // plus.
  const regiesObservatrices = setup.controlState.peers.filter(p => !p.isHolder);
  const signatureObservation = regiesObservatrices.length > 0
    ? regiesObservatrices.map(p => p.sessionId || p.addr).sort().join('|')
    : `nombre:${setup.controlState.regieCount}`;
  const bandeauObservationVisible =
    setup.controlState.isHolder
    && !setup.incomingControlRequest
    && setup.controlState.regieCount > 1
    && signatureObservation !== observationAcquittee;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 p-3">
      {/* ── Établissement de la connexion au serveur ────────────────────────
          La régie publie son programme dès qu'elle a une image, et la
          négociation WebRTC prend quelques secondes. Sans ce message, ces
          secondes-là ressemblent à une régie qui ne répond pas : les téléphones
          n'ont pas encore de retour, le passage à l'antenne échouerait, et rien
          à l'écran ne dit pourquoi.

          `pointer-events-none` est délibéré : on informe, on ne bloque pas. La
          régie reste pilotable pendant la négociation — poser une source ou
          régler un niveau n'attend pas le serveur. */}
      {programConnecting && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center pointer-events-none">
          <div className="flex items-center gap-3 rounded-xl border border-sky-400/40 bg-slate-900/90 px-6 py-4 shadow-2xl">
            <span className="h-4 w-4 animate-spin rounded-full border-2 border-sky-400 border-t-transparent" />
            <span className="text-sm font-semibold text-sky-100">
              {programReconnexion
                ? 'Connexion à la régie interrompue — rétablissement en cours…'
                : 'Connexion de la régie au serveur…'}
            </span>
          </div>
        </div>
      )}

      {/* ── Contrôle de la régie ────────────────────────────────────────────
          Plusieurs régies peuvent être ouvertes sur la même chaîne, une seule
          pilote les devices. Ce bandeau n'apparaît QUE pour celle qui ne pilote
          pas : sans lui, elle continuerait d'envoyer des ordres que le serveur
          ignore, en croyant commander — c'est ce qui se passait avant le
          15/08/2026, sans le moindre signe à l'écran. */}
      {/* ── Source audio fixe tombée ───────────────────────────────────────
          Sans ce bandeau, la régie diffusait du silence sans le moindre signe
          (Akinax, 03/11). Rouge : c'est ce qui part en direct qui change. */}
      {audioSwitchState.mode === 'fixe' && downFixedAudio && (
        <div className="mb-3 rounded-lg border border-red-500/60 bg-red-500/15 px-4 py-3 text-sm text-red-100">
          <span className="font-semibold">Source audio fixe indisponible.</span>{' '}
          « {downFixedAudio.label} » ne donne plus de son (micro débranché, appareil déconnecté…) :
          c'est le son de la source à l'antenne qui part en direct. Le Switch audio reviendra
          tout seul sur « {downFixedAudio.label} » dès qu'elle sera de retour.
        </div>
      )}
      {observerMode === 'silent' && (
        <div className="mb-3 rounded-lg border border-sky-500/60 bg-sky-500/15 px-4 py-3 text-sm text-sky-100">
          <span className="font-semibold">Observation discrète.</span>{' '}
          Vous regardez la chaîne d'un autre créateur : vous ne pouvez pas la piloter et il n'est pas prévenu de votre présence.
        </div>
      )}
      {!setup.controlState.isHolder && observerMode !== 'silent' && (
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-amber-500/60 bg-amber-500/15 px-4 py-3">
          <div className="text-sm text-amber-100">
            <span className="font-semibold">Cette régie ne pilote pas.</span>{' '}
            {setup.controlState.holderLabel
              ? <>Le contrôle est détenu par « {decrireRegie(setup.controlState.holderLabel, setup.controlState.holderAddr)} ».</>
              : <>Une autre régie détient le contrôle.</>}{' '}
            Vous voyez l'antenne, mais vos actions sur les caméras distantes ne sont pas prises en compte.
            {/* La conséquence est annoncée AVANT le clic, pas après : reprendre
                la main coupe tous les téléphones, et l'opérateur doit pouvoir
                décider en le sachant. */}
            {!setup.controlRequest && (
              <>
                {' '}
                <span className="font-semibold text-amber-200">
                  Prendre le contrôle déconnectera les sources externes :
                </span>{' '}
                chaque appareil distant coupe sa session et se reconnecte tout seul à votre
                régie — comptez quelques secondes sans image.
              </>
            )}
            {setup.controlRequest && (
              <>
                {' '}
                <span className="font-semibold">
                  Demande envoyée à « {decrireRegie(setup.controlRequest.holderLabel, setup.controlRequest.holderAddr)} » — en attente de sa réponse.
                </span>
                {setup.controlRequest.canForce
                  ? <> Sans réponse de sa part, vous pouvez passer en force.</>
                  : <> Si elle ne répond pas, vous pourrez passer en force dans quelques secondes.</>}
                {' '}À la bascule, les sources externes seront déconnectées, puis se
                reconnecteront d'elles-mêmes à votre régie.
              </>
            )}
          </div>
          {/* Le geste change selon l'état de la demande, et il n'y en a jamais
              qu'un seul de proposé : demander, patienter (avec la possibilité de
              renoncer), puis forcer — mais UNIQUEMENT faute de réponse. Un refus
              explicite clôt la demande côté serveur : le bouton « Forcer » ne
              réapparaît pas, il n'y a plus rien à forcer. */}
          <div className="flex shrink-0 items-center gap-2">
            {!setup.controlRequest && (
              <button
                onClick={setup.requestControl}
                className="rounded-md bg-amber-500 px-4 py-2 text-sm font-semibold text-slate-900 hover:bg-amber-400"
              >
                Prendre le contrôle
              </button>
            )}
            {setup.controlRequest && (
              <button
                onClick={setup.cancelControlRequest}
                className="rounded-md border border-amber-400/60 px-4 py-2 text-sm font-semibold text-amber-100 hover:bg-amber-500/20"
              >
                Annuler la demande
              </button>
            )}
            {setup.controlRequest?.canForce && (
              <button
                onClick={setup.forceControl}
                className="rounded-md bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-500"
              >
                Passer en force
              </button>
            )}
          </div>
        </div>
      )}
      {/* Le porteur du contrôle doit savoir qu'il n'est pas seul : c'est ce qui
          rend une reprise de main compréhensible quand elle arrive, au lieu d'une
          révocation surgie de nulle part. Muet dans le cas courant d'une régie
          unique. */}
      {/* ── Une autre régie demande la main ───────────────────────────────
          Volontairement un BANDEAU, et non une modale : le porteur est à
          l'antenne, et une fenêtre par-dessus le pupitre lui prendrait le cut et
          les raccourcis au pire moment. La question attend, elle ne bloque pas.

          Ne pas répondre n'est pas refuser : passé le délai, la demandeuse peut
          forcer. C'est écrit ici, pour que le silence soit un choix et non une
          surprise. */}
      {setup.controlState.isHolder && setup.incomingControlRequest && (
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-amber-400/70 bg-amber-500/20 px-4 py-3">
          <div className="text-sm text-amber-50">
            <span className="font-semibold">
              « {decrireRegie(setup.incomingControlRequest.label, setup.incomingControlRequest.addr)} » demande le contrôle de cette chaîne.
            </span>{' '}
            En acceptant, vous cessez de piloter : vous continuerez de voir et d'entendre
            l'antenne, mais vos actions sur les caméras distantes ne seront plus prises en
            compte.{' '}
            <span className="font-semibold text-amber-200">
              Les sources externes seront déconnectées :
            </span>{' '}
            chaque appareil distant coupe sa session et se reconnecte tout seul à la régie
            qui prend la main — quelques secondes sans image, sans rien à faire sur les
            téléphones. Sans réponse de votre part, cette régie pourra passer en force au
            bout de {Math.round(setup.incomingControlRequest.graceS)} secondes — avec les
            mêmes conséquences.
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <button
              onClick={() => setup.answerControlRequest(false)}
              className="rounded-md border border-amber-300/60 px-4 py-2 text-sm font-semibold text-amber-50 hover:bg-amber-500/30"
            >
              Refuser
            </button>
            <button
              onClick={() => setup.answerControlRequest(true)}
              className="rounded-md bg-amber-400 px-4 py-2 text-sm font-semibold text-slate-900 hover:bg-amber-300"
            >
              Passer la main
            </button>
          </div>
        </div>
      )}
      {bandeauObservationVisible && (
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-sky-500/40 bg-sky-500/10 px-4 py-2 text-sm text-sky-100">
          <div>
            Vous pilotez cette chaîne. {setup.controlState.regieCount - 1} autre
            {setup.controlState.regieCount > 2 ? 's régies sont connectées' : ' régie est connectée'} en
            observation — elle{setup.controlState.regieCount > 2 ? 's' : ''} peut
            {setup.controlState.regieCount > 2 ? 'vent' : ''} demander le contrôle à tout moment.
            {/* Nommer les postes plutôt que les compter : c'est la seule façon
                pour le porteur de savoir si l'observateur est un collègue à côté
                de lui ou une régie qu'il n'attendait pas. */}
            {regiesObservatrices.length > 0 && (
              <> Connectée{regiesObservatrices.length > 1 ? 's' : ''} depuis{' '}
                {regiesObservatrices.map(p => decrireRegie(p.label, p.addr)).join(', ')}.
              </>
            )}
          </div>
          {/* Une information permanente, pas une alerte : le porteur l'acquitte
              et récupère sa hauteur d'écran. Elle revient d'elle-même si une
              autre régie arrive ou repart. */}
          <button
            onClick={() => setObservationAcquittee(signatureObservation)}
            className="shrink-0 rounded-md border border-sky-400/60 px-4 py-1.5 text-sm font-semibold text-sky-50 hover:bg-sky-500/20"
          >
            OK
          </button>
        </div>
      )}
      {windowState.open && windowState.message && (
        <Window message={windowState.message} messageType={windowState.type} messageImportance={windowState.importance} onClickOnValidateButton={windowState.onValidate} onClickOnCancelButton={windowState.onCancel}
                onClickOnExtraButton={windowState.onExtra} extraButtonText={windowState.extraButtonText}
                validButtonText={windowState.validButtonText} cancelButtonText={windowState.cancelButtonText}
                width={windowState.width} />
      )}
      {showDeviceInfoPanel && activeDeviceInfo && (
        <DeviceInfoPanel info={activeDeviceInfo} onClose={() => setShowDeviceInfoPanel(false)} />
      )}
      {showWhipInfoPanel && (
        <WhipInfoPanel info={whipInfo} activeSourceLabel={activeSourceLabel} onClose={() => setShowWhipInfoPanel(false)} onRenegotiate={forceWhipRenegotiation} />
      )}
      <div className="hidden">
        <video ref={videoRef} autoPlay playsInline muted />
      </div>
      {/*
        Les deux <video> source du double tampon sont HORS ÉCRAN, pas en
        display:none : un élément display:none ne produit pas forcément de
        frames décodables pour un MediaStream (c'est précisément pourquoi
        l'ancienne implémentation devait décoder sur un <video> temporaire
        positionné hors écran avant de transférer le flux). Ils doivent rester
        dans le flux de rendu, invisibles et inertes.
      */}
      <div style={{ position: 'fixed', top: -9999, left: -9999, width: 1, height: 1,
                    overflow: 'hidden', opacity: 0, pointerEvents: 'none' }} aria-hidden="true">
        <video ref={sourceVideoRef}    autoPlay playsInline muted />
        <video ref={sourceVideoAltRef} autoPlay playsInline muted />
      </div>
      {/* ── Écoute de l'intercomm device → régie ──────────────────────────────
          Le flux de retour arrivait bien (`ontrack` → `intercommDeviceStreams`),
          mais RIEN NE LE JOUAIT : son seul consommateur était la boucle de
          VU-mètre du multiview, un analyser jamais relié à `ctx.destination`.
          L'opérateur entendait donc la régie parler aux téléphones, et jamais
          les téléphones lui répondre.

          Délibérément HORS du graphe Web Audio, contrairement à tout le reste du
          son de la régie, pour deux raisons :

          1. l'intercomm ne doit JAMAIS atteindre le bus WHIP. Le brancher dans
             la chaîne A le mettrait à un geste de partir à l'antenne ;
          2. sous Chromium, un `MediaStream` distant ne produit d'échantillons
             pour `createMediaStreamSource` que s'il est aussi rattaché à un
             élément média. Cet élément est donc ce qui rend AUSSI les VU-mètres
             d'intercom vivants — ils lisaient un flux muet.

          C'est un chemin audible que les mutes de la régie ne pilotent pas : ici
          c'est voulu — on doit toujours entendre le retour d'un opérateur qui
          appuie sur « parler ». */}
      <div style={{ position: 'fixed', top: -9999, left: -9999, width: 1, height: 1,
                    overflow: 'hidden', opacity: 0, pointerEvents: 'none' }} aria-hidden="true">
        {Array.from(intercommDeviceStreams.entries()).map(([deviceId, stream]) => (
          <audio
            key={deviceId}
            autoPlay
            ref={el => {
              // Ne jamais réaffecter à l'identique : la lecture repart de zéro.
              if (!el || el.srcObject === stream) return;
              el.srcObject = stream;
              el.play().catch(err => {
                console.warn(`[Intercomm] Lecture du retour ${deviceId} refusée:`, err?.message);
                addSdpLogRegie({
                  direction: 'received', type: 'error',
                  label: `🎙 Retour ${deviceId} : lecture refusée par le navigateur (${err?.name ?? 'erreur'})`,
                  deviceId,
                });
              });
            }}
          />
        ))}
      </div>
      <div style={{ margin: '0 auto' }}>
        {showSaveConfig && channelUuid && (
          <RegieConfigModal mode="save" kind="deck" channelName={broadcastingChannel?.name ?? 'Chaîne'} channelUuid={channelUuid} userName={authentication.admin ? `${authentication.admin.firstname} ${authentication.admin.lastname}` : "Utilisateur"} deckSlots={deckSlots} keyMap={keyMap} slotConfigs={slotConfigs.map(c => c.sourceId && !deckScopeIds().has(c.sourceId) ? { ...c, sourceId: null } : c)} sourceAliases={scopeRecord(sourceAliases as Record<string, any>, deckScopeIds()) as typeof sourceAliases} getSourceAudioConfig={getSourceAudioConfig} selectedAudioDeviceIds={scopeMap(selectedAudioDeviceIds, deckScopeIds())} stereoVolumes={scopeMap(stereoVolumes, deckScopeIds())} audioEffectsMap={scopeMap(audioEffectsMap, deckScopeIds())} audioEffectConfigsMap={scopeMap(audioEffectConfigsMap, deckScopeIds())} videoEffectsSettingsMap={scopeMap(videoEffectsSettingsMap, deckScopeIds())} imageSlots={imageSlots} soundSlots={soundSlots} jingleSlots={jingleSlots} layoutSlots={layoutSlots} pipSlots={videoOverlaySlots} deviceTransport={settings.deviceTransport} knownSourceIds={videoSources.map(sv => sv.id)} autoDirectorSettings={autoDirector.settings} audioSwitchState={audioSwitchState} whipEncoding={loadWhipEncoding(whipEncodingStorageKey(broadcastingChannel?.id ?? null))} getExternalSources={() => channelPublicRef ? collectExternalSources({ microserviceUrl: `${settings.serverUrl}/${channelPublicRef}/regie`, serverUrl: settings.serverUrl, streamPath: channelPublicRef }) : Promise.resolve(null)} onClose={() => setShowSaveConfig(false)} />
        )}
        {showLoadConfig && channelUuid && (
          <RegieConfigModal mode="load" kind="deck" channelUuid={channelUuid} onLoad={handleLoadConfig} onClose={() => setShowLoadConfig(false)} />
        )}

        {/* ── Voile de la régie secondaire ──────────────────────────────────
            Plusieurs régies peuvent être ouvertes sur la même chaîne ; une seule
            pilote. Le bandeau au-dessus le dit, mais rien n'empêchait la
            spectatrice d'appuyer sur les boutons : le serveur ignore ses ordres
            sans le lui rendre visible, ce qui ressemble exactement à une régie
            qui plante.

            Le voile est TRANSPARENT et volontairement inerte : il avale le clic
            sans rien déclencher. Le seul chemin pour agir reste le bandeau du
            haut — « Prendre le contrôle », puis « Annuler la demande » ou
            « Passer en force » —, rendu HORS de ce conteneur et donc cliquable.

            Il ne couvre pas les modales déjà ouvertes au moment de la
            révocation : elles sont en `position: fixed`, au-dessus. Le cas est
            marginal — on ne peut plus en ouvrir une une fois le voile posé. */}
        {/* L'inertie passe par `pointer-events` et non plus par un voile posé
            PAR-DESSUS. La différence compte : l'héritage de `pointer-events` est
            déterministe, alors qu'un recouvrement dépend de l'ordre de peinture
            — et surtout, un descendant peut le lever pour lui seul, ce qu'aucun
            voile ne permet. C'est ainsi que la régie en observation garde la
            PRÉVISUALISATION : ces flux-là (HLS, Xtream, caméras RTMP) sont tirés
            par son propre navigateur, ils ne passent pas par le porteur, et les
            regarder n'agit sur rien. Le CUT, lui, reste hors d'atteinte.

            Les modales du pupitre passent toutes par `createPortal` : rendues
            hors de ce sous-arbre, elles n'héritent de rien et restent utilisables
            si l'une était déjà ouverte quand la main a été perdue — exactement ce
            que faisait le voile, qui ne les couvrait pas non plus. */}
        <div ref={regieWrapRef} style={{ position: 'relative', minWidth: regieMinWidth || undefined, cursor: setup.controlState.isHolder ? undefined : 'not-allowed' }}>
        <div style={{ pointerEvents: setup.controlState.isHolder ? undefined : 'none' }}>
        <LiveStreamLayoutV2
          isHolder={setup.controlState.isHolder}
          canvasPreviewRef={canvasPreviewRef} hlsMonitorStream={hlsMonitorStream} deckPreviewVideoRef={deckPreviewVideoRef}
          monitorHlsUrl={monitorHlsUrl}
          hasCurrentStream={!!currentStream} isStreaming={isStreaming} isStreamButtonActive={isStreamButtonActive}
          deckPreviewLabel={activeLayoutPreviewId ? (layoutSlots.find(l => l.id === activeLayoutPreviewId)?.label ?? 'Scène') : (videoSources.find(s => s.id === deckPreviewSourceId)?.label ?? '—')}
          activeJingleId={activeJingleId}
          previewMuted={previewMuted} onTogglePreviewMuted={handleTogglePreviewMuted}
          finalMuted={finalMuted} onToggleFinalMuted={handleToggleFinalMuted}
          isStartingStream={isStartingStream} isStoppingStream={isStoppingStream} onStartStreaming={handleStartStreaming} onStopStreaming={handleStopStreaming}
          isRecording={isRecording} isTogglingRecording={isTogglingRecording} recordingElapsedSec={recordingElapsedSec} remainingRecordingSec={displayRemainingRecordingSec} onStartRecording={handleStartRecording} onStopRecording={handleStopRecording}
          onOpenStorage={handleOpenStorageModal}
          remoteDevices={remoteDevices} remoteConnections={remoteConnections} serverUrl={settings.serverUrl} streamPath={channelPublicRef}
          onLoadDevices={loadDevices} onRegisterDevice={handleRegisterDevice} onDeleteDevice={handleDeleteDevice} onDisconnectDevice={handleDisconnectDevice} onRegeneratePassword={handleRegeneratePassword} onSetDirector={handleSetDirector} directorDeviceIds={directorDevices} deviceNetworkQuality={deviceNetworkQuality}
          deckSlots={deckSlots} deckSlotLayouts={deckSlotLayouts} onSlotLayoutChange={handleDeckSlotLayoutChange} onDeckPreviewLayout={handleDeckPreviewLayout} activeLayoutPreviewId={activeLayoutPreviewId}
          selectedVideoSource={selectedVideoSource} onDeckSwitch={handleDeckSwitch} onDeckPreview={handleDeckPreviewWrapped} onAcquirePreview={startPreviewForSource}
          deckPreviewSourceId={deckPreviewSourceId} onDeckCut={handleDeckCut} previewStreams={previewStreams}
          tbarValue={tbar.barValue} tbarValueRef={tbar.barValueRef} tbarBaseline={tbar.baseline} onTbarChange={tbar.scrub} onTbarAuto={tbar.triggerAuto}
          tbarAutoActive={tbar.isAuto} tbarSpeedSec={tbar.speedSec} onTbarSpeedChange={tbar.setSpeedSec}
          tbarEnchainementId={tbarEnchainementId} onTbarEnchainementChange={setTbarEnchainementId}
          tbarEnchainementConfig={activeTbarEnchainementConfig} onTbarEnchainementConfigChange={handleTbarEnchainementConfigChange}
          imageSlots={imageSlots} activeImageIds={activeImageIds} onToggleImage={toggleImageSlot}
          soundSlots={soundSlots} activeSoundIds={activeSoundIds} onTriggerSound={triggerSoundSlot}
          jingleSlots={jingleSlots} onTriggerJingle={triggerJingleSlot}
          videoOverlaySlots={videoOverlaySlots} activeVideoOverlayIds={activeVideoOverlayIds} pipVideoEls={pipVideoEls} onStartVideoOverlay={startVideoOverlay}
          keyMap={keyMap} onOpenKeyboardShortcuts={handleOpenKeyboardShortcuts}
          onOpenMediaboardConfig={handleToggleMediaboardConfig} showMediaboardConfig={showMediaboardConfig} onCloseMediaboardConfig={handleCloseMediaboardConfig}
          onImageChange={setImageSlots} onSoundChange={setSoundSlots} onPipChange={setVideoOverlaySlots} onJingleChange={setJingleSlots}
          loadedAudioBuffers={loadedAudioBuffersRef.current}
          showKeyboardShortcuts={showKeyboardShortcuts} onCloseKeyboardShortcuts={handleCloseKeyboardShortcuts} onKeyMapChange={handleKeyMapChange}
          videoSlotLabels={videoSlotLabelsOrdered}
          imageSlotLabels={imageSlots.map(s => s.url ? (s.label || s.url) : '')}
          soundSlotLabels={soundSlots.map(s => s.url ? (s.label || s.url) : '')}
          pipSlotLabels={videoOverlaySlots.map(s => s.label)}
          jingleSlotLabels={jingleSlots.map((s, i) => s.url ? (s.label || `Jingle ${i + 1}`) : '')}
          screenShareSlots={screenShareSlots} screenShareStreams={screenShareStreams} onScreenSlotsChange={setScreenShareSlots} onScreenReset={handleScreenReset}
          channelActive={channelActive} channelFullyActive={channelFullyActive} channelReadyForRecording={channelReadyForRecording} availableSources={videoSources}
          slotConfigs={slotConfigs} onSlotConfigsChange={handleSlotConfigsChange}
          onSlotsReorder={handleMultiviewReorder}
          closeOnFullscreen={closeOnFullscreen} onToggleCloseOnFullscreen={handleToggleCloseOnFullscreen} onOpenFullscreen={handleOpenFullscreen}
          sourceAliases={sourceAliases} onSourceAliasesChange={setSourceAliases} showSourceAliasConfig={showSourceAliasConfig} onOpenSourceAliasConfig={handleOpenSourceAliasConfig} onCloseSourceAliasConfig={handleCloseSourceAliasConfig}
          onOpenDeckFullscreen={handleOpenDeckFullscreen}
          layoutSlots={layoutSlots} activeLayoutId={activeLayoutId} onToggleLayout={handleToggleLayout} onLayoutChange={setLayoutSlots}
          getSourceAudioConfig={getSourceAudioConfig} onSourceVolumeChange={handleSourceVolumeChange} onSourceMuteToggle={handleSourceMuteToggle}
          isDeckExternal={isDeckExternal} isMultiviewExternal={isMultiviewExternal}
          availableAudioDevices={extendedAudioDevices} onDeckSwitchSource={handleDeckSwitchSource} onAliasChange={handleAliasChange}
          selectedAudioDeviceIds={selectedAudioDeviceIds} onAudioDeviceChange={handleAudioDeviceChange}
          deviceGeo={deviceGeo} onGeoWatch={setDeviceGeoWatch}
          programLinkActive={isStreaming} programLinkOnAir={streamSource === 'webrtc'} onCloseProgramLink={closeProgramLink}
          onReopenProgramLink={reopenProgramLink}
          onMetricsLog={setDeviceMetricsLog}
          stereoVolumes={stereoVolumesShown} onStereoVolumeChange={handleStereoVolumeChange}
          audioEffectsMap={audioEffectsMap} onToggleAudioEffect={handleToggleAudioEffect}
          audioEffectConfigsMap={audioEffectConfigsMap} onAudioEffectConfigsChange={handleAudioEffectConfigsChange}
          videoEffectsSettingsMap={videoEffectsSettingsMap} onVideoEffectsSettingsChange={handleVideoEffectsSettingsChange} onAudioVisualChange={handleAudioVisualChange}
          onSlotSourceChangeAlias={handleDeckSlotChange} onSourceConfigOpen={handleSourceConfigOpen} onSourceConfigClose={handleSourceConfigClose}
          hlsProbeStatusMap={hlsProber.statusMap} hlsWasEverOk={hlsProber.wasEverOk} onRetryHls={handleRetryHls}
          onSlotSourceChange={handleDeckSlotChangeWrapped} onSlotPowerToggle={handleSlotPowerToggle} poweredOffSlots={poweredOffSlots}
          streamSource={streamSource} defaultStreamSource={defaultStreamSource}
          onStreamSourceChange={onStreamSourceChange}
          onDefaultStreamSourceChange={onDefaultStreamSourceChange}
          onStopStreamingExternal={onStopStreaming}
          isRegiePowered={isRegiePowered} onToggleRegiePower={handleToggleRegiePower}
          onSaveConfig={handleOpenSaveConfig}
          onLoadConfig={handleOpenLoadConfig}
          onResetRegie={handleResetRegieToDefaults}
          broadcastUuid={broadcastUuid}
          onAddHlsSource={handleAddHlsSource}
          onRegisterXtreamConnection={handleAddHlsSource}
          microserviceUrl={channelPublicRef ? `${settings.serverUrl}/${channelPublicRef}/regie` : ''}
          audioSwitchState={audioSwitchState}
          onAudioSwitchChange={handleAudioSwitchChange}
          deckAttachedAudioIds={deckAttachedAudioIds}
          mixSourceIds={mixSourceIds}
          onMixerDraftChange={setMixDraft}
          audioSourceRegistry={audioSourceRegistry}
          autoDirectorEnabled={autoDirector.enabled} onAutoDirectorToggle={autoDirector.setEnabled}
          autoDirectorStatus={autoDirector.status}
          autoDirectorSettings={autoDirector.settings} onAutoDirectorSettingsChange={autoDirector.setSettings}
          autoDirectorCandidates={directorDeckPayload.sources}
          hlsVolume={hlsVolume}
          hlsMuted={hlsMuted}
          onHlsVolumeChange={onHlsVolumeChange}
          onHlsMutedChange={onHlsMutedChange}
          broadcastingChannel={broadcastingChannel}
          multiview={multiviewActive}
          onMultiviewChange={handleMultiviewChange}
          onRemoveSource={handleRemoveSource}
          onReloadSource={handleReloadSource} onReloadAudio={handleReloadAudio}
          onRenameSource={handleRenameSource}
          hlsAudioRegistry={hlsAudioRegistry}
          hlsAudioSourceId={hlsAudioSourceId}
          onHlsReset={onHlsReset}
          localMics={localMics}
          // ── Intercomm ──────────────────────────────────────────────────
          intercommMicStream={intercommMicStream}
          intercommDeviceStreams={intercommDeviceStreams}
          intercommSpeakingDevices={intercommSpeakingDevices}
          onIntercommActivate={handleIntercommActivate}
          onIntercommMicChange={handleIntercommMicChange}
        />
        </div>
        </div>
      </div>

      {showLiveExportModal && (
        <LiveExportModal
          sources            = {multiviewGridSources}
          initialSelectedIds = {exportedMultiviewSourceIds ?? undefined}
          onCancel           = {() => setShowLiveExportModal(false)}
          onStart            = {handleLiveExportConfirm}
        />
      )}

      {showRecordingModal && (
        <RecordingSessionModal
          multiAngle   = {multiviewActive}
          defaultName  = {defaultRecordingName}
          remainingSec = {remainingRecordingSec}
          deckSources  = {recordableDeckSources}
          sourceHealth = {sourceHealth}
          onCancel     = {() => setShowRecordingModal(false)}
          onStart      = {handleRecordingModalStart}
          onBuyStorage = {() => openBuyStorage(showAlert)}
        />
      )}

      {liveRecordingToPublish && channelUuid && (
        <RecordingPublishModal session={liveRecordingToPublish} channelUuid={channelUuid} managerUrl={settings.managerUrl} apiKey={settings.managerApiKey} angleCount={liveRecordingAngleCount} onClose={() => setLiveRecordingToPublish(null)} />
      )}
      {showStorageModal && channelUuid && (
        <RecordingStorageModal channelUuid={channelUuid} channelName={broadcastingChannel?.name ?? 'Chaîne'} managerUrl={settings.managerUrl} apiKey={settings.managerApiKey} adminId={authentication.admin?.id ?? 0} quotaDiskBytes={authentication.admin?.quotaDisk ?? 0} quotaUsedBytes={authentication.admin?.quotaUsed ?? 0} configuredVideoBitrateKbps={settings.videoBitrate} configuredAudioBitrateKbps={settings.audioBitrate} onClose={handleCloseStorageModal} />
      )}

      {/* « Monter sur le plateau » : demandes des spectateurs de Kleon.tv (chaînes de la régie web seulement). */}
      <StageRequestsDock
        adminId          = {authentication.admin?.id}
        channelId        = {broadcastingChannel?.id}
        createdFromRegie = {broadcastingChannel?.createdFromRegie ?? false}
        onGuestsChanged  = {loadDevices}
        showAlert        = {showAlert}
      />

      {/* Santé du navigateur (retard de boucle + mémoire JS), à gauche de « Public ». */}
      <ClientHealthBadge />

      {/* Sondages en direct : préparer, lancer, clore, et le bandeau à l'antenne (chaînes de la régie web seulement). */}
      <PollsDock
        adminId          = {authentication.admin?.id}
        channelId        = {broadcastingChannel?.id}
        createdFromRegie = {broadcastingChannel?.createdFromRegie ?? false}
        overlayRef       = {pollOverlayRef}
        showAlert        = {showAlert}
      />
    </div>
  );
};

export default LiveStreamManager;